// src/cockpit4/workspaces/MarketingStudioWorkspace.tsx
// ============================================
// Content Factory Workspace - 内容工厂（原内容工厂，Phase D + Phase III）
//
// 功能：
//   1. 输入主题/描述/风格，生成全套媒体素材（封面/九宫格/视频/BGM/脚本）
//   2. 按日计划自动生成：读取 Obsidian 今日计划，提取主题，自动生成
//   3. 素材 Tab 切换：全部 / 图片 / 脚本 / 视频 / 音乐
//   4. 审核操作：通过 / 拒绝（重新生成）/ 下载
//   5. 待发布队列：已审核通过的素材包，一键人工发布
//   6. 知识关联追溯：显示引用的知识库笔记
//
// 对接 Runtime：MediaStudioRuntime
//   - execute('generate_full_package') 生成全套
//   - execute('generate_from_plan') 按计划生成
//   - execute('approve_asset') / execute('reject_asset') 审核
//   - execute('publish_package') 发布
//   - query({ type: 'list_packages' }) 历史列表
//   - query({ type: 'list_publish_queue' }) 待发布队列
// ============================================

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ImageIcon,
  Video,
  Music,
  Sparkles,
  Loader2,
  Check,
  X,
  Camera,
  Eye,
  RefreshCw,
  Send,
  Grid3x3,
  Layout,
  Play,
  Pause,
  ChevronRight,
  ChevronDown,
  FileText,
  CalendarClock, BookOpen, Gauge,
  CalendarRange,
  MessagesSquare,
  Archive,
  Edit3,
  Save,
  ArrowRightLeft,
  Newspaper,
  ExternalLink,
  FolderArchive,
  FolderOpen,
  RefreshCcw,
  FileSearch,
  Layers,
  TrendingUp,
  // v6.2.8 就地讨论增强：复制 / 放大预览 / 消息图标
  Copy,
  Maximize2,
  MessageSquare,
  // P2 标签图标
  CircleUserRound,
  Coins,
  Download,
  PencilRuler,
  // v6.3.16 口吻管理标签图标
  BookOpenText,
  // v6.24 官博发布标签图标
  PenSquare,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
// Phase 3 P0-3：页面顶部业务说明 + 跨模块跳转
import WorkspaceBizBar from "../components/WorkspaceBizBar";
import type { MediaPackage, MediaAsset, ScriptLibraryItem, MediaStudioCommand } from "../../runtime/MediaStudio/types";
import { getContentPlanService, PLATFORM_CONFIG, type ContentPlanItem, type SocialPlatform } from "../../services/contentPlan";
// v6.3.22 品牌常量（唯一真相源：内容工厂与营销中心共用）
import { BRAND_OPTIONS, DEFAULT_BRAND_ID } from "../../services/brands";
import { getWeChatPublishService } from "../../services/wechatPublish";
import { apiConfig } from "../../services/apiConfig";
// Phase 2.2 内容发布 → CRM 留资闭环：发布成功后记录一笔内容引流线索（source=平台）
import { recordCrmLeadFromContent } from "../../services/crmLeadStorage";
import SimpleMarkdownRenderer from "../components/SimpleMarkdownRenderer";
// v6.28 重构：周度内容大纲卡片拆分为纯展示子组件（状态/逻辑仍在父组件，只收 props + 回调）
import WeeklyOutlineToolbar from "./components/WeeklyOutlineToolbar";
import WeeklyOutlineCard from "./components/WeeklyOutlineCard";
import HermesDiscussCard from "./components/HermesDiscussCard";
// v6.24 官博发布面板（方案B：写文 → 一键发布到官网博客）
import BlogPublishPanel from "../components/BlogPublishPanel";
import { useCockpit4Store } from "../store";
// Phase3 P2-4：老板只读模式
import { useReadonlyMode } from "../hooks/useReadonlyMode";
// P0 新增：内容日历 / 质量评分 / 多平台分发
import ContentCalendar from "../components/ContentCalendar";
import ContentQualityScorer from "../components/ContentQualityScorer";
import ContentDistributor from "../components/ContentDistributor";
// P1 新增：内容工厂四个子组件（资产库/优化闭环/审核工作流/系列化管理）
import ContentAssetLibrary from "../components/ContentAssetLibrary";
import ContentOptimizationLoop from "../components/ContentOptimizationLoop";
// v6.3.16 口吻管理（语料自助注入，内容生成的人格层入口）
import PersonalVoiceManager from "../components/PersonalVoiceManager";
// v6.7 订阅号「小舍AI」热度号工作台（独立副业号入口）
import SubscriptionHub from "../components/SubscriptionHub";
// v6.16 内容仓库：查看/复制所有生成内容（口播稿/多平台文章/草稿）
import ContentRepository from "../components/ContentRepository";
import ContentReviewWorkflow from "../components/ContentReviewWorkflow";
import ContentSeriesManager from "../components/ContentSeriesManager";
// Content Protocol（第十四章）：强制流程面板
import ContentProtocolFlow from "../components/ContentProtocolFlow";
// Content Protocol（第十四章）：每日经验沉淀（经验驱动选题）
import DailyExperiencePanel from "../components/DailyExperiencePanel";
import ViralContentBriefLoader from "../components/ViralContentBriefLoader";
// P2-功能8：内容矩阵管理
import ContentMatrixManager from "../components/ContentMatrixManager";
// P2-功能9：IP 人设管理系统
import IPPersonaManager from "../components/IPPersonaManager";
// P2-功能10：内容商业化引擎
import ContentMonetizationEngine from "../components/ContentMonetizationEngine";
// MVP：内容效果预测与爆款反推（爆款样本→反推公式→预测→复盘校准）
import ContentPredictPanel from "../components/ContentPredictPanel";
// v5.2 生成前对比选择弹窗（约束版 vs 裸写版 → 人选最优 → 正式生成）
import ContentPreCompareModal from "../components/ContentPreCompareModal";

// v6.45 内容工厂左栏改为嵌入自媒体 AI 工作台（原 AIGC 二级站改为外链）
import MediaWorkbench, { type WbGoto } from "./MediaWorkbench";

/**
 * v6.45 主站平台 key → 工作台模板平台名。
 * 工作台 12 个模板里没有的（B站 / 每日直播 / 视频号）一律留空，不给假入口。
 */
const WB_PLAT_MAP: Partial<Record<SocialPlatform, string>> = {
  wechat: "公众号",
  douyin_enterprise: "抖音企业号",
  xiaohongshu: "小红书",
  zhihu: "知乎",
  douyin_personal: "抖音个人号",
  toutiao: "头条文章",
  wechat_moments: "微信朋友圈",
  wechat_subscription: "公众号",
  csdn: "CSDN",
};

// ============================================
// 类型定义
// ============================================

type AssetTab = "all" | "images" | "scripts" | "video" | "music";

interface GeneratingState {
  active: boolean;
  topic: string;
  progress: number;
  message: string;
  /** 标记当前消息是否为错误（控制展示颜色：错误红色/成功绿色） */
  error?: boolean;
  /** v5.2.2 修复：生成作用域。platform=平台级生成（该平台按钮转圈）；itemId=单项生成（仅该项转圈）。
   *  二者都为空时表示全局等待（无按钮转圈）。解决「点一个生成、所有平台按钮全转圈冻结」的 bug */
  platform?: string | null;
  itemId?: string | null;
}

interface PreviewState {
  type: "image" | "video" | "audio" | "script";
  url?: string;
  content?: string;
  title: string;
}

// ============================================
// 公共辅助函数：轮询等待 RuntimeManager 就绪
// （30+ Runtime 全部初始化需要数秒，点击按钮时可能尚未就绪）
// ============================================
async function waitForRuntime(
  maxRetries = 60,
  intervalMs = 300
): Promise<boolean> {
  // 统一工具函数（P2 性能优化）：RuntimeManager.waitForRuntime
  return RuntimeManager.waitForRuntime({ timeoutMs: maxRetries * intervalMs, intervalMs });
}

// ============================================
// 主组件
// ============================================

export default function MarketingStudioWorkspace() {
  // 输入表单
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  // v6.1.10 封面风格多样性：从风格池随机初始化（"每次图都一样"根因=固定默认风格），
  // 用户可下拉固定；未手动选择时每次同步生成封面随机换风格
  const COVER_STYLES = [
    "科技感深色背景，霓虹蓝绿色调，玻璃拟态风格",
    "极简留白风格，暖白底色，柔和大色块，商务质感",
    "深空蓝渐变背景，粒子光效，未来主义",
    "暖橙色系，几何图形与线条，活力年轻感",
    "暗金黑金配色，大理石纹理，高端奢华感",
    "清新马卡龙色调，圆润插画风，亲和力强",
    "墨绿深色+金色线条，中国风与现代融合",
    "浅灰背景+大面积品牌色点缀，干净利落",
    "渐变紫蓝，光晕与流动线条，梦幻科技感",
    "暗红+暖黄，胶片质感，复古文艺风",
  ];
  const [style, setStyle] = useState(() => COVER_STYLES[Math.floor(Math.random() * COVER_STYLES.length)]);
  // 跳转工作区
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  // Phase3 P2-4：老板只读模式（隐藏/禁用生成、编辑、导入等写操作）
  const { readonly, guard } = useReadonlyMode();

  // 生成状态
  const [generating, setGenerating] = useState<GeneratingState>({
    active: false,
    topic: "",
    progress: 0,
    message: "",
    error: false,
    platform: null,
    itemId: null,
  });

  // 当前媒体包
  const [currentPackage, setCurrentPackage] = useState<MediaPackage | null>(null);

  // 历史媒体包
  const [packages, setPackages] = useState<MediaPackage[]>([]);

  // 待发布队列
  const [publishQueue, setPublishQueue] = useState<MediaPackage[]>([]);

  // 当前 Tab
  const [activeTab, setActiveTab] = useState<AssetTab>("all");

  // 预览状态
  const [preview, setPreview] = useState<PreviewState | null>(null);

  // 音频播放
  const [audioPlaying, setAudioPlaying] = useState<string | null>(null);

  // 审核状态
  const [approvedAssets, setApprovedAssets] = useState<Set<string>>(new Set());

  // 自媒体内容计划
  const [contentPlan, setContentPlan] = useState<ContentPlanItem[]>([]);
  // v6.1 知乎问答模式：每个条目的问题输入草稿（key=item.id）
  const [zhihuDraft, setZhihuDraft] = useState<Record<string, string>>({});
  const [planLoading, setPlanLoading] = useState(false);

  // 手动刷新今日计划状态（基于 Obsidian 知识库重新生成）
  const [refreshingPlan, setRefreshingPlan] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<{ success: boolean; message: string } | null>(null);

  // v5.2 按主题重写今日计划（不满意自动选题时，全部围绕指定主题写）
  const [planTopicHint, setPlanTopicHint] = useState("");
  const [regeneratingTopic, setRegeneratingTopic] = useState(false);
  // v6.31 单平台自定义标题生成：输入标题只给指定平台写一篇（不碰其他平台，一鱼多吃已失效）
  const [customOpen, setCustomOpen] = useState<Record<string, boolean>>({});
  const [customDraft, setCustomDraft] = useState<Record<string, string>>({});

  // v5.2 生成前对比：选中计划项 → 弹双版本对比选择
  const [compareItem, setCompareItem] = useState<ContentPlanItem | null>(null);

  const handleRegenerateByTopic = async () => {
    const hint = planTopicHint.trim();
    if (!hint) return;
    setRegeneratingTopic(true);
    setRefreshMessage(null);
    try {
      const service = getContentPlanService();
      const plan = await service.regeneratePlanByTopic(hint);
      if (plan.length > 0) {
        setContentPlan(plan);
        setRefreshMessage({
          success: true,
          message: `已按主题「${hint}」重写今日计划，共 ${plan.length} 条（全部围绕该主题选题）`,
        });
      } else {
        setRefreshMessage({
          success: false,
          message: `未在 Obsidian 知识库中找到含「${hint}」的内容，请换个主题或先录入相关内容`,
        });
      }
    } catch (err: any) {
      setRefreshMessage({
        success: false,
        message: `按主题重写失败：${err?.message || String(err)}`,
      });
    } finally {
      setRegeneratingTopic(false);
      setTimeout(() => setRefreshMessage(null), 6000);
    }
  };

  // v6.31 单平台自定义标题生成：输入标题只给指定平台写一篇（不碰其他平台，一鱼多吃已失效）
  // 用户诉求（2026-08-15）："OPC 创业到底是什么？怎么做好？"只想写抖音个人号
  const handleGenerateCustomTopic = async (platform: SocialPlatform) => {
    const title = (customDraft[platform] || '').trim();
    if (!title) {
      setRefreshMessage({ success: false, message: '请先输入标题再点生成' });
      setTimeout(() => setRefreshMessage(null), 4000);
      return;
    }
    setRefreshMessage(null);
    try {
      const service = getContentPlanService();
      const today = new Date().toISOString().slice(0, 10);
      const item = await service.addCustomPlanItem(today, platform, title);
      if (!item) {
        setRefreshMessage({ success: false, message: '自定义选题写入计划失败，请重试' });
        setTimeout(() => setRefreshMessage(null), 5000);
        return;
      }
      // 加入内存计划（该平台出现新条目）→ 自动开始生成这一篇
      setContentPlan((prev) => [...prev, item]);
      setCustomDraft((prev) => ({ ...prev, [platform]: '' }));
      setCustomOpen((prev) => ({ ...prev, [platform]: false }));
      setRefreshMessage({ success: true, message: `已为${PLATFORM_CONFIG[platform].name}添加自定义选题，开始生成…` });
      setTimeout(() => setRefreshMessage(null), 4000);
      await handleGenerateFromPlanItem(item);
    } catch (err: any) {
      setRefreshMessage({ success: false, message: `自定义生成失败：${err?.message || String(err)}` });
      setTimeout(() => setRefreshMessage(null), 5000);
    }
  };

  // v5.2 成本优化：已生成项重新生成（1 次 LLM，替代对比重生成的 2 次 LLM）
  // v6.3.15 换内容差异化：随机换一种切入角度，避免每次生成一模一样（用户反馈"换内容内容都一样"）
  const handleRegenerateItem = async (item: ContentPlanItem) => {
    // 重新生成 = 走标准生成链路（约束版，1 次 LLM），新产物覆盖当前
    const variations = [
      "换个角度重写：这次从真实经历/踩坑故事开场，讲具体细节",
      "换个角度重写：这次从行业现象/常见误区开场，再讲解决方法",
      "换个角度重写：这次从具体数据/场景开场，再引出观点",
      "换个角度重写：这次从反问/提问开场，再展开讲",
    ];
    const pick = variations[Math.floor(Math.random() * variations.length)];
    await handleGenerateFromPlanItem(item, pick);
  };

  // v6.1 知乎问答模式：切换「文章/问答」，问答模式下条目回到待生成（换问题后需重新生成）
  const handleZhihuModeChange = async (item: ContentPlanItem, mode: 'article' | 'qa') => {
    setContentPlan((prev) =>
      prev.map((p) =>
        p.id === item.id ? { ...p, itemType: mode, status: 'pending' as const, packageId: undefined } : p
      )
    );
    await getContentPlanService()
      .updatePlanItemQuestion(item.date, item.topic, mode, item.question)
      .catch(() => {});
    setRefreshMessage({
      success: true,
      message: mode === 'qa' ? '已切换为问答模式：填入问题后生成，将按问题输出知乎回答' : '已切换为文章模式：生成知乎专栏文章',
    });
    setTimeout(() => setRefreshMessage(null), 4000);
  };

  // v6.1 知乎问答模式：保存用户输入的问题并写回计划（未生成条目可反复改）
  const handleZhihuQuestionSave = async (item: ContentPlanItem, question: string) => {
    const q = question.trim();
    if (!q) {
      setRefreshMessage({ success: false, message: '请输入问题内容' });
      setTimeout(() => setRefreshMessage(null), 3000);
      return;
    }
    setContentPlan((prev) =>
      prev.map((p) =>
        p.id === item.id ? { ...p, question: q, itemType: 'qa' as const, status: 'pending' as const, packageId: undefined } : p
      )
    );
    await getContentPlanService()
      .updatePlanItemQuestion(item.date, item.topic, 'qa', q)
      .catch(() => {});
    setRefreshMessage({ success: true, message: `问题已保存：${q.slice(0, 30)}${q.length > 30 ? '…' : ''}` });
    setTimeout(() => setRefreshMessage(null), 3500);
  };

  // v6：未生成条目「换一篇」= 换选题（从今日新闻/知识库换一个新主题，待生成）
  const handleSwitchTopic = async (item: ContentPlanItem) => {
    if (generating.active) return;
    const service = getContentPlanService();
    try {
      const newTopic = await service.switchTopicForItem(item.date, item.topic);
      if (!newTopic) {
        setPlatformSyncResult((prev) => ({
          ...prev,
          [item.platform]: { success: false, message: '暂无可替换的选题，请先采集今日新闻或录入知识库' },
        }));
        setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [item.platform]: null })), 5000);
        return;
      }
      // 更新内存计划（换主题 → 状态回到待生成）
      setContentPlan((prev) =>
        prev.map((p) =>
          p.id === item.id
            ? { ...p, topic: newTopic, status: 'pending' as const, packageId: undefined, knowledgeNote: undefined }
            : p
        )
      );
      // 写回计划文件（Rule 05）
      await service.updatePlanItemTopic(item.date, item.topic, newTopic).catch(() => {});
      setRefreshMessage({ success: true, message: `已为${PLATFORM_CONFIG[item.platform].name}换选题：「${newTopic}」` });
      setTimeout(() => setRefreshMessage(null), 4000);
    } catch (err: any) {
      setRefreshMessage({ success: false, message: `换选题失败：${err?.message || String(err)}` });
      setTimeout(() => setRefreshMessage(null), 5000);
    }
  };

  // v5.2 生成前对比：用户选中版本后，用所选内容直接落库（对比时已消耗 2 次 LLM，不再重复生成）
  const handleCompareSelect = async (content: string, source: "constrained" | "raw") => {
    if (!compareItem) return;
    const item = compareItem;
    setCompareItem(null);
    try {
      const now = new Date();
      const packageId = `script-${item.platform}-${Date.now()}`;
      const createdAt = now.toISOString();
      const desc = `为${PLATFORM_CONFIG[item.platform].name}生成内容：${item.topic}（${source === "constrained" ? "约束版" : "裸写版"}）`;
      // v6.1 平台→脚本类型映射（含微信视频号/朋友圈）
      // v6.3.19 今日头条 → toutiao_script（头条专属图文规则）；v6.7 CSDN → article_script（文章类）
      const scriptMarker = item.platform === "wechat" ? "article_script"
        : (item.platform === "douyin_enterprise" || item.platform === "douyin_personal") ? "douyin_script"
        : item.platform === "xiaohongshu" ? "xiaohongshu_script"
        : item.platform === "bilibili" ? "bilibili_script"
        : item.platform === "wechat_moments" ? "moments_script"
        : item.platform === "wechat_channels" ? "video_script"
        : item.platform === "toutiao" ? "toutiao_script"
        : item.platform === "csdn" ? "article_script"
        : "article_script";
      const asset = { id: `asset-${packageId}-${scriptMarker}`, type: "script" as const, purpose: scriptMarker as any, content, title: `${PLATFORM_CONFIG[item.platform].name}脚本：${item.topic}`, model: "deepseek-chat", status: "completed" as const, createdAt };

      // 持久化到 Vault（Rule 05）
      const safeTopic = item.topic.replace(/[\/\\:*?"<>|]/g, "_").slice(0, 30);
      const filePath = `XEAOS/ContentPackages/${createdAt.slice(0, 10)}/${packageId}-${safeTopic}.md`;
      const fm = [
        "---", `id: ${packageId}`, `topic: "${item.topic}"`, "status: completed",
        `createdAt: ${createdAt}`, `completedAt: ${createdAt}`, `description: "${desc}"`, "sourceNotes: 0",
        "hasCover: false", "hasWechatCover: false", "hasXiaohongshuGrid: false", "hasDouyinCover: false",
        "hasVideo: false", "hasBgm: false", `hasArticleScript: ${scriptMarker === "article_script"}`, "hasVideoScript: false",
        `hasXiaohongshuScript: ${scriptMarker === "xiaohongshu_script"}`, `hasBilibiliScript: ${scriptMarker === "bilibili_script"}`, `hasDouyinScript: ${scriptMarker === "douyin_script"}`, "hasLiveStreamScript: false",
        "---", "", `# ${item.topic}`, "", `**状态**：completed  ·  **来源**：生成前对比（${source === "constrained" ? "约束版" : "裸写版"}）`, "",
        `<!-- XEAOS_SCRIPT:${scriptMarker} -->`, content, "",
      ].join("\n");
      await fetch(`/vault-api/write?path=${encodeURIComponent(filePath)}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content: fm }),
      }).catch(() => {});

      // 更新内存计划 + 写回计划文件 + 刷新包列表
      setContentPlan((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: "completed" as const, packageId } : p)));
      getContentPlanService().updatePlanItemStatus(item.date, item.platform, item.topic, "completed", packageId).catch(() => {});
      loadPackages();
      setGenerating({ active: false, topic: "", progress: 100, message: `已用${source === "constrained" ? "约束版" : "裸写版"}生成：「${item.topic}」`, error: false, platform: null, itemId: null });
    } catch (err: any) {
      setGenerating({ active: false, topic: "", progress: 0, message: `对比生成落库失败：${err?.message || String(err)}`, error: true, platform: null, itemId: null });
    }
  };

  // v5.2：一键采集今日新闻（NewsRuntime → Vault，复活「新闻→内容」链路源头）
  const [newsFetching, setNewsFetching] = useState(false);
  const [newsMessage, setNewsMessage] = useState<{ success: boolean; message: string } | null>(null);

  // v5.2 审计修复 P1：内容生产品牌选择（白皮书第十四章流程①：先选品牌再选平台）
  // v6.3.22 品牌常量抽离到 src/services/brands.ts（与营销中心共用唯一真相源）
  const [brandId, setBrandId] = useState(DEFAULT_BRAND_ID);

  // v5.2.3 主播参考图：口播内容封面合成用（上传主播照片 → 封面左下角合成头像，抖音口播封面样式）
  // 全局配置一次，所有口播封面生效；localStorage 持久化（xeaos_anchor_photo）
  const [anchorPhoto, setAnchorPhoto] = useState<string | null>(() => {
    try { return localStorage.getItem("xeaos_anchor_photo"); } catch { return null; }
  });
  const anchorInputRef = useRef<HTMLInputElement>(null);
  const handleAnchorPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setAnchorPhoto(dataUrl);
      try { localStorage.setItem("xeaos_anchor_photo", dataUrl); } catch { /* 超出配额忽略 */ }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // 脚本编辑
  const [editingScript, setEditingScript] = useState<{ asset: MediaAsset; content: string } | null>(null);
  // v5.2：保存去向提示（Rule 05 写回透明化）
  const [saveTip, setSaveTip] = useState<string | null>(null);

  // v5.2：下载脚本为 Markdown 文件（本地导出，脱离系统也能用）
  const handleDownloadScript = () => {
    if (!editingScript) return;
    const topicSafe = (currentPackage?.topic || '脚本').replace(/[\/\\:*?"<>|]/g, '_').slice(0, 30);
    const purposeName =
      editingScript.asset.purpose === 'article_script' ? '公众号文章' :
      editingScript.asset.purpose === 'douyin_script' ? '抖音脚本' :
      editingScript.asset.purpose === 'xiaohongshu_script' ? '小红书图文' :
      editingScript.asset.purpose === 'bilibili_script' ? 'B站脚本' :
      editingScript.asset.purpose === 'video_script' ? '视频分镜' :
      editingScript.asset.purpose === 'live_stream_script' ? '直播脚本' : '脚本';
    const fileName = `${purposeName}-${topicSafe}-${new Date().toISOString().slice(0, 10)}.md`;
    const blob = new Blob([editingScript.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSaveTip(`📥 已下载：${fileName}`);
    setTimeout(() => setSaveTip(null), 3000);
  };

  // 发布状态
  const [publishing, setPublishing] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<Record<string, { success: boolean; message: string }>>({});

  // 同步到营销中心（ContentRuntime）状态
  const [syncingToMarketing, setSyncingToMarketing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ success: boolean; message: string; canJumpToMarketing?: boolean } | null>(null);

  // 按平台同步状态：记录每个平台是否正在同步 / 同步结果
  // 用户偏好：同步按钮应在每个分类框内显示，方便用户操作
  const [platformSyncing, setPlatformSyncing] = useState<Record<string, boolean>>({});
  const [platformSyncResult, setPlatformSyncResult] = useState<Record<string, { success: boolean; message: string } | null>>({});
  // v6.2.27 每日计划平台分类展开状态：默认展开所有平台（Set<platform>），点击标题栏收起/展开
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(() => new Set());
  const togglePlatformExpand = (platform: string) => {
    setExpandedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  // ============ v6.1.20 周度内容大纲（AI 出周计划 → Hermes 讨论确定 → 平台生成围绕大纲） ============
  const [weeklyOutline, setWeeklyOutline] = useState("");
  const [weeklyFocus, setWeeklyFocus] = useState("");
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyGenerating, setWeeklyGenerating] = useState(false);
  const [weeklyWeekLabel, setWeeklyWeekLabel] = useState("");
  // v6.2.9 历史大纲列表（周度大纲 + 详细版），集中查看/恢复
  const [outlineHistory, setOutlineHistory] = useState<{ name: string; path: string; type: "weekly" | "detail" }[]>([]);
  const [outlineHistoryOpen, setOutlineHistoryOpen] = useState(false);
  const [outlineHistoryLoading, setOutlineHistoryLoading] = useState(false);
  const [viewingHistoryOutline, setViewingHistoryOutline] = useState<{ name: string; content: string; path: string } | null>(null);
  // v6.2.9 默认大纲已自动落盘标记（首次加载无大纲时生成）
  const [weeklyDefaultApplied, setWeeklyDefaultApplied] = useState(false);

  // 计算当前周标签（2026-W32）
  const getWeekLabel = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const weekNo = Math.ceil(((now.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
    return `${now.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
  }, []);

  // v6.2.9 默认周度大纲模板（无大纲时自动落盘，保证每天都能看到"之前的大纲是什么"）
  // 内容来自口播系列·手把手搭建企业AI系统（知识库认知系列 10 问）默认排期
  const buildDefaultWeeklyOutline = (week: string) => {
    return [
      `# 周度内容大纲 · ${week}（默认模板）`,
      ``,
      `> 本大纲为系统默认模板（源自口播系列·手把手搭建企业AI系统 知识库认知系列）。`,
      `> 可与 Hermes 就地讨论调整，采纳后覆盖本文件；也可点「生成本周大纲」按重点方向重新生成。`,
      ``,
      `## 周一（认知铺垫：先想清楚）`,
      `- 主题1：别瞎选！企业系统、个人工作台、Obsidian 到底怎么选才不踩坑？`,
      `- 主题2：先搭工作台，还是先建知识库？90% 的人都搞反了`,
      ``,
      `## 周二（动手搭建）`,
      `- 主题1：Obsidian 知识库怎么搭才不乱？10 大分区踩坑实录`,
      `- 主题2：Obsidian 知识库、AI、Hermes 到底是什么关系？一张图说清楚`,
      ``,
      `## 周三（打通与铁律）`,
      `- 主题1：知识库＋Hermes 打通 vs 知识库＋通用 AI 打通，哪个才真有用？`,
      `- 主题2：为什么我敢让 AI 替我回答业务问题？就靠这一条铁律`,
      ``,
      `## 周四（越用越聪明）`,
      `- 主题1：知识库怎么越用越聪明？就靠"喂经验"这一个笨办法`,
      `- 主题2：本地写笔记、服务器自动读：双端同步没你想的那么难`,
      ``,
      `## 周五（系统串联）`,
      `- 主题1：企业大脑＋手脚，到底怎么配合？给你看我一个人怎么当一家公司`,
      `- 主题2：30 分钟跑通：一个人怎么一天产出 26 条内容？`,
      ``,
      `## 周六（自由选题）`,
      `- 主题1：（可替换）本周踩坑复盘 / 观众问题答疑`,
      `- 主题2：（可替换）AI 行业热点结合个人经验分享`,
      ``,
      `## 周日（休息 / 复盘）`,
      `- 主题1：本周内容数据复盘 + 下周选题预告`,
      `- 主题2：（可选）朋友圈/视频号轻量内容`,
      ``,
    ].join("\n");
  };

  // 加载本周大纲（Vault 最新周度大纲文件）
  // v6.2.9 改进：无大纲时自动写入默认大纲（保证每天都能看到"之前的大纲"），并记录已采纳状态
  const loadWeeklyOutline = useCallback(async () => {
    setWeeklyLoading(true);
    try {
      const week = getWeekLabel();
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Content/周度大纲-${week}.md`)}`);
      if (resp.ok) {
        const r = await resp.json();
        if (r.success && r.content) {
          const raw = String(r.content);
          const clean = raw.replace(/^---\n[\s\S]*?\n---\n/, '');
          setWeeklyOutline(clean.trim());
          setWeeklyWeekLabel(week);
          // v6.2.9 从 frontmatter 判断是否默认模板（刷新页面后标记不丢）
          const fm = raw.match(/^---\n([\s\S]*?)\n---/);
          setWeeklyDefaultApplied(!!fm && /默认模板/.test(fm[1]));
        } else {
          // v6.2.9 无大纲 → 自动写入默认大纲（口播系列 10 问模板），用户有"默认大纲"可看
          const defOutline = buildDefaultWeeklyOutline(week);
          await fetch(`/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Content/周度大纲-${week}.md`)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              content: `---\ntitle: "周度内容大纲-${week}"\ndate: ${new Date().toISOString().slice(0, 10)}\ntags:\n  - Operations（运营层）\n  - Content\n  - 周度大纲\n  - 默认模板\n---\n\n${defOutline}`,
            }),
          });
          setWeeklyOutline(defOutline.trim());
          setWeeklyWeekLabel(week);
          setWeeklyDefaultApplied(true);
        }
      }
    } catch { /* 忽略 */ }
    setWeeklyLoading(false);
  }, [getWeekLabel]);

  useEffect(() => { loadWeeklyOutline(); }, [loadWeeklyOutline]);

  // 生成本周大纲
  const handleGenerateWeeklyOutline = async () => {
    setWeeklyGenerating(true);
    try {
      const week = getWeekLabel();
      const resp = await RuntimeManager.execute(RuntimeName.MediaStudio, "generate_weekly_outline", {
        week,
        focus: weeklyFocus,
      });
      if (resp?.success && resp.data) {
        const data = resp.data as any;
        if (typeof data === "string") { setWeeklyOutline(data); }
        else if (data && typeof data.content === "string") { setWeeklyOutline(data.content); }
        setWeeklyWeekLabel(week);
        setWeeklyFocus("");
        loadWeeklyOutline();
      } else {
        alert(`周度大纲生成失败：${(resp as any)?.error || "未知错误"}`);
      }
    } catch (err: any) {
      alert(`周度大纲生成异常：${err?.message || err}`);
    }
    setWeeklyGenerating(false);
  };

  // v6.1.21 周度大纲概要解析：从大纲 markdown 提取每天主题行（每天一行摘要）
  const weeklySummary = useMemo(() => {
    if (!weeklyOutline) return [] as { day: string; topics: string }[];
    const sections = weeklyOutline.split(/^##\s+/m);
    const out: { day: string; topics: string }[] = [];
    for (const sec of sections) {
      const header = sec.match(/^(周[一二三四五六日])（([^）]+)）/);
      if (!header) continue;
      const topics = (sec.match(/^[-*]\s*主题\d*\s*[:：]\s*(.+)$/gm) || [])
        .map((t) => t.replace(/^[-*]\s*主题\d*\s*[:：]\s*/, "").trim())
        .slice(0, 3);
      out.push({ day: `${header[1]}（${header[2]}）`, topics: topics.join(" ｜ ") || "（待定）" });
    }
    return out;
  }, [weeklyOutline]);

  // v6.2.9 历史大纲：列出 Content 目录下所有周度大纲 + 详细版文件，供集中查看/恢复
  const loadOutlineHistory = useCallback(async () => {
    setOutlineHistoryLoading(true);
    setOutlineHistory([]);
    try {
      const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent('10_Operations（运营层）/Content')}&ext=.md`);
      if (resp.ok) {
        const r = await resp.json();
        const files: any[] = (r.success && Array.isArray(r.files)) ? r.files : [];
        // 只保留周度大纲相关文件，按名字倒序（最新在前）
        const list = files
          .filter((f: any) => /^周度大纲/.test(f.name || ""))
          .map((f: any) => ({
            name: (f.name || "").replace(/\.md$/, ""),
            path: f.path || `10_Operations（运营层）/Content/${f.name}`,
            type: ((f.name || "").includes("详细版") ? "detail" : "weekly") as "weekly" | "detail",
          }))
          .sort((a, b) => (a.name < b.name ? 1 : -1));
        setOutlineHistory(list);
      }
    } catch { /* 忽略 */ }
    setOutlineHistoryLoading(false);
  }, []);

  // 打开历史大纲列表时刷新
  const openOutlineHistory = useCallback(() => {
    setOutlineHistoryOpen(true);
    loadOutlineHistory();
  }, [loadOutlineHistory]);

  // 查看某份历史大纲内容
  const viewHistoryOutline = async (path: string, name: string) => {
    setViewingHistoryOutline({ name, content: "加载中...", path });
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`);
      if (resp.ok) {
        const r = await resp.json();
        if (r.success && r.content) {
          setViewingHistoryOutline({ name, content: String(r.content), path });
        } else {
          setViewingHistoryOutline({ name, content: "（文件不存在或为空）", path });
        }
      }
    } catch (err: any) {
      setViewingHistoryOutline({ name, content: `读取失败：${err?.message || err}`, path });
    }
  };

  // 把某份历史大纲恢复为当前大纲（写回本周文件 + 刷新界面）
  const restoreHistoryOutline = async (content: string) => {
    if (!weeklyWeekLabel) return;
    try {
      const cleaned = String(content).replace(/^---\n[\s\S]*?\n---\n/, "");
      const markdown = `---\ntitle: "周度内容大纲-${weeklyWeekLabel}"\ndate: ${new Date().toISOString().slice(0, 10)}\ntags:\n  - Operations（运营层）\n  - Content\n  - 周度大纲\n---\n\n${cleaned}`;
      await fetch(`/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Content/周度大纲-${weeklyWeekLabel}.md`)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: markdown }),
      });
      // v6.2.13 恢复后立即基于"恢复的内容"生成详细内容（传 baseOverride 避免 React setState 时序竞态）
      // v6.2.15 force=true 强制生成：ref 复位 + 无视守卫（修复"恢复后全是占位符"——守卫读旧值短路死循环）
      detailGeneratingRef.current = false;
      const detail = await generateDetailOutline(cleaned, true);
      setWeeklyDefaultApplied(false);
      setViewingHistoryOutline(null);
      setOutlineHistoryOpen(false);
      // 同步写入详细版文件（保证「配到每日计划」时各平台能读到基准内容）
      await fetch(`/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Content/周度大纲详细版-${weeklyWeekLabel}.md`)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: `---\ntitle: "周度内容大纲详细版-${weeklyWeekLabel}"\ndate: ${new Date().toISOString().slice(0, 10)}\ntags:\n  - Operations（运营层）\n  - Content\n  - 周度大纲\n  - 详细版\n---\n\n# 周度内容大纲详细版 · ${weeklyWeekLabel}\n\n${detail}`,
        }),
      });
      await loadWeeklyOutline();
      alert(`✅ 已把「${weeklyWeekLabel}」周度大纲恢复为所选历史大纲\n并已基于恢复的标题自动生成详细内容（已写入周度大纲详细版）\n→ 点「配到每日计划」即可分发各平台`);
    } catch { /* 忽略 */ }
  };

  // v6.1.21 周度大纲详情弹窗
  const [weeklyDetailOpen, setWeeklyDetailOpen] = useState(false);

  // v6.1.21 内嵌 Hermes 对话（就地讨论大纲，多端对话入口之一）
  const [hermesMsgs, setHermesMsgs] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [hermesInput, setHermesInput] = useState("");
  const [hermesBusy, setHermesBusy] = useState(false);

  // v6.3.13 口吻铁律修复：Hermes 讨论链路（标题讨论 + 详细大纲）此前未注入个人语料 →
  // 生成的大纲/详细内容源头就偏 AI 味。现在从 Vault 读取个人语料注入两处对话上下文。
  // v6.3.14 双文件加载：问答样本（短句判断/口头禅）+ 文章样本（长文结构/叙事节奏）
  const [hermesVoiceSample, setHermesVoiceSample] = useState("");
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const read = async (path: string) => {
          const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`).catch(() => null);
          if (!resp || !resp.ok) return "";
          const result = await resp.json();
          if (!result.success || !result.content) return "";
          return String(result.content).replace(/^---\n[\s\S]*?\n---\n/, "").trim();
        };
        const [qa, article] = await Promise.all([
          read("XEAOS/PersonalVoice/个人语料-知乎问答.md"),
          read("XEAOS/PersonalVoice/个人语料-文章.md"),
        ]);
        const clean = (qa + "\n\n" + article).trim().slice(0, 8000);
        if (!cancelled) setHermesVoiceSample(clean);
      } catch { /* 静默失败，不阻断 */ }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  // ============ v6.2.6 第二层：详细内容讨论（弹窗内）============
  // 第一层（卡片内"就地讨论"）讨论主标题 → 采纳 → 替换大纲标题
  // 第二层（弹窗内）基于采纳后的大纲自动生成详细内容，右侧独立对话框讨论详细内容
  const [detailMsgs, setDetailMsgs] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [detailInput, setDetailInput] = useState("");
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailOutline, setDetailOutline] = useState("");      // 弹窗左栏：基于采纳大纲生成的详细内容
  const [detailGenerating, setDetailGenerating] = useState(false);
  // v6.2.15 修复：detailGeneratingRef 同步标记生成中状态（useRef 实时读，不受 React setState 异步影响）
  // 根因：generateDetailOutline 守卫 `if (detailGenerating) return` 闭包里读到旧值 true →
  //       恢复历史大纲后调用被守卫短路 → 不生成也不走 finally → detailGenerating 永远 true →
  //       弹窗永远显示"生成中"占位符（用户反馈"全是占位符、没有生成内容"）
  const detailGeneratingRef = useRef(false);
  const setDetailGen = (v: boolean) => { detailGeneratingRef.current = v; setDetailGenerating(v); };
  // v6.2.29 详细版"基准指纹"：记录 detailOutline 是基于哪份大纲生成的。
  // 用途：换标题（采纳/恢复）后，任何入口（打开弹窗/配到每日计划）发现
  // 当前大纲与指纹不一致 → 强制重新生成详细版，杜绝"标题新的、内容旧的"错配。
  const detailBaseRef = useRef<string>("");
  // v6.2.8 就地讨论放大预览（大屏查看完整对话，解决字体小/滚动不便）
  const [chatPreviewOpen, setChatPreviewOpen] = useState(false);

  // 场景身份常量（第一层/第二层共用）
  // v6.2.4 场景身份注入（内容工厂 = 自媒体内容生产场景）
  // 背景：Hermes 不知道这份大纲是"自媒体口播系列"，用企业咨询/业务规划视角回答
  //       （"先明确业务目标/团队协作/KPI"），用户要的是自媒体口播逻辑。
  // 核心定义（用户 2026-08-07 明确）：
  //   ① 生产模式 = 人+AI 协作写作（真人出镜 + AI 辅助生产）
  //   ② 提纲作用 = 分享真实经验与价值
  //   ③ 价值核心 = 经验与踩的坑（自媒体只有持续输出有价值的内容才有流量）
  // 修复：注入场景定位，让 Hermes 用自媒体口播视角直接改稿，禁止企业咨询视角。
  // v6.2.4 新增：合集递进结构 + 通俗化要求（用户 2026-08-07 明确）：
  //   ① 这是一个合集（连续剧），标题之间必须有递进关系：
  //      第 1-5 问 = 认知铺垫（Obsidian/企业系统/个人工作台是什么、怎么选、顺序、关系）
  //      第 6-10 问 = 动手实践（搭知识库 → 越用越聪明 → 双端同步 → 大脑+手脚 → 30分钟闭环）
  //   ② 标题必须让大部分普通人看懂：禁用专业术语
  //      （"沉淀-索引-检索"→"越用越聪明"；"Git 同步"→"本地写、服务器自动读"；
  //       太专业=观众划走=口播失效）
  //   ③ 每集标题 = 钩子 + 大白话结论或踩坑 + 观众可带走的价值
  const sceneContext = [
    '【当前场景·内容工厂（自媒体内容生产）】',
    '你在「内容工厂 · 每日计划」工作区。用户是一名自媒体创作者（企业 IP 主理人），',
    '生产模式是「人 + AI 协作写作」：真人出镜口播 + AI 辅助选题/文案/封面/分发。',
    '用户讨论的这份大纲是【口播系列·手把手搭建企业AI系统】，用于抖音/视频号/公众号/小红书等平台。',
    '【用户真实背景（必须基于事实，禁止编造）】',
    '- 用户是【自己动手开发/搭建】的实践者：XEAOS 企业 AI 系统是用户自己开发的，Obsidian 知识库是自己搭的，',
    '  不是花钱买来的商用系统、也不是采购的软件套餐；',
    '- 用户的经历是：从零开始研究、试错、踩坑、自己写代码/自己搭架构，最终跑通"知识库+AI+执行层"整套系统；',
    '- 所以叙事主语是"我怎么做/我怎么踩坑/我怎么解决"，而不是"我买了什么/我花了多少钱/我选了哪家供应商"；',
    '- 严禁编造购买行为（"花了十几万买系统/买了某软件"等）——用户没有这样的经历，编造会严重损害可信度与共鸣。',
    '【这份提纲的价值定义】',
    '- 提纲的作用 = 分享用户的真实经验与价值，不是产品说明书、也不是业务规划方案；',
    '- 自媒体的流量逻辑 = 持续输出有价值的内容才有流量；',
    '- 最有价值的内容 = 真实经验 + 踩过的坑（观众爱看"我怎么踩坑/我怎么解决"，不爱看工具对比清单）；',
    '- 所以每一条标题都应指向一个"经验点/踩坑点/认知反转"，而不是"工具功能介绍"。',
    '【合集递进结构（出标题时必须遵守）】',
    '- 这是一个合集（连续剧），标题之间必须有递进关系，让观众一眼看出是一套系列：',
    '  · 第 1-5 问 = 认知铺垫：把 Obsidian/企业系统/个人工作台是什么、怎么选、先后顺序、关系讲清楚',
    '  · 第 6-10 问 = 动手实践：搭知识库 → 越用越聪明 → 双端同步 → 大脑+手脚配合 → 30 分钟闭环',
    '- 递进主线 = "搞清楚是什么 → 搭起来 → 用起来 → 跑通全流程"；',
    '【通俗化要求（硬性）】',
    '- 标题必须让大部分普通人看懂，禁止专业术语；',
    '- 专业词改大白话："沉淀-索引-检索"→"越用越聪明"；"Git 同步"→"本地写、服务器自动读"；',
    '  "企业大脑+执行层"→"大脑和手脚怎么配合"；',
    '- 太专业 = 观众划走 = 口播失效。',
    '【标题哲学（v6.2.32 硬性，用户明确：真诚 > 钩子，匹配 > 选择）】',
    '- 标题的逻辑不是"别踩坑/别选错/XX%的人都搞反了"这种制造焦虑的对立式钩子，',
    '  而是"讲清楚各自优势 + 怎么匹配自己的情况"——真诚分享，观众自然有共鸣；',
    '- 核心观点：企业系统/个人工作台/Obsidian 三者【不冲突、各司其职】：',
    '  公司整体运转 → 企业系统；个人单点提效 → 工作台；经验与知识资产 → Obsidian；',
    '- 标题示例（真诚导向）：「企业系统、个人工作台、Obsidian 各管什么事？对号入座就行」；',
    '  禁止示例（焦虑导向）：「别瞎选！到底怎么选才不踩坑？」「90% 的人都搞反了」；',
    '- 允许真实踩坑分享（"我一开始走了弯路"），但目的是分享经验帮别人少走弯路，不是吓人。',
    '【优化大纲时的要求】',
    '1. 围绕自媒体口播逻辑改稿：钩子（抓眼球）、观点（带判断）、口语直给、真实踩坑、',
    '   每集给观众一个"可带走的价值点"（认知/方法/避坑）；',
    '2. 禁止用企业咨询视角（业务目标/团队协作/预算/KPI 体系/成功标准）来引导或改写；',
    '3. 直接产出修改后的完整标题列表，标清"修改自哪条/新增"，不要反问。',
    '',
  ].join('\n');

  const handleHermesChat = async () => {
    const msg = hermesInput.trim();
    if (!msg || hermesBusy) return;
    setHermesMsgs((prev) => [...prev, { role: "user", content: msg }]);
    setHermesInput("");
    setHermesBusy(true);
    try {
      let context = sceneContext;
      // v6.3.13 口吻注入：标题讨论也带上作者真实口吻样本（让 Hermes 讨论的标题/方向就是"作者本人会怎么表达"）
      if (hermesVoiceSample) {
        context += `\n\n【作者个人口吻样本（来自其真实知乎问答，讨论标题/方向时必须模仿这个口吻——判断开场/亲身经历/大白话/本质总结，禁 AI 味）】\n${hermesVoiceSample}\n\n`;
      }
      if (weeklyOutline) {
        context += `【当前周度大纲（来自 Obsidian 10_Operations/Content）】\n${weeklyOutline.slice(0, 2500)}\n\n`;
      }
      const seriesResp = await fetch(`/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Content/口播系列_手把手搭建企业AI系统/00_系列细化大纲.md`)}`).catch(() => null);
      if (seriesResp && seriesResp.ok) {
        const sr = await seriesResp.json();
        if (sr.success && sr.content) {
          const clean = String(sr.content).replace(/^---\n[\s\S]*?\n---\n/, "");
          context += `【口播系列·手把手搭建企业AI系统 细化大纲（来自 Obsidian，讨论 Obsidian/知识库/AI/Hermes 相关问题时参考）】\n${clean.slice(0, 3500)}\n\n`;
        }
      }
      const resp = await RuntimeManager.execute(RuntimeName.Hermes, "chat", { content: msg, context });
      // v6.1.24 修复：RuntimeManager 返回 {success,data}，HermesResponse 回复字段是 data.response
      const inner = resp as any;
      const reply =
        typeof resp === "string" ? resp
        : inner?.data?.response || inner?.response
        || inner?.data?.reply || inner?.reply
        || inner?.data?.content || inner?.content
        || "（Hermes 暂无回复）";
      setHermesMsgs((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err: any) {
      setHermesMsgs((prev) => [...prev, { role: "assistant", content: `⚠️ Hermes 暂时无法回复：${err?.message || err}` }]);
    }
    setHermesBusy(false);
  };

  // 把 Hermes 最后一条回答写回周度大纲文件（读写经 Vault，架构铁律不变）
  const handleApplyHermesOutline = async () => {
    const last = [...hermesMsgs].reverse().find((m) => m.role === "assistant");
    if (!last || !weeklyWeekLabel) return;
    try {
      const markdown = `---\ntitle: "周度内容大纲-${weeklyWeekLabel}"\ndate: ${new Date().toISOString().slice(0, 10)}\ntags:\n  - Operations（运营层）\n  - Content\n  - 周度大纲\n---\n\n${last.content}`;
      await fetch(`/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Content/周度大纲-${weeklyWeekLabel}.md`)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: markdown }),
      });
      // v6.2.9 采纳后清除"默认模板"标记（现在是用户采纳的真实大纲了）
      setWeeklyDefaultApplied(false);
      // v6.2.29 采纳新标题 = 大纲内容变了 → 旧详细版作废：
      // 清空 detailOutline + 指纹，下次打开弹窗/配到每日计划时必基于新标题重新生成，
      // 杜绝"标题是新的、详细内容是旧的"错配（用户担心换标题后对不上）
      setDetailOutline("");
      detailBaseRef.current = "";
      setDetailMsgs([]);
      setDetailInput("");
      // 重新加载周度大纲（await 确保 state 更新后再返回，避免调用方拿到旧值）
      await loadWeeklyOutline();
    } catch { /* 忽略 */ }
  };

  // ============ v6.2.6 第二层：详细内容生成 + 详细内容讨论 ============
  // 第一层（卡片内就地讨论）→ 采纳 → 替换大纲标题
  // 打开弹窗 → 基于【采纳后的周度大纲】生成详细内容（每天/每主题展开）
  // 弹窗右侧 = 独立于第一层的第二层对话框，专门讨论详细内容

  // 组装上下文（第一层/第二层共用）：场景 + 周度大纲 + 口播系列大纲
  const buildHermesContext = useCallback(async () => {
    let context = sceneContext;
    // v6.3.13 口吻注入：详细大纲/Hermes 讨论链路此前缺个人语料 → 生成源头 AI 味；现在注入
    if (hermesVoiceSample) {
      context += `\n\n【作者个人口吻样本（来自其真实知乎问答，展开详细内容时必须模仿这个口吻——判断开场/亲身经历/大白话/本质总结，禁 AI 味，像作者本人写的）】\n${hermesVoiceSample}\n\n`;
    }
    if (weeklyOutline) {
      context += `【当前周度大纲（来自 Obsidian 10_Operations/Content）】\n${weeklyOutline.slice(0, 2500)}\n\n`;
    }
    const seriesResp = await fetch(`/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Content/口播系列_手把手搭建企业AI系统/00_系列细化大纲.md`)}`).catch(() => null);
    if (seriesResp && seriesResp.ok) {
      const sr = await seriesResp.json();
      if (sr.success && sr.content) {
        const clean = String(sr.content).replace(/^---\n[\s\S]*?\n---\n/, "");
        context += `【口播系列·手把手搭建企业AI系统 细化大纲（来自 Obsidian，讨论 Obsidian/知识库/AI/Hermes 相关问题时参考）】\n${clean.slice(0, 3500)}\n\n`;
      }
    }
    return context;
  }, [sceneContext, weeklyOutline, hermesVoiceSample]);

  // 第二层：生成详细内容（打开弹窗时自动执行，或手动重新生成）
  // v6.2.10 返回生成的详细内容（Promise<string>），供「配到每日计划」在详细内容缺失时自动生成
  // v6.2.13 支持外部传入基准大纲 baseOverride（解决恢复历史大纲后的时序竞态：
  //   React setState 异步 → loadWeeklyOutline() 后闭包里的 weeklyOutline 仍是旧值，
  //   恢复后立即打开弹窗/配到每日计划会拿到空基准 → 什么都不生成。传入恢复内容直接生成，不依赖 state）
  // v6.2.15 守卫改用 detailGeneratingRef 实时判断 + force 参数强制生成：
  //   修复"恢复后全是占位符"——守卫读 setState 闭包旧值 true → 短路返回空且不走 finally →
  //   detailGenerating 永久 true → 弹窗永远"生成中"。ref 实时读 + force 绕过守卫。
  const generateDetailOutline = useCallback(async (baseOverride?: string, force?: boolean): Promise<string> => {
    if (detailGeneratingRef.current && !force) return detailOutline;
    // 基准优先级：外部传入 > 周度大纲 weeklyOutline > 第一层采纳后的 Hermes 回答
    const adoptedOutline = [...hermesMsgs].reverse().find((m) => m.role === "assistant")?.content;
    const baseOutline = (baseOverride && baseOverride.trim())
      ? baseOverride
      : ((weeklyOutline && weeklyOutline.trim()) ? weeklyOutline : (adoptedOutline || ""));
    if (!baseOutline.trim()) {
      setDetailOutline("");
      detailBaseRef.current = ""; // v6.2.29 无基准 → 指纹清空（下次入口必重新生成）
      return "";
    }
    setDetailGen(true);
    // v6.2.29 记录基准指纹（取前 60 字符足够区分大纲版本；换标题后指纹必变）
    detailBaseRef.current = baseOutline.trim().slice(0, 60);
    try {
      // v6.2.16 修复核心 bug：baseOverride（恢复的大纲内容）此前只用于判断非空，
      // 真正传给 Hermes 的上下文却是 buildHermesContext() 里的旧 weeklyOutline →
      // Hermes 没看到恢复的标题 → 生成占位符内容（用户反馈"详细版是占位符"的根因）。
      // 现在：始终把 baseOutline 显式注入上下文（无论外部传入还是 state 值），
      // 让 Hermes 基于"当前要展开的真实大纲"生成。
      const baseCtx = await buildHermesContext();
      // v6.3.14 顺序调整：把【待展开的大纲】放在最前，配合 service 侧 14000 截断窗口，
      // 保证真正要展开的大纲 + 作者口吻样本一定进入上下文（此前大纲排在最后，易被截断丢失）。
      const context =
        `【待展开的大纲（以此为准，逐条展开为详细内容）】\n${baseOutline.slice(0, 4000)}\n\n` +
        baseCtx;
      const prompt =
        `请基于上面【待展开的大纲】的每一天主题，展开为详细的选题执行清单（详细内容）。\n` +
        // v6.2.17 注意：prompt 禁用"口播"二字（会触发 VideoPipeline 意图走视频生成流水线，
        // 返回"视频生成流水线已完成"占位符且触发 TTS 自动播报）——用"真人表达/短视频"替代
        // v6.2.26 标题铁律：【标题】必须【原样保留大纲原标题】（用户明确：大纲原标题
        // 比如"别瞎选！企业系统、个人工作台、Obsidian 到底怎么选才不踩坑？"比改写的更好，
        // 之前 Hermes 擅自改写导致详细版与大纲标题不一致）——只展开内容，不修改标题。
        // v6.2.30 用户真实背景 + 叙事铁律：用户是【自己开发/搭建】的实践者（XEAOS 自己开发、
        // Obsidian 自己搭），不是花钱买系统的消费者。叙事必须基于真实经验与踩坑，
        // 严禁编造购买行为（"花了十几万买系统"等）——编造会损害可信度与共鸣。
        `要求（自媒体创作逻辑 + 合集递进 + 通俗化）：\n` +
        `1. 按天分节（周一到周日），每个主题给出：\n` +
        `   【标题】直接原样引用大纲原标题（逐字保留，禁止改写、禁止加"合集第N集"后缀）\n` +
        `   【开场钩子】前 5 秒抓人的一句话\n` +
        `   【内容要点】3-4 个要点（含真实经验/踩坑分享）\n` +
        `   【视频时长】短视频控制在 1 分钟左右（约 250-300 字真人出镜稿，短视频平台优先）；\n` +
        `     公众号/知乎等文章平台可适当展开到 800-1500 字\n` +
        `   【观众带走】一个可带走的价值点\n` +
        `   【平台建议】推荐发哪个平台（抖音/视频号/公众号/小红书）\n` +
        `2. 内容要点通俗易懂，禁止专业术语（"沉淀-索引-检索"→"越用越聪明"等）；\n` +
        `3. 【叙事铁律（硬性，必须遵守）】\n` +
        `   - 用户是"自己动手开发/搭建"的实践者：XEAOS 系统是自己开发的、Obsidian 是自己搭的，\n` +
        `     不是花钱买的商用系统；\n` +
        `   - 叙事主语必须是"我怎么做 / 我怎么踩坑 / 我怎么解决"，禁止"我买了什么 / 我花了多少钱 / 我选了哪家供应商"；\n` +
        `   - 严禁编造购买行为（如"花了十几万买系统/买了某软件"）——用户没有这种经历；\n` +
        `   - 宗旨 = 分享真实经验 + 踩过的坑 + 有价值的判断，让观众有共鸣、觉得你讲得对；\n` +
        `   - 如果某个具体细节你没有真实依据（如具体金额、具体产品名），宁可说得模糊（"我一开始走了弯路"），\n` +
        `     也不要编造具体数字或购买行为。\n` +
        `4. 直接输出 markdown 全文，不要反问，不要只说"好的"。`;
      // v6.2.8 超时控制：Hermes 生成详细内容最长等 3 分钟，避免永久"生成中"占位
      const DETAIL_TIMEOUT_MS = 3 * 60 * 1000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("生成详细内容超时（3 分钟），请重试")), DETAIL_TIMEOUT_MS);
      });
      const resp = await Promise.race([
        RuntimeManager.execute(RuntimeName.Hermes, "chat", {
          content: prompt,
          context,
          // v6.2.18 绕过意图识别：prompt 含"内容/生成/创作"等词极易被误路由到
          // Content/VideoPipeline 生产流水线（返回"视频流水线/内容闭环工作流"占位符）。
          // 生成详细内容是纯对话任务，直接走 handleChat 快速生成。
          bypassIntent: true,
        }),
        timeoutPromise,
      ]);
      const inner = resp as any;
      const reply =
        typeof resp === "string" ? resp
        : inner?.data?.response || inner?.response
        || inner?.data?.reply || inner?.reply
        || inner?.data?.content || inner?.content
        || "（生成失败）";
      setDetailOutline(reply);
      // v6.2.17 生成成功后自动同步到"周度大纲详细版"文件（用户诉求：详细大纲·讨论
      // 与详细版必须关联同步——生成即写入，无需手动点"保存详细内容"）
      // 注意：只有真正生成的内容（非失败提示）才写入，避免把错误提示覆盖进 Vault
      if (reply && !reply.startsWith("⚠️") && !reply.startsWith("（生成失败") && weeklyWeekLabel) {
        try {
          await fetch(`/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Content/周度大纲详细版-${weeklyWeekLabel}.md`)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              content: `---\ntitle: "周度内容大纲详细版-${weeklyWeekLabel}"\ndate: ${new Date().toISOString().slice(0, 10)}\ntags:\n  - Operations（运营层）\n  - Content\n  - 周度大纲\n  - 详细版\n---\n\n# 周度内容大纲详细版 · ${weeklyWeekLabel}\n\n${reply}`,
            }),
          });
        } catch { /* 同步失败不阻断主流程 */ }
      }
      return reply;
    } catch (err: any) {
      const msg = `⚠️ 详细内容生成失败：${err?.message || err}\n\n（基准大纲：${baseOutline.slice(0, 200)}...）`;
      setDetailOutline(msg);
      return msg;
    } finally {
      setDetailGen(false);
    }
  }, [weeklyOutline, hermesMsgs, buildHermesContext, detailOutline]);

  // 打开弹窗：基于采纳后的大纲生成详细内容 + 重置第二层对话
  // v6.2.13 传入当前 weeklyOutline 作基准兜底（恢复历史大纲后 detailOutline 已生成，
  //   generateDetailOutline 内部有 detailGenerating 守卫不会重复生成）
  const openWeeklyDetail = useCallback(() => {
    setWeeklyDetailOpen(true);
    setDetailMsgs([]);
    setDetailInput("");
    // 大纲有变化（或首次）时自动生成详细内容
    generateDetailOutline();
  }, [generateDetailOutline]);

  // 第二层：讨论详细内容（独立于第一层 hermesMsgs）
  const handleDetailChat = async () => {
    const msg = detailInput.trim();
    if (!msg || detailBusy) return;
    setDetailMsgs((prev) => [...prev, { role: "user", content: msg }]);
    setDetailInput("");
    setDetailBusy(true);
    try {
      const baseCtx = await buildHermesContext();
      const context = `${baseCtx}` +
        (detailOutline ? `\n\n【当前详细内容（弹窗左栏，基于采纳后大纲生成）】\n${detailOutline.slice(0, 3000)}\n\n` : "");
      const resp = await RuntimeManager.execute(RuntimeName.Hermes, "chat", {
        content: `${msg}\n\n（这是针对上面详细内容的讨论，请直接基于详细内容回答/修改，输出成品 markdown，不要反问）`,
        context,
        // v6.2.18 讨论详细内容同样是纯对话任务，绕过意图识别防误路由
        bypassIntent: true,
      });
      const inner = resp as any;
      const reply =
        typeof resp === "string" ? resp
        : inner?.data?.response || inner?.response
        || inner?.data?.reply || inner?.reply
        || inner?.data?.content || inner?.content
        || "（Hermes 暂无回复）";
      setDetailMsgs((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err: any) {
      setDetailMsgs((prev) => [...prev, { role: "assistant", content: `⚠️ Hermes 暂时无法回复：${err?.message || err}` }]);
    }
    setDetailBusy(false);
  };

  // 第二层：把详细内容讨论结果写回周度大纲文件（详细版），或仅展示
  const handleApplyDetailOutline = async () => {
    const last = [...detailMsgs].reverse().find((m) => m.role === "assistant");
    const target = last?.content || detailOutline;
    if (!target || !weeklyWeekLabel) return;
    try {
      const markdown = `---\ntitle: "周度内容大纲详细版-${weeklyWeekLabel}"\ndate: ${new Date().toISOString().slice(0, 10)}\ntags:\n  - Operations（运营层）\n  - Content\n  - 周度大纲\n  - 详细版\n---\n\n# 周度内容大纲详细版 · ${weeklyWeekLabel}\n\n${target}`;
      await fetch(`/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Content/周度大纲详细版-${weeklyWeekLabel}.md`)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: markdown }),
      });
      alert(`✅ 详细大纲已写入 Vault：周度大纲详细版-${weeklyWeekLabel}.md`);
    } catch { /* 忽略 */ }
  };

  // ============ v6.2.7 闭环：详细内容 → 每日自媒体计划（各平台按规则差异化生成）============
  const [detailPlanGenerating, setDetailPlanGenerating] = useState(false);
  const handlePlanFromWeeklyDetail = async () => {
    if (detailPlanGenerating) return;
    setDetailPlanGenerating(true);
    try {
      // v6.2.10 确保详细内容存在：优先用已生成的 detailOutline；
      // 若为空（如恢复历史大纲后未打开弹窗），先自动生成详细内容（基于当前大纲/采纳标题）
      let detail = detailOutline;
      if (!detail || detail.startsWith("⚠️")) {
        detail = await generateDetailOutline();
      }
      const adoptedOutline = [...hermesMsgs].reverse().find((m) => m.role === "assistant")?.content;
      const fallback = (weeklyOutline && weeklyOutline.trim()) ? weeklyOutline : (adoptedOutline || "");
      const target = (detail && detail.trim()) ? detail : fallback;
      if (!target.trim()) {
        alert("⚠️ 当前没有可用的大纲内容，请先生成/采纳大纲");
        setDetailPlanGenerating(false);
        return;
      }
      // v6.2.10 无论用详细内容还是标题兜底，都写入"周度大纲详细版"文件，
      // 保证各平台生成时能读取到基准内容（原实现只在 detailOutline 非空时写入 →
      // 恢复历史大纲后详细版缺失 → 各平台生成无基准，用户反馈 bug 的根因）
      const detailPath = `10_Operations（运营层）/Content/周度大纲详细版-${weeklyWeekLabel}.md`;
      await fetch(`/vault-api/write?path=${encodeURIComponent(detailPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: `---\ntitle: "周度内容大纲详细版-${weeklyWeekLabel}"\ndate: ${new Date().toISOString().slice(0, 10)}\ntags:\n  - Operations（运营层）\n  - Content\n  - 周度大纲\n  - 详细版\n---\n\n# 周度内容大纲详细版 · ${weeklyWeekLabel}\n\n${target}`,
        }),
      });
      const service = getContentPlanService();
      const today = new Date().toISOString().slice(0, 10);
      // v6.2.28 关键：把周度大纲原始标题（weeklyOutline）作为最优先解析源传入，
      // 保证配到每日计划的标题与周度大纲一致（用户明确要求用"别瞎选！…"原版标题，
      // 不用 Hermes 在详细版里自由改写的标题）。
      const plan = await service.regeneratePlanFromWeeklyDetail(today, target, detailPath, weeklyOutline);
      if (plan.length > 0) {
        setContentPlan(plan);
        // v6.2.23 强制从 Vault 重读计划（双保险：即使 setContentPlan 未触发重渲染
        // 或页面有多个实例，也保证每日计划面板显示最新内容）
        try {
          const freshPlan = await service.getTodayPlan();
          if (freshPlan.length > 0) setContentPlan(freshPlan);
        } catch { /* 重读失败不阻断 */ }
        // v6.2.24 成功后关闭弹窗并滚动到每日计划面板，让用户直接看到标题变化
        setWeeklyDetailOpen(false);
        const scrollTarget = document.getElementById("daily-plan-section");
        scrollTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
        // v6.2.25 内容矩阵：每个平台都有全部标题（9 平台 × 全部标题）
        alert(`✅ 已把大纲配到每日自媒体计划（${plan.length} 条）！\n每个平台都包含全部标题（9 平台 × ${plan.length / 9} 标题）\n请逐平台点「生成」，将基于详细内容按平台规则生成脚本`);
      } else {
        alert("⚠️ 未从大纲中解析到主题，无法生成计划");
      }
    } catch (err: any) {
      alert(`配到每日计划失败：${err?.message || err}`);
    }
    setDetailPlanGenerating(false);
  };

  // 今日计划文档查看器：界面内直接查看完整计划 Markdown，无需翻找 Obsidian
  const [planDocViewer, setPlanDocViewer] = useState<{ content: string; loading: boolean } | null>(null);

  // 右侧面板视图模式：每日计划 / 脚本库
  const [viewMode, setViewMode] = useState<"plan" | "library">("plan");

  // v6.45 左栏工作台联动：右侧每日计划点「去写稿」→ 左栏 iframe 切到 AI 创作并预选平台
  const [wbGoto, setWbGoto] = useState<WbGoto | null>(null);
  const gotoWorkbenchCreate = (platCn: string) => {
    setWbGoto({ page: "create", plat: platCn, nonce: Date.now() });
  };

  // 脚本库数据
  const [libraryScripts, setLibraryScripts] = useState<ScriptLibraryItem[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  // 脚本内容查看 Modal
  const [viewingScript, setViewingScript] = useState<{ item: ScriptLibraryItem; content: string; loading: boolean } | null>(null);

  // P0/P1 新增：工作室区块切换（P0: 生成工作室/内容日历/质量评分/多平台分发 + P1: 资产库/优化闭环/审核工作流/系列化管理）
  const [studioSection, setStudioSection] = useState<
    | "studio"
    | "content_protocol"
    | "calendar"
    | "quality"
    | "distributor"
    | "assetLibrary"
    | "optimizationLoop"
    | "reviewWorkflow"
    | "seriesManager"
    | "predictPanel"
    | "contentMatrix"
    | "ipPersona"
    | "monetization"
    // v6.3.16 口吻管理
    | "voice"
    // v6.7 订阅号「小舍AI」热度号工作台
    | "subscription"
    // v6.16 内容仓库（查看所有生成内容）
    | "repository"
    // v6.24 官博发布（方案B）
    | "blog"
  >("studio");
  // v6 收敛：低频功能收进"更多▾"下拉
  const [showMoreSections, setShowMoreSections] = useState(false);

  // ============================================
  // 数据加载
  // ============================================

  const loadPackages = useCallback(async () => {
    if (!RuntimeManager.isInitialized()) return;
    try {
      const [listResp, queueResp] = await Promise.all([
        RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" }),
        RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_publish_queue" }),
      ]);
      let list: MediaPackage[] = [];
      if (listResp?.success && listResp.data) {
        list = (listResp.data as any).packages || [];
      }
      if (queueResp?.success && queueResp.data) {
        setPublishQueue((queueResp.data as any).packages || []);
      }
      // v5.2 合并 Vault 直写的新包（对比生成等前端直写产物，Runtime 内存没有）
      try {
        const vaultPkgs = await fetchPkgListFromVault();
        if (vaultPkgs.length > 0) {
          const byId = new Map<string, MediaPackage>();
          for (const p of vaultPkgs) byId.set(p.id, p);
          for (const p of list) byId.set(p.id, p);
          list = Array.from(byId.values());
        }
      } catch {
        // 忽略
      }
      setPackages(list);
    } catch {
      // 忽略
    }
  }, []);

  // Token 优化：轮询等待 RuntimeManager 初始化完成后再加载数据
  // 原固定 500ms 不足以等待 30+ Runtime 全部初始化，导致"尚未初始化"报错
  useEffect(() => {
    let cancelled = false;
    const waitAndLoad = async () => {
      // v5.2：读取 GEO 策略跳转带入的话题（打通 GEO→内容 最小闭环衔接）
      try {
        const gotoTopic = localStorage.getItem("xeaos_goto_topic");
        if (gotoTopic) {
          setTopic(gotoTopic);
          localStorage.removeItem("xeaos_goto_topic");
        }
      } catch { /* 忽略 */ }
      for (let i = 0; i < 60; i++) {
        if (cancelled) return;
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (cancelled) return;
      loadPackages();
      loadContentPlan();
    };
    waitAndLoad();
    return () => { cancelled = true; };
  }, [loadPackages]);

  // v5.2 修复：计划项状态回填（刷新后查阅/资产库仍可用）
  // 根因：页面刷新后 contentPlan 从 Vault 重新加载，所有项回到"待生成"，
  //       导致 completed 才出现的「查阅/同步」按钮消失、资产库(currentPackage)为空。
  // 解法：从已恢复的包列表（Vault 产物）按 topic 反查，把匹配项回填为 completed + packageId。
  useEffect(() => {
    if (packages.length === 0) return;
    setContentPlan((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        if (item.status === 'completed' || item.status === 'published' || item.status === 'generating') return item;
        const hit =
          packages.find((p) => p.topic === item.topic) ||
          packages.find((p) => p.topic && item.topic && (p.topic.includes(item.topic) || item.topic.includes(p.topic)));
        if (hit && (hit.status === 'completed' || hit.status === 'partial')) {
          changed = true;
          return { ...item, status: 'completed' as const, packageId: hit.id };
        }
        return item;
      });
      return changed ? next : prev;
    });
  }, [packages]);

  // ============================================
  // 加载每日自媒体内容计划
  // ============================================
  const loadContentPlan = useCallback(async () => {
    setPlanLoading(true);
    try {
      const service = getContentPlanService();
      const plan = await service.getTodayPlan();
      setContentPlan(plan);
      // v6.2.27 默认展开有内容的平台（避免首次加载全收起看不到内容）
      const withItems = new Set(plan.map((p) => p.platform));
      setExpandedPlatforms(withItems);
    } catch {
      // 忽略
    } finally {
      setPlanLoading(false);
    }
  }, []);

  // ============================================
  // 加载今日计划的完整 Markdown 文档内容，供界面内直接查看
  // ============================================
  const loadTodayPlanDoc = async () => {
    setPlanDocViewer({ content: '', loading: true });
    try {
      const today = new Date().toISOString().slice(0, 10);
      const path = `10_Operations（运营层）/Content/每日自媒体计划-${today}.md`;
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`);
      const data = await resp.json();
      if (data.success && data.content) {
        setPlanDocViewer({ content: data.content, loading: false });
      } else {
        setPlanDocViewer({ content: '今日计划尚未生成，请先点击「刷新今日计划」按钮生成。', loading: false });
      }
    } catch (err: any) {
      setPlanDocViewer({ content: `读取失败：${err?.message || String(err)}`, loading: false });
    }
  };

  // ============================================
  // 手动刷新今日计划（基于 Obsidian 知识库重新生成）
  // ============================================
  const handleRefreshTodayPlan = async () => {
    setRefreshingPlan(true);
    setRefreshMessage(null);
    try {
      const service = getContentPlanService();
      const plan = await service.refreshTodayPlan();
      if (plan.length > 0) {
        // 刷新成功：用新生成的计划覆盖当前 contentPlan
        setContentPlan(plan);
        setRefreshMessage({
          success: true,
          message: `已基于 Obsidian 知识库刷新今日计划，共 ${plan.length} 条`,
        });
      } else {
        // Obsidian 知识库无可读内容
        setRefreshMessage({
          success: false,
          message: '刷新失败：Obsidian 知识库无可读内容',
        });
      }
    } catch (err: any) {
      setRefreshMessage({
        success: false,
        message: `刷新异常：${err?.message || String(err)}`,
      });
    } finally {
      setRefreshingPlan(false);
      // 5 秒后自动清除提示
      setTimeout(() => setRefreshMessage(null), 5000);
    }
  };

  // ============================================
  // 加载脚本库（Vault 中持久化的历史脚本）
  // ============================================
  const loadLibraryScripts = useCallback(async () => {
    if (!RuntimeManager.isInitialized()) return;
    setLibraryLoading(true);
    try {
      const resp = await RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_scripts" });
      if (resp?.success && resp.data) {
        setLibraryScripts((resp.data as any).scripts || []);
      }
    } catch {
      // 忽略
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  // 切换到脚本库视图时按需加载
  useEffect(() => {
    if (viewMode === "library" && libraryScripts.length === 0 && !libraryLoading) {
      loadLibraryScripts();
    }
  }, [viewMode, libraryScripts.length, libraryLoading, loadLibraryScripts]);

  // ============================================
  // 查看脚本内容（通过 /vault-api/read 读取 Markdown 全文）
  // ============================================
  const handleViewScript = async (item: ScriptLibraryItem) => {
    setViewingScript({ item, content: "", loading: true });
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(item.path)}`);
      if (resp.ok) {
        const result = await resp.json();
        setViewingScript({ item, content: result.content || "", loading: false });
      } else {
        setViewingScript({ item, content: "（读取失败）", loading: false });
      }
    } catch (err: any) {
      setViewingScript({ item, content: `（读取异常：${err?.message || String(err)}）`, loading: false });
    }
  };

  // ============================================
  // 按内容计划生成素材（点击即生成）
  // ============================================

  // v6.2.27 从详细版提取当前 topic 对应的主题段落（生成时只围绕该条内容，不整份喂给 LLM）
  // 匹配策略：按标题名定位 `### 主题N：【标题】` 或 `### 主题N：【改写标题】` 所在段落，
  // 从该段落开始到下一个 `### 主题` / `##` 分节前截取。
  const extractDetailSegment = (detailRaw: string, topicKey: string): string => {
    const key = (topicKey || '').trim();
    if (!key) return '';
    // 标题可能含特殊字符，用核心词（去标点前 12 字）做包含匹配定位
    const topicCore = key.replace(/[【】《》（）!！?？,，.。:：、\s"']/g, '').slice(0, 12);
    if (!topicCore) return '';
    const lines = detailRaw.split('\n');
    let startIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // 候选标题行（v6.2.28 兼容 Hermes 三种输出格式）：
      //   A. `### 主题1：【原始大纲标题】`（带冒号+【】）
      //   B. `### 主题1` 换行 `【标题】xxx`（无冒号无【】——服务器当前实际格式）
      //   C. `- **标题**：xxx`（列表项）
      const isTopicHeader = /^#{2,5}\s*主题/.test(line);
      const isTitleLine = /[-*]\s*\*\*标题\*\*[：:]/.test(line) || /^【(?:口播)?标题】/.test(line.trim());
      if (!isTopicHeader && !isTitleLine) continue;
      // 组合候选行文本：若 ### 主题N 自身无标题文字，把下一行【标题】拼进来
      let combined = line;
      if (isTopicHeader && !/【/.test(line)) {
        const next = lines[i + 1] || '';
        if (/^【(?:口播)?标题】/.test(next.trim())) combined = line + next;
      }
      const lineCore = combined.replace(/[【】《》（）!！?？,，.。:：、\s*#>]/g, '');
      if (topicCore && lineCore.includes(topicCore.slice(0, 8))) {
        startIdx = i;
        break;
      }
    }
    if (startIdx === -1) return '';
    // 截取到下一个分节（### 主题 / ## 分节 / ---）
    const segment: string[] = [];
    for (let j = startIdx; j < lines.length; j++) {
      if (j > startIdx && (/^#{2,5}\s*主题/.test(lines[j]) || /^##\s/.test(lines[j]) || /^---\s*$/.test(lines[j]))) {
        break;
      }
      segment.push(lines[j]);
    }
    return segment.join('\n').slice(0, 3000);
  };

  const handleGenerateFromPlanItem = async (item: ContentPlanItem, variation?: string) => {
    // 轮询等待 RuntimeManager 就绪（替代硬 alert 失败）
    const ready = await waitForRuntime();
    if (!ready) {
      alert("RuntimeManager 初始化超时，请刷新页面重试");
      return;
    }

    // 更新计划状态为生成中
    setContentPlan((prev) =>
      prev.map((p) => (p.id === item.id ? { ...p, status: 'generating' } : p))
    );

    // 阶段 1：启动生成
    setGenerating({
      active: true,
      topic: item.topic,
      progress: 10,
      message: `正在为${PLATFORM_CONFIG[item.platform].name}生成：${item.topic}`,
      error: false,
    platform: item.platform,
    itemId: item.id,
    });

    try {
      const description = `为${PLATFORM_CONFIG[item.platform].name}生成内容：${item.topic}`;

      // 阶段 2：搜索知识库（Runtime 内部执行，此处更新进度提示）
      setGenerating({
        active: true,
        topic: item.topic,
        progress: 30,
        message: "正在搜索知识库...",
        error: false,
      platform: item.platform,
      itemId: item.id,
      });

      // 阶段 3：生成脚本与封面（Runtime 内部并发执行）
      // 注意：脚本生成调用 LLM（DeepSeek），封面生成调用图片 API（多米），
      // 两者并发执行，总耗时取决于较慢的一方。LLM 通常 10-60s，图片 API 1-5min。
      setGenerating({
        active: true,
        topic: item.topic,
        progress: 50,
        message: "正在生成脚本与封面（脚本10-60s + 封面1-5min 并发，请耐心等待）...",
        error: false,
      platform: item.platform,
      itemId: item.id,
      });

      // 超时控制：5 分钟超时（封面生成最慢约 5 分钟），避免 UI 永久卡死
      const GENERATE_TIMEOUT_MS = 5 * 60 * 1000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('生成超时（5分钟）：请检查 LLM/图片 API 是否可用，或稍后重试')), GENERATE_TIMEOUT_MS);
      });

      // v6.1 知乎问答模式：itemType==='qa' 时按问题生成回答；
      // v6.1.2 修复：用户输入问题但未点"设为问题"时，itemType 仍为 undefined →
      // qaMode=false → 走了文章模板（用户反馈"回答还是文章"的根因）。
      // 改为：知乎条目只要有问题内容（草稿或已保存），一律按问答模式生成。
      // v6.20 多问题聚焦：一次输入多个问题（换行/序号分隔）时只取第一个生成（避免答非所问）
      // v6.21 修复：原正则把"问1、"拆成"问"字 → 兼容 问1、/问题2：/Q3./3. ① 等编号格式
      const zhihuDraftText = (zhihuDraft[item.id]?.trim() || item.question || '');
      const zhihuQuestion = item.platform === 'zhihu'
        ? (() => {
            const t = zhihuDraftText.trim();
            if (!t) return '';
            const cleaned = t
              .replace(/^(?:问|问题|Q|q)?\s*\d+\s*[、.．)）:：]\s*/, '')
              .replace(/^[①②③④⑤⑥⑦⑧⑨⑩]\s*/, '')
              .trim();
            const parts = cleaned
              .split(/\n|(?:问\d+\s*[、.．)）:：])|(?:问题\d+\s*[、.．)）:：])|(?:[Qq]\d+\s*[、.．)）:：])|(?:\d+\s*[、.．)）:：])/)
              .map((s) => s.trim())
              .filter(Boolean);
            return parts[0] || cleaned;
          })()
        : '';
      const isZhihuQa = item.platform === 'zhihu' && (item.itemType === 'qa' || !!zhihuQuestion);
      // v6.2.7 闭环：当 knowledgeNote 是"关联：详细内容路径"或裸路径（.md 结尾）时，
      // 读取详细内容作为生成上下文——各平台基于同一份详细内容、按各自平台规则差异化生成。
      // v6.2.22 修复：v6.2.20 起 knowledgeNote 已不带"关联："前缀（裸路径），
      // 旧判断 startsWith('关联：') 不命中 → 把路径字符串当上下文传给生成器 → 读不到详细内容。
      // v6.2.27 修复：生成时只提取【当前条目 topic 对应的一段】而非整份详细版——
      // 旧实现把整个详细版 4000 字（含全部 10 个主题）传给 LLM，Hermes 分不清围绕哪个写，
      // 导致"标题和内容对不上"（用户反馈核心 bug）。同时注入个人口吻样本。
      let genContext = item.platform === 'zhihu' && isZhihuQa ? '' : item.knowledgeNote || '';
      const looksLikePath =
        genContext.startsWith('关联：') ||
        /\.md$/.test(genContext.trim()) ||
        genContext.trim().startsWith('10_Operations') ||
        genContext.trim().startsWith('XEAOS/');
      if (genContext && looksLikePath) {
        const notePath = genContext.replace(/^关联：\s*/, '').trim();
        try {
          const noteResp = await fetch(`/vault-api/read?path=${encodeURIComponent(notePath)}`);
          if (noteResp.ok) {
            const nr = await noteResp.json();
            if (nr.success && nr.content) {
              const detailRaw = String(nr.content).replace(/^---\n[\s\S]*?\n---\n/, '');
              // 定位当前条目 topic 对应的主题段落（### 主题N：【标题】 或 ### 主题N：【改写标题】）
              const topicKey = item.topic.trim();
              // v6.2.28 修复：extractDetailSegment 是组件内箭头函数，不能 this 调用
              // （React 函数组件内 this 为 undefined，会抛错 → 生成中断）
              const segment = extractDetailSegment(detailRaw, topicKey);
              genContext =
                `【基准内容（周度大纲详细内容·当前主题段落，按此展开，主体不变）】\n` +
                `${segment || detailRaw.slice(0, 3000)}\n\n` +
                `【本次任务】围绕标题「${item.topic}」按平台「${item.platform}」规则生成内容：` +
                `开头钩子抓人、内容用大白话含真实经验/踩坑、有可带走的价值点，正文口语化像真人写的。`;
            }
          }
        } catch { /* 读取失败则回退原值 */ }
      }
      let resp: any;
      try {
        resp = await Promise.race([
          RuntimeManager.execute(
            RuntimeName.MediaStudio,
            "generate_script",
            {
              platform: item.platform,
              topic: item.topic,
              description,
              // v6.1.3 知乎问答模式不传旧主题的关联笔记（knowledgeNote 是文章模式的素材，
              // 问答模式要客观，避免旧主题笔记把回答带偏成围绕系统的内容）
              context: genContext,
              // v6.3.15 换内容差异化：重新生成时传随机角度指令，避免每次内容一样
              variation: variation || "",
              style, // 传入视觉风格，供封面生成使用
              brand: brandId, // v5.2 审计修复 P1：品牌选择进入内容约束（generate_brief）
              skipCover: true, // v5.2 生成流程拆分：先生成内容，查阅OK后同步时再出标题+封面（省成本）
              // v6.1 知乎问答模式：有问题即按回答生成（不再强制要求点"设为问题"）
              qaMode: isZhihuQa,
              question: zhihuQuestion,
            }
          ),
          timeoutPromise,
        ]);
      } catch (timeoutErr: any) {
        // 超时或异常：更新计划状态 + 显示错误提示
        setContentPlan((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p))
        );
        setGenerating({
          active: false,
          topic: "",
          progress: 0,
          message: timeoutErr?.message || "生成超时",
          error: true,
        platform: null,
        itemId: null,
        });
        return;
      }

      if (resp?.success && resp.data) {
        // v5.2.2 关键修复：resp.data 是 MediaStudioResult 包装层（{success,data,runtime,timestamp}），
        // 真正的 MediaPackage 在 resp.data.data。若直接取 resp.data → pkg.id=undefined →
        // 计划项 packageId 写空 → 查阅/预览/发布按钮全部消失（用户反馈 bug 的根因）。
        // 用系统标准解包方法剥到最里层。
        const wrapped = resp.data as any;
        // v6.32 修复：RuntimeManager 的 success 恒为"执行未抛异常"（true），业务失败在
        // resp.data.success=false（generate_script 内 LLM/DeepSeek 失败时返回 success:false）。
        // 旧代码只看外层 resp.success → 把生成失败误判成功 → 计划标 completed 但无 packageId、
        // 无内容包 → 查阅永远找不到（用户反馈"今日头条生成的文章不能查阅"根因）。
        if (wrapped && typeof wrapped === "object" && wrapped.success === false) {
          setContentPlan((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p))
          );
          setGenerating({
            active: false,
            topic: "",
            progress: 0,
            message: wrapped.error || "生成失败（LLM/DeepSeek 调用异常）",
            error: true,
            platform: null,
            itemId: null,
          });
          return;
        }
        const pkg = (wrapped && typeof wrapped === "object" && wrapped.data && (wrapped.data.id || wrapped.data.topic))
          ? (wrapped.data as MediaPackage)
          : (wrapped as MediaPackage);

        // 阶段 4：保存到素材库
        setGenerating({
          active: true,
          topic: item.topic,
          progress: 90,
          message: "正在保存到素材库...",
          error: false,
        platform: item.platform,
        itemId: item.id,
        });

        // 自动选中新生成的包，左侧预览区立即展示脚本与封面
        setCurrentPackage(pkg);
        setContentPlan((prev) =>
          prev.map((p) =>
            p.id === item.id ? { ...p, status: 'completed', packageId: pkg.id } : p
          )
        );

        // v5.2 根治：生成状态写回 Obsidian 计划文件（Rule 05）
        // 刷新后 parsePlanFromMarkdown 直接恢复"已生成"，不再回"待生成"
        getContentPlanService()
          .updatePlanItemStatus(item.date, item.platform, item.topic, 'completed', pkg.id)
          .then((ok) => {
            if (!ok) console.warn('[ContentStudio] 计划状态写回失败，将依赖内存回填');
          });

        // v5.2：生成完成后自动弹出预览（封面/标题/脚本一步到位）
        const autoScript = pkg.articleScript || pkg.videoScript || pkg.xiaohongshuScript || pkg.bilibiliScript || pkg.douyinScript || pkg.momentsScript;
        if (autoScript) {
          setEditingScript({ asset: autoScript, content: autoScript.content || '' });
        } else if (pkg.cover?.url) {
          setPreview({ type: 'image', url: pkg.cover.url, title: item.topic });
        }

        // v5.2.1 防假内容：校验生成结果是否仍是模板占位符（历史 bug 曾把占位符当成功）
        const scriptText = autoScript?.content || '';
        const isPlaceholder = /第 \d+ 点：围绕「|展开核心论述/.test(scriptText) || /【约束版 · 完整链路】|【裸写版 · 无约束】/.test(scriptText);
        if (isPlaceholder) {
          // 占位符说明生成链路异常 → 标记失败，不落库
          setContentPlan((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p)));
          setGenerating({
            active: false,
            topic: "",
            progress: 0,
            message: "检测到模板占位内容（生成链路异常），未保存。请检查 DeepSeek 配置后重试",
            error: true,
          platform: null,
          itemId: null,
          });
          return;
        }

        // 阶段 5：完成（v5.2 确认：产出为流程链路版 = 新闻→Hermes→Obsidian→约束→生成）
        const hasCover = !!pkg.cover;
        setGenerating({
          active: false,
          topic: "",
          progress: 100,
          message: `${PLATFORM_CONFIG[item.platform].name}素材生成完成（流程链路版 · 脚本${hasCover ? " + 封面" : ""}）`,
          error: false,
        platform: null,
        itemId: null,
        });
        loadPackages();
      } else {
        // 生成失败：更新计划状态 + 显示错误提示（红色）
        setContentPlan((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p))
        );
        setGenerating({
          active: false,
          topic: "",
          progress: 0,
          message: resp?.error || "生成失败",
          error: true,
        platform: null,
        itemId: null,
        });
      }
    } catch (err: any) {
      // 异常：更新计划状态 + 显示错误提示（红色）
      setContentPlan((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p))
      );
      setGenerating({
        active: false,
        topic: "",
        progress: 0,
        message: err?.message || "生成异常",
        error: true,
      platform: null,
      itemId: null,
      });
    }
  };

  // ============================================
  // 一键生成全部待处理计划（串行，避免 API 限流）
  // ============================================
  const handleGenerateAllPending = async () => {
    // 轮询等待 RuntimeManager 初始化完成（30+ Runtime 全部初始化需要时间）
    if (!RuntimeManager.isInitialized()) {
      setGenerating({
        active: true,
        topic: "等待初始化",
        progress: 5,
        message: "RuntimeManager 正在初始化，请稍候...",
        error: false,
        platform: null,
        itemId: null,
      });
      for (let i = 0; i < 60; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setGenerating({
          active: false,
          topic: "",
          progress: 0,
          message: "RuntimeManager 初始化超时，请刷新页面重试",
          error: true,
          platform: null,
          itemId: null,
        });
        return;
      }
      setGenerating({ active: false, topic: "", progress: 0, message: "", error: false, platform: null, itemId: null });
    }
    const pendingItems = contentPlan.filter((item) => item.status === 'pending');
    if (pendingItems.length === 0) {
      alert("没有待生成的计划项，请先点击「刷新今日计划」从 CEO Workspace 加载今日计划");
      return;
    }
    // 串行调用，避免 API 限流
    for (const item of pendingItems) {
      await handleGenerateFromPlanItem(item);
    }
  };

  // ============================================
  // 按平台分类生成（一键生成该平台所有待处理计划）
  // ============================================
  const handleGenerateByPlatform = async (platform: SocialPlatform) => {
    // 轮询等待 RuntimeManager 初始化
    if (!RuntimeManager.isInitialized()) {
      setGenerating({
        active: true,
        topic: "等待初始化",
        progress: 5,
        message: "RuntimeManager 正在初始化，请稍候...",
        error: false,
        platform,
        itemId: null,
      });
      for (let i = 0; i < 60; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setGenerating({
          active: false,
          topic: "",
          progress: 0,
          message: "RuntimeManager 初始化超时，请刷新页面重试",
          error: true,
          platform: null,
          itemId: null,
        });
        return;
      }
      setGenerating({ active: false, topic: "", progress: 0, message: "", error: false, platform: null, itemId: null });
    }
    const platformPending = contentPlan.filter(
      (item) => item.platform === platform && (item.status === 'pending' || item.status === 'failed')
    );
    if (platformPending.length === 0) {
      alert(`${PLATFORM_CONFIG[platform].name} 没有待生成的计划`);
      return;
    }
    // 串行调用，避免 API 限流
    for (const item of platformPending) {
      await handleGenerateFromPlanItem(item);
    }
  };

  // ============================================
  // 编辑脚本
  // ============================================
  const handleEditScript = (asset: MediaAsset) => {
    setEditingScript({ asset, content: asset.content || "" });
  };

  const handleSaveScript = async () => {
    if (!editingScript || !currentPackage) return;
    const updated = { ...currentPackage };
    if (editingScript.asset.purpose === 'article_script' && updated.articleScript) {
      updated.articleScript = { ...updated.articleScript, content: editingScript.content };
    } else if (editingScript.asset.purpose === 'video_script' && updated.videoScript) {
      updated.videoScript = { ...updated.videoScript, content: editingScript.content };
    } else if (editingScript.asset.purpose === 'xiaohongshu_script' && updated.xiaohongshuScript) {
      updated.xiaohongshuScript = { ...updated.xiaohongshuScript, content: editingScript.content };
    } else if (editingScript.asset.purpose === 'bilibili_script' && updated.bilibiliScript) {
      updated.bilibiliScript = { ...updated.bilibiliScript, content: editingScript.content };
    } else if (editingScript.asset.purpose === 'douyin_script' && updated.douyinScript) {
      updated.douyinScript = { ...updated.douyinScript, content: editingScript.content };
    }
    setCurrentPackage(updated);

    // v5.2 保存去向透明化：保存 = 更新当前产物内容 + 持久化到 Obsidian（Rule 05 WriteBack）
    // 保存位置：Obsidian → XEAOS/ContentPackages/{日期}/{packageId}.md（刷新后仍可恢复）
    let saveOk = false;
    if (updated._vaultPath || updated.id) {
      try {
        const existing = updated._vaultPath;
        // 读取现有产物文件 → 替换对应脚本段 → 写回
        const filePath = existing || `XEAOS/ContentPackages/${(updated.createdAt || new Date().toISOString()).slice(0, 10)}/${updated.id}-topic.md`;
        if (existing) {
          const rr = await fetch(`/vault-api/read?path=${encodeURIComponent(filePath)}`);
          const rj = await rr.json();
          let content = rj.success ? String(rj.content || '') : '';
          if (content) {
            // 用新的脚本内容替换原 XEAOS_SCRIPT 段（保留 frontmatter 与其他素材段）
            const purpose = editingScript.asset.purpose;
            const marker = `<!-- XEAOS_SCRIPT:${purpose} -->`;
            const startIdx = content.indexOf(marker);
            if (startIdx >= 0) {
              const after = content.indexOf('<!-- XEAOS_SCRIPT:', startIdx + marker.length);
              const endIdx = after >= 0 ? after : content.length;
              const newSection = `${marker}\n${editingScript.content}\n\n`;
              content = content.slice(0, startIdx) + newSection + content.slice(endIdx);
            } else {
              content += `\n${marker}\n${editingScript.content}\n\n`;
            }
            const wr = await fetch(`/vault-api/write?path=${encodeURIComponent(filePath)}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ content }),
            });
            const wj = await wr.json();
            saveOk = wj.success === true;
          }
        }
      } catch (err) {
        console.warn('[ContentStudio] 脚本保存到 Vault 失败:', err);
      }
    }

    setEditingScript(null);
    // 明确告知保存去向（Rule 05 写回 + 可追溯）
    const where = updated._vaultPath || `Obsidian Vault → XEAOS/ContentPackages/${(updated.createdAt || '').slice(0, 10)}/`;
    setSaveTip(saveOk
      ? `✅ 已保存到 ${where}`
      : `⚠️ 已保存到当前会话（Vault 写回失败：${where}），建议点「同步」持久化到营销中心`);
  };

  // ============================================
  // 发布到平台（微信全自动，其他平台提示手动）
  // ============================================
  const handlePublishToPlatform = async (item: ContentPlanItem, pkg: MediaPackage) => {
    setPublishing(item.id);
    const platformConfig = PLATFORM_CONFIG[item.platform];

    try {
      if (item.platform === 'wechat' && platformConfig.autoPublish) {
        // 微信公众号全自动发布
        const wechatService = getWeChatPublishService();
        if (!wechatService.isConfigured()) {
          setPublishResult({
            ...publishResult,
            [item.id]: { success: false, message: "微信公众号未配置，请在设置中配置 AppID 和 AppSecret" },
          });
          return;
        }

        // 获取文章脚本
        const script = pkg.articleScript?.content || '';
        if (!script) {
          setPublishResult({
            ...publishResult,
            [item.id]: { success: false, message: "缺少文章脚本内容" },
          });
          return;
        }

        // 获取封面图
        const coverUrl = pkg.cover?.url || pkg.wechatCover?.url;
        // 封面图缺失时给出明确提示，避免静默失败
        if (!coverUrl) {
          setPublishResult({
            ...publishResult,
            [item.id]: { success: false, message: "缺少封面图，请先生成封面图（文章封面或公众号配图）后再发布" },
          });
          return;
        }

        const result = await wechatService.autoPublish({
          title: item.topic,
          content: script,
          thumbMediaId: '',
          coverUrl,
          author: 'XEAOS',
        });

        setPublishResult({
          ...publishResult,
          [item.id]: { success: result.success, message: result.success ? "已自动发布到微信公众号" : (result.error || "发布失败") },
        });

        if (result.success) {
          setContentPlan((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, status: 'published' } : p))
          );
          // v5.2 根治：发布状态同样写回 Obsidian 计划文件（刷新后保持"已发布"）
          getContentPlanService()
            .updatePlanItemStatus(item.date, item.platform, item.topic, 'published', item.packageId)
            .catch(() => {});
          // v6.3.17 知识飞轮入口：公众号发布成功 → 记录内容结果（零 token 入库，待人工回填效果）
          recordContentResultToExperience({
            id: item.packageId || `pub-${Date.now()}`,
            title: item.topic,
            topic: item.topic,
            domain: '企业AI',
            platform: '公众号',
            metrics: { views: 0, likes: 0 },
          }).catch(() => {});
          // Phase 2.2 闭环：公众号自动发布成功 → 记录内容引流线索（source=公众号）
          recordCrmLeadFromContent({ platform: '公众号', title: item.topic }).catch(() => {});
        }
      } else {
        // 抖音/小红书/知乎：提示手动发布
        setPublishResult({
          ...publishResult,
          [item.id]: {
            success: true,
            message: `${platformConfig.name}需手动发布，素材已准备好，点击"查看素材"获取内容`,
          },
        });
        // v6.3.17 知识飞轮入口：手动发布提示 = 记录为"待回填"结果（发布后人工补效果指标）
        recordContentResultToExperience({
          id: item.packageId || `pub-${Date.now()}`,
          title: item.topic,
          topic: item.topic,
          domain: '企业AI',
          platform: platformConfig.name,
          metrics: { views: 0, likes: 0 },
        }).catch(() => {});
        // Phase 2.2 闭环：手动发布平台（抖音/小红书/知乎等）→ 记录内容引流线索（source=平台）
        recordCrmLeadFromContent({ platform: platformConfig.name, title: item.topic }).catch(() => {});
      }
    } catch (err: any) {
      setPublishResult({
        ...publishResult,
        [item.id]: { success: false, message: err?.message || "发布异常" },
      });
    } finally {
      setPublishing(null);
    }
  };

  // ============================================
  // 按素材类型分类生成（图片 / 视频 / 文本 / 音乐）
  // 用户可只生成某一类素材，不必每次生成全套
  // ============================================
  type GenerateCategory = "images" | "video" | "scripts" | "music";

  const CATEGORY_COMMANDS: Record<GenerateCategory, MediaStudioCommand[]> = {
    // 图片：直接用用户输入作为提示词文生图（支持自定义比例/清晰度）
    images: ["generate_custom"],
    // 视频：短视频
    video: ["generate_video"],
    // 文本：公众号 / 视频 / 小红书 / B站 / 抖音脚本
    scripts: [
      "generate_article_script",
      "generate_video_script",
      "generate_xiaohongshu_script",
      "generate_bilibili_script",
      "generate_douyin_script",
    ],
    // 音乐：BGM
    music: ["generate_bgm"],
  };

  const CATEGORY_LABELS: Record<GenerateCategory, { label: string; hint: string }> = {
    images: { label: "图片", hint: "文生图（直接用输入作为提示词）" },
    video: { label: "视频", hint: "短视频" },
    scripts: { label: "文本", hint: "各平台脚本" },
    music: { label: "音乐", hint: "BGM 背景乐" },
  };

  const handleGenerateByCategory = async (category: GenerateCategory) => {
    if (!topic.trim()) {
      alert("请输入主题");
      return;
    }
    const ready = await waitForRuntime();
    if (!ready) {
      setGenerating({
        active: false,
        topic: "",
        progress: 0,
        message: "RuntimeManager 初始化超时，请刷新页面重试",
        error: true,
        platform: null,
        itemId: null,
      });
      return;
    }

    const commands = CATEGORY_COMMANDS[category];
    setGenerating({
      active: true,
      platform: null,
      itemId: null,
      topic,
      progress: 10,
      message: `正在生成${CATEGORY_LABELS[category].label}类素材...`,
      error: false,
    });

    try {
      const results: { command: string; ok: boolean; pkg?: MediaPackage }[] = [];
      for (let i = 0; i < commands.length; i++) {
        const command = commands[i];
        setGenerating({
          active: true,
          topic,
          progress: Math.round(10 + (i / commands.length) * 70),
          message: `正在生成 ${CATEGORY_LABELS[category].hint}（${i + 1}/${commands.length}）...`,
          error: false,
          platform: null,
          itemId: null,
        });
        // 各命令失败不阻断后续素材生成（与 generate_full_package 降级策略一致）
        try {
          // generate_custom（文生图）需要 type/prompt/opts 参数；
          // 其余封面/脚本命令沿用 title/description/style 参数
          const isCustomImage = command === "generate_custom";
          const resp = await RuntimeManager.execute(
            RuntimeName.MediaStudio,
            command,
            isCustomImage
              ? {
                  type: "image",
                  prompt: topic, // 直接用用户输入作为文生图提示词
                  topic,
                  opts: { ratio: "方图", clarity: "1K", mode: "文生图" },
                }
              : {
                  title: topic,
                  description,
                  style,
                  topic,
                  mood: "科技感",
                }
          );
          // generate_custom 现已直接返回 MediaPackage（含持久化到 Vault）
          const pkgData = resp?.success && resp.data ? resp.data as MediaPackage : undefined;
          results.push({ command, ok: !!resp?.success, pkg: pkgData });
          // 生成成功则选中该包，预览区立即展示
          if (pkgData) {
            setCurrentPackage(pkgData);
            // 自动切换到对应分类的 Tab（图片→图片 / 视频→视频 / 文本→脚本 / 音乐→音乐）
            setActiveTab(category as AssetTab);
          }
        } catch (e) {
          results.push({ command, ok: false });
        }
      }

      const okCount = results.filter((r) => r.ok).length;
      setGenerating({
        active: false,
        topic: "",
        progress: 100,
        message:
          okCount === 0
            ? `${CATEGORY_LABELS[category].label}类素材生成失败`
            : okCount === commands.length
            ? `✅ ${CATEGORY_LABELS[category].label}类素材生成完成（${okCount}/${commands.length}）`
            : `✅ ${CATEGORY_LABELS[category].label}类素材部分完成（${okCount}/${commands.length}）`,
        error: okCount === 0,
        platform: null,
        itemId: null,
      });
      if (okCount > 0) loadPackages();
    } catch (err: any) {
      setGenerating({
        active: false,
        topic: "",
        progress: 0,
        message: err?.message || "分类生成异常",
        error: true,
        platform: null,
        itemId: null,
      });
    }
  };



  // ============================================
  // 按日计划自动生成
  // ============================================

  const handleGenerateFromPlan = async () => {
    // 轮询等待 RuntimeManager 就绪（替代硬 alert 失败）
    const ready = await waitForRuntime();
    if (!ready) {
      setGenerating({
        active: false,
        topic: "",
        progress: 0,
        message: "RuntimeManager 初始化超时，请刷新页面重试",
        error: true,
      });
      return;
    }

    setGenerating({
      active: true,
      topic: "今日计划",
      progress: 10,
      message: "正在读取今日计划并提取主题...",
      error: false,
    });

    try {
      const resp = await RuntimeManager.execute(
        RuntimeName.MediaStudio,
        "generate_from_plan",
        { style }
      );

      if (resp?.success && resp.data) {
        const data = resp.data as any;
        const pkgs: MediaPackage[] = data.packages || [];
        if (pkgs.length > 0) {
          setCurrentPackage(pkgs[0]);
          setGenerating({
            active: false,
            topic: "",
            progress: 100,
            message: `按计划生成完成：${pkgs.length} 个媒体包，主题：${(data.topics || []).join("、")}`,
            error: false,
          });
          loadPackages();
        } else {
          setGenerating({
            active: false,
            topic: "",
            progress: 0,
            message: "未生成任何媒体包",
            error: true,
          });
        }
      } else {
        setGenerating({
          active: false,
          topic: "",
          progress: 0,
          message: resp?.error || "按计划生成失败",
          error: true,
        });
      }
    } catch (err: any) {
      setGenerating({
        active: false,
        topic: "",
        progress: 0,
        message: err?.message || "生成异常",
        error: true,
      });
    }
  };

  // ============================================
  // 单项重新生成
  // ============================================

  const handleRegenerate = async (asset: MediaAsset) => {
    if (!currentPackage) return;
    setGenerating({
      active: true,
      topic: currentPackage.topic,
      progress: 50,
      message: `正在重新生成 ${asset.purpose}...`,
    });

    try {
      const commandMap: Record<string, string> = {
        cover: "generate_cover",
        wechat_cover: "generate_wechat_cover",
        douyin_cover: "generate_douyin_cover",
        video: "generate_video",
        bgm: "generate_bgm",
        article_script: "generate_article_script",
        video_script: "generate_video_script",
        xiaohongshu_script: "generate_xiaohongshu_script",
        bilibili_script: "generate_bilibili_script",
        douyin_script: "generate_douyin_script",
      };
      const command = commandMap[asset.purpose];
      if (!command) return;

      const resp = await RuntimeManager.execute(
        RuntimeName.MediaStudio,
        command as any,
        {
          title: currentPackage.topic,
          description: currentPackage.description,
          style,
          topic: currentPackage.topic,
          mood: "科技感",
        }
      );

      if (resp?.success && resp.data) {
        const newAsset = resp.data as MediaAsset;
        const updated = { ...currentPackage };
        if (asset.purpose === "cover") updated.cover = newAsset;
        else if (asset.purpose === "wechat_cover") updated.wechatCover = newAsset;
        else if (asset.purpose === "douyin_cover") updated.douyinCover = newAsset;
        else if (asset.purpose === "video") updated.video = newAsset;
        else if (asset.purpose === "bgm") updated.bgm = newAsset;
        else if (asset.purpose === "article_script") updated.articleScript = newAsset;
        else if (asset.purpose === "video_script") updated.videoScript = newAsset;
        else if (asset.purpose === "xiaohongshu_script") updated.xiaohongshuScript = newAsset;
        else if (asset.purpose === "bilibili_script") updated.bilibiliScript = newAsset;
        else if (asset.purpose === "douyin_script") updated.douyinScript = newAsset;
        setCurrentPackage(updated);
      }
    } finally {
      setGenerating({ active: false, topic: "", progress: 0, message: "", error: false });
    }
  };

  // ============================================
  // 审核操作
  // ============================================

  const handleApprove = async (assetId: string) => {
    setApprovedAssets((prev) => new Set(prev).add(assetId));
    await RuntimeManager.execute(RuntimeName.MediaStudio, "approve_asset", {
      assetId,
    });
    loadPackages();
  };

  const handleReject = async (asset: MediaAsset) => {
    setApprovedAssets((prev) => {
      const next = new Set(prev);
      next.delete(asset.id);
      return next;
    });
    await RuntimeManager.execute(RuntimeName.MediaStudio, "reject_asset", {
      assetId: asset.id,
    });
    handleRegenerate(asset);
  };

  // ============================================
  // 发布
  // ============================================

  const handlePublish = async (pkgId: string) => {
    const resp = await RuntimeManager.execute(
      RuntimeName.MediaStudio,
      "publish_package",
      { packageId: pkgId }
    );
    if (resp?.success) {
      alert("媒体包已发布，可前往对应平台手动发布");
      loadPackages();
    }
  };

  // ============================================
  // 同步到营销中心（ContentRuntime）- 关联6平台
  // 将当前媒体包的脚本内容写入 ContentRuntime，
  // 营销中心可通过 list_packages 查询到对应平台的内容
  // 不开新 tab：通过 setWorkspace("marketing") 在应用内切换
  // ============================================
  const handleSyncToMarketing = async () => {
    if (!currentPackage) return;
    setSyncingToMarketing(true);
    setSyncMessage(null);
    try {
      if (!RuntimeManager.isInitialized()) {
        setSyncMessage({ success: false, message: "RuntimeManager 尚未初始化" });
        return;
      }

      // 收集各平台的脚本内容（按平台适配）
      const platformScripts: Array<{ platform: string; script: string; label: string }> = [
        { platform: 'wechat_official', script: currentPackage.articleScript?.content || '', label: '微信公众号' },
        { platform: 'douyin_enterprise', script: currentPackage.douyinScript?.content || '', label: '抖音企业号' },
        { platform: 'douyin_personal', script: currentPackage.douyinScript?.content || '', label: '抖音个人号' },
        { platform: 'xiaohongshu', script: currentPackage.xiaohongshuScript?.content || '', label: '小红书' },
        { platform: 'zhihu', script: currentPackage.articleScript?.content || '', label: '知乎' },
        { platform: 'bilibili', script: currentPackage.bilibiliScript?.content || '', label: 'B站' },
      ].filter(p => p.script.length > 0);

      if (platformScripts.length === 0) {
        setSyncMessage({ success: false, message: "当前媒体包缺少任何脚本内容，请先生成脚本" });
        return;
      }

      // 逐平台同步到 ContentRuntime
      const results: Array<{ platform: string; label: string; success: boolean; error?: string }> = [];
      for (const { platform, script, label } of platformScripts) {
        try {
          const resp = await RuntimeManager.execute(
            RuntimeName.Content,
            "publish",
            {
              platform,
              item: {
                id: `media-sync-${currentPackage.id}-${platform}-${Date.now()}`,
                title: currentPackage.topic || `未命名-${label}`,
                body: script,
                contentType: "markdown",
                source: { runtime: "media_studio", provider: "sync", uri: currentPackage.id, platform },
              },
              options: { asDraft: true, autoPublish: false },
            }
          );
          results.push({ platform, label, success: resp?.success ?? false, error: resp?.error });
        } catch (err: any) {
          results.push({ platform, label, success: false, error: err?.message || String(err) });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failedList = results.filter(r => !r.success).map(r => r.label);

      if (successCount === platformScripts.length) {
        setSyncMessage({
          success: true,
          message: `✓ 已同步 ${successCount}/${platformScripts.length} 个平台到营销中心（${results.map(r => r.label).join('、')}）`,
          canJumpToMarketing: true,
        });
      } else if (successCount > 0) {
        setSyncMessage({
          success: true,
          message: `部分同步成功：${successCount}/${platformScripts.length}。失败：${failedList.join('、')}`,
          canJumpToMarketing: true,
        });
      } else {
        setSyncMessage({
          success: false,
          message: `同步失败：${failedList.join('、')} 均未成功`,
        });
      }
    } catch (err: any) {
      setSyncMessage({
        success: false,
        message: `同步异常：${err?.message || String(err)}`,
      });
    } finally {
      setSyncingToMarketing(false);
    }
  };

  // ============================================
  // 按平台同步到营销中心 + 微信公众号（每个分类框内的同步按钮）
  // 用户偏好：同步按钮应在每个分类框内显示，方便用户操作
  // 行为：
  //   1. 将该平台所有已生成(completed)的脚本同步到 ContentRuntime（营销中心内容工厂产物）
  //   2. 微信公众号平台额外调用 wechatPublish.autoPublish 同步到微信公众号
  //   3. 同步结果在分类框内显示（成功绿色/失败红色）
  // ============================================
  const handleSyncPlatformToMarketing = async (platform: SocialPlatform) => {
    const platformItems = contentPlan.filter(
      (item) => item.platform === platform && item.status === 'completed' && item.packageId
    );
    if (platformItems.length === 0) {
      setPlatformSyncResult((prev) => ({
        ...prev,
        [platform]: { success: false, message: '该平台暂无已生成的内容，请先生成' },
      }));
      setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [platform]: null })), 4000);
      return;
    }

    const ready = await waitForRuntime();
    if (!ready) {
      setPlatformSyncResult((prev) => ({
        ...prev,
        [platform]: { success: false, message: 'RuntimeManager 初始化超时' },
      }));
      return;
    }

    setPlatformSyncing((prev) => ({ ...prev, [platform]: true }));
    setPlatformSyncResult((prev) => ({ ...prev, [platform]: null }));

    // ContentRuntime 平台标识映射
    const platformLabelMap: Record<SocialPlatform, { content: string; label: string }> = {
      wechat: { content: 'wechat_official', label: '微信公众号' },
      douyin_enterprise: { content: 'douyin_enterprise', label: '抖音企业号' },
      douyin_personal: { content: 'douyin_personal', label: '抖音个人号' },
      xiaohongshu: { content: 'xiaohongshu', label: '小红书' },
      zhihu: { content: 'zhihu', label: '知乎' },
      bilibili: { content: 'bilibili', label: 'B站' },
      live_stream: { content: 'live_stream', label: '每日直播' },
      wechat_channels: { content: 'wechat_channels', label: '微信视频号' },
      wechat_moments: { content: 'wechat_moments', label: '微信朋友圈' },
      // v6.3.19 今日头条
      toutiao: { content: 'toutiao', label: '今日头条' },
      // v6.6 订阅号「小舍AI」热度号（补录，手动发布；旧实现缺失导致"内容通过"报 undefined.content）
      wechat_subscription: { content: 'wechat_subscription', label: '订阅号小舍AI' },
      // v6.7 CSDN：技术博客（无开放自动发布 API，素材归档 Outputs/CSDN/ 人工发布；此处仅用于同步到营销中心草稿）
      csdn: { content: 'csdn', label: 'CSDN' },
    };
    const targetPlatform = platformLabelMap[platform].content;
    const platformLabel = platformLabelMap[platform].label;

    let successCount = 0;
    let wechatPublished = false;
    let wechatError = '';

    for (const item of platformItems) {
      // v5.2：pkg 查找容错（packageId → topic → currentPackage）
      let pkg = findPkgForItem(item);
      // v5.2 根治：内存未命中 → Vault 兜底读取（不依赖 packages 异步竞态）
      if (!pkg && item.packageId) {
        pkg = await fetchPkgFromVault(item.packageId);
        if (pkg) {
          const resolved = pkg;
          setPackages((prev) => {
            if (prev.find((p) => p.id === resolved.id)) return prev;
            return [...prev, resolved];
          });
        }
      }
      if (!pkg) continue;

      // v5.2 生成流程拆分：内容查阅 OK 后，同步时补生成标题+封面（成本可控）
      // v5.2.3 修复：判断改为 pkg.cover?.url（历史双层 data bug 曾把包装层对象塞进 cover，
      // 导致 !pkg.cover 判断为已有封面而跳过生成）
      if (!pkg.cover?.url && item.packageId) {
        try {
          const coverResp = await RuntimeManager.execute(
            RuntimeName.MediaStudio,
            "generate_cover",
            {
              title: item.topic,
              description: pkg.description || '',
              // v6.1.10 同步生成封面时：若用户未手动固定风格，随机换一个（保证每次封面不同）
              style: style || COVER_STYLES[Math.floor(Math.random() * COVER_STYLES.length)],
              // v6.11 公众号/知乎横版封面，其余竖版
              landscape: item.platform === "wechat" || item.platform === "zhihu",
              packageId: item.packageId, // 挂载到包并持久化（Rule 05）
            }
          );
          if (coverResp?.success && coverResp.data) {
            // v5.2.3 修复：coverResp.data 是 MediaStudioResult 包装层，asset 在 .data.data
            const wrapped = coverResp.data as any;
            const asset = (wrapped && typeof wrapped === "object" && wrapped.data && wrapped.data.url)
              ? (wrapped.data as MediaAsset)
              : (wrapped as MediaAsset);
            if (asset && asset.url) {
              // v5.2.3 主播参考图合成：若配置了主播照片，把头像合成到封面左下角（口播封面样式）
              const finalCover = await composeAnchorCover(asset.url, item.topic, platform);
              if (finalCover) {
                asset.url = finalCover; // 合成后的封面（dataURL，会话内展示/下载）
              }
              pkg.cover = asset;
            }
          }
        } catch {
          // 封面生成失败不阻断同步
        }
      }

      // 根据平台取对应脚本内容
      const scriptAsset =
        platform === 'wechat' ? pkg.articleScript :
        platform === 'douyin_enterprise' || platform === 'douyin_personal' ? pkg.douyinScript :
        platform === 'xiaohongshu' ? pkg.xiaohongshuScript :
        platform === 'bilibili' ? pkg.bilibiliScript :
        platform === 'wechat_channels' ? pkg.videoScript || pkg.douyinScript :
        platform === 'wechat_moments' ? pkg.momentsScript :
        pkg.articleScript || pkg.videoScript; // 知乎/直播兜底
      const scriptContent = scriptAsset?.content || '';
      if (!scriptContent) continue;

      // 1. 同步到 ContentRuntime（营销中心内容工厂产物）
      // v6.7 CSDN：无开放自动发布 API → 不调用 ContentRuntime 发布（避免"平台未注册"异常），
      // 直接记为"素材已就绪，可人工发布"（内容归档 Outputs/CSDN/ 供复制）
      const isManualSync = platform === 'csdn';
      if (!isManualSync) {
        try {
          const resp = await RuntimeManager.execute(
            RuntimeName.Content,
            "publish",
            {
              platform: targetPlatform,
              item: {
                id: `media-sync-${pkg.id}-${targetPlatform}-${Date.now()}`,
                title: item.topic,
                body: scriptContent,
                contentType: "markdown",
                source: { runtime: "media_studio", provider: "platform_sync", uri: pkg.id, platform: targetPlatform },
              },
              options: { asDraft: true, autoPublish: false },
            }
          );
          if (resp?.success) successCount++;
        } catch {
          // 单条失败不阻断
        }
      } else {
        successCount++; // CSDN：素材已就绪（手动发布），计入成功
      }

      // 2. 微信公众号平台额外同步到微信公众号
      if (platform === 'wechat') {
        try {
          const wechatService = getWeChatPublishService();
          if (wechatService.isConfigured()) {
            const coverUrl = pkg.cover?.url || pkg.wechatCover?.url;
            const result = await wechatService.autoPublish({
              title: item.topic,
              content: scriptContent,
              thumbMediaId: '',
              coverUrl: coverUrl || '',
              author: 'XEAOS',
            });
            if (result.success) {
              wechatPublished = true;
            } else {
              wechatError = result.error || '微信发布失败';
            }
          } else {
            wechatError = '微信公众号未配置 AppID/AppSecret';
          }
        } catch (err: any) {
          wechatError = err?.message || '微信发布异常';
        }
      }
    }

    // 构造结果消息
    let message: string;
    let success: boolean;
    if (platform === 'wechat') {
      if (successCount > 0 && wechatPublished) {
        message = `✓ 已同步营销中心(${successCount}) + 已发布到微信公众号`;
        success = true;
      } else if (successCount > 0 && !wechatPublished) {
        message = `✓ 已同步营销中心(${successCount}) · 微信未发布：${wechatError}`;
        success = true;
      } else {
        message = `同步失败：${wechatError || '营销中心同步失败'}`;
        success = false;
      }
    } else {
      if (successCount > 0) {
        // v6.7 CSDN：无自动发布 → 提示素材已就绪待人工发布
        message = platform === 'csdn'
          ? `✓ 素材已就绪 ${successCount}/${platformItems.length} 篇，请复制内容手动发布到CSDN`
          : `✓ 已同步 ${successCount}/${platformItems.length} 篇到营销中心`;
        success = true;
      } else {
        message = `同步失败：${platformLabel}内容未同步成功`;
        success = false;
      }
    }

    setPlatformSyncResult((prev) => ({ ...prev, [platform]: { success, message } }));
    setPlatformSyncing((prev) => ({ ...prev, [platform]: false }));
    // 6 秒后自动清除提示
    setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [platform]: null })), 6000);
  };

  // ============================================
  // v5.2：一键采集今日新闻（NewsRuntime.fetch_today → 写入 XEAOS/Daily/News-{date}.md）
  // 定位：前端定时器不常驻导致新闻 8 天未更新，此按钮手动触发采集，随后自动刷新今日计划
  //       （contentPlan 会从新闻摘要派生「每日动态」选题 → 关联到内容生成）
  // ============================================
  const handleFetchTodayNews = async () => {
    const ready = await waitForRuntime();
    if (!ready) {
      alert("RuntimeManager 初始化超时，请刷新页面重试");
      return;
    }
    setNewsFetching(true);
    setNewsMessage(null);
    try {
      const resp = await RuntimeManager.execute(RuntimeName.News, "fetch_today");
      const inner = resp?.data as any;
      const ok = !!(resp?.success || inner?.success);
      const articles = Array.isArray(inner?.articles) ? inner.articles : Array.isArray(inner?.news) ? inner.news : [];
      const topCount = Array.isArray(inner?.topStories) ? inner.topStories.length : 0;
      const detail = articles.length > 0 ? `采集 ${articles.length} 条新闻${topCount > 0 ? `，Top ${topCount} 已提取` : ""}` : (inner?.summary || "");
      setNewsMessage({
        success: ok,
        message: ok ? `✅ 今日新闻已入库（${detail}），正在刷新计划...` : `❌ 采集失败：${resp?.error || inner?.error || "未知错误"}`,
      });
      // 新闻入库后刷新今日计划（派生「每日动态」选题）
      if (ok) {
        setTimeout(() => loadContentPlan(), 300);
      }
    } catch (err: any) {
      setNewsMessage({ success: false, message: `❌ 采集异常：${err?.message || String(err)}` });
    } finally {
      setNewsFetching(false);
      setTimeout(() => setNewsMessage(null), 8000);
    }
  };

  // ============================================
  // 按平台查阅最新生成内容（每个分类框内的查阅按钮）
  // 行为：打开该平台最新已生成条目的脚本预览 Modal
  // ============================================
  /**
   * v5.2 修复：pkg 查找容错（根治"查阅/同步点击没反应"）
   * 查找顺序：packageId 精确 → topic 匹配 → currentPackage 兜底
   * 原因：生成后 packages 列表刷新存在异步竞态，packageId 可能暂时匹配不上
   */
  const findPkgForItem = (item: { packageId?: string; topic?: string }): MediaPackage | null => {
    if (!item) return null;
    if (item.packageId) {
      const byId = currentPackage?.id === item.packageId
        ? currentPackage
        : packages.find((p) => p.id === item.packageId);
      if (byId) return byId;
    }
    if (item.topic) {
      const byTopic =
        packages.find((p) => p.topic === item.topic) ||
        packages.find((p) => p.topic && item.topic && (p.topic.includes(item.topic) || item.topic.includes(p.topic))) ||
        (currentPackage?.topic === item.topic ? currentPackage : null);
      if (byTopic) return byTopic;
    }
    return null;
  };

  // v5.2 根治：直接从 Vault 读取产物包（不依赖 packages 内存竞态）
  // 场景：刷新后 packages 异步加载未完成时点击查阅/同步，内存查找必然 miss → 静默无反应
  // 解法：遍历 XEAOS/ContentPackages 目录按 packageId 定位文件 → 解析 frontmatter + 脚本标记
  const [vaultFetching, setVaultFetching] = useState<Record<string, boolean>>({});

  const fetchPkgFromVault = useCallback(async (packageId: string): Promise<MediaPackage | null> => {
    try {
      // 1. 列举日期目录
      const rootResp = await fetch(`/vault-api/list?dir=${encodeURIComponent('XEAOS/ContentPackages')}&ext=.md`);
      if (!rootResp.ok) return null;
      const rootResult = await rootResp.json();
      if (!rootResult.success) return null;
      const dateDirs = (rootResult.files || []).filter((f: any) => f.isDirectory);

      // 2. 在每个日期目录中查找 packageId 匹配的文件
      for (const dir of dateDirs) {
        const subResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir.path)}&ext=.md`);
        if (!subResp.ok) continue;
        const subResult = await subResp.json();
        if (!subResult.success) continue;
        for (const file of (subResult.files || [])) {
          // 文件名格式：{id}-{safeTopic}.md，按前缀匹配 packageId
          if (!String(file.name || '').startsWith(packageId + '-')) continue;
          const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(file.path)}`);
          if (!readResp.ok) continue;
          const readResult = await readResp.json();
          if (!readResult.success || !readResult.content) continue;
          // 解析 frontmatter
          const content = String(readResult.content);
          const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
          if (!fmMatch) continue;
          const fm = fmMatch[1];
          const get = (key: string): string => {
            const m = fm.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
            return m ? m[1].trim().replace(/^["']|["']$/g, '') : '';
          };
          const id = get('id');
          if (!id) continue;
          const pkg: MediaPackage = {
            id,
            topic: get('topic'),
            status: (get('status') as MediaPackage['status']) || 'completed',
            createdAt: get('createdAt') || new Date().toISOString(),
            completedAt: get('completedAt') || undefined,
            description: get('description') || undefined,
            sourceNotes: [],
            _vaultPath: file.path,
          } as MediaPackage;
          // 解析脚本标记
          const scriptRegex = /<!-- XEAOS_SCRIPT:(\w+) -->\n([\s\S]*?)(?=<!-- XEAOS_SCRIPT:|## 素材链接|$)/g;
          let sm: RegExpExecArray | null;
          const scriptIndex: Record<string, string> = {};
          while ((sm = scriptRegex.exec(content)) !== null) {
            scriptIndex[sm[1]] = sm[2].replace(/\n+$/, '').trim();
          }
          const mk = (purpose: string, text: string): MediaAsset => ({
            id: `asset-${id}-${purpose}`,
            type: 'script' as const,
            purpose: purpose as any,
            content: text,
            title: `${purpose}：${pkg.topic}`,
            model: 'deepseek-chat',
            status: 'completed' as const,
            createdAt: pkg.createdAt,
          });
          if (scriptIndex.article_script) pkg.articleScript = mk('article_script', scriptIndex.article_script);
          if (scriptIndex.video_script) pkg.videoScript = mk('video_script', scriptIndex.video_script);
          if (scriptIndex.xiaohongshu_script) pkg.xiaohongshuScript = mk('xiaohongshu_script', scriptIndex.xiaohongshu_script);
          if (scriptIndex.bilibili_script) pkg.bilibiliScript = mk('bilibili_script', scriptIndex.bilibili_script);
          if (scriptIndex.douyin_script) pkg.douyinScript = mk('douyin_script', scriptIndex.douyin_script);
          return pkg;
        }
      }
    } catch {
      // 忽略，返回 null
    }
    return null;
  }, []);

  // v5.2.2 关键修复：独立解析计划项对应的内容包（内存 → Vault 兜底），并自动选中。
  // 解决「重新生成后 packageId 已更新但 packages/currentPackage 内存未就绪 → 预览/发布按钮消失」的竞态。
  const resolvePkgForItem = async (item: { packageId?: string; topic?: string }): Promise<MediaPackage | null> => {
    if (!item) return null;
    // 1. 内存快路径
    let pkg = findPkgForItem(item);
    // 2. Vault 兜底（packageId 或 topic 均可）
    if (!pkg && item.packageId) {
      try {
        pkg = await fetchPkgFromVault(item.packageId);
      } catch { /* 忽略 */ }
    }
    // 3. 兜底后自动选中 + 加入内存列表（供后续编辑/发布复用）
    if (pkg) {
      setCurrentPackage(pkg);
      setPackages((prev) => (prev.find((p) => p.id === pkg.id) ? prev : [...prev, pkg]));
    }
    return pkg;
  };

  const fetchPkgListFromVault = useCallback(async (): Promise<MediaPackage[]> => {
    const result: MediaPackage[] = [];
    try {
      const rootResp = await fetch(`/vault-api/list?dir=${encodeURIComponent('XEAOS/ContentPackages')}&ext=.md`);
      if (!rootResp.ok) return result;
      const rootResult = await rootResp.json();
      if (!rootResult.success) return result;
      const dateDirs = (rootResult.files || []).filter((f: any) => f.isDirectory);
      await Promise.all(dateDirs.map(async (dir: any) => {
        try {
          const subResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir.path)}&ext=.md`);
          if (!subResp.ok) return;
          const subResult = await subResp.json();
          if (!subResult.success) return;
          for (const file of (subResult.files || [])) {
            try {
              const idPrefix = String(file.name).replace(/\.md$/, '').split('-').slice(0, 3).join('-');
              const pkg = await fetchPkgFromVault(idPrefix);
              if (pkg) result.push(pkg);
            } catch {
              // 单文件失败跳过
            }
          }
        } catch {
          // 目录失败跳过
        }
      }));
    } catch {
      // 整体失败返回空
    }
    return result;
  }, [fetchPkgFromVault]);

  const handleViewPlatformLatest = async (platform: SocialPlatform) => {
    // v5.2.2 兼容：不再强制 packageId（历史 bug 曾把 packageId 写空，导致"已生成却报暂无内容"）
    const completedItems = contentPlan.filter(
      (item) => item.platform === platform && (item.status === 'completed' || item.status === 'published')
    );
    if (completedItems.length === 0) {
      // 明确反馈，不再静默
      setPlatformSyncResult((prev) => ({
        ...prev,
        [platform]: { success: false, message: `${PLATFORM_CONFIG[platform].name}暂无已生成内容，请先点「生成」` },
      }));
      setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [platform]: null })), 4000);
      return;
    }
    // 取最新一条：优先取 id 时间戳最大（内容计划 id 含 Date.now），否则取数组最后一个
    const latestItem = [...completedItems].sort((a, b) => (b.id || '').localeCompare(a.id || ''))[0];
    // 1. 先查内存（快路径）
    let pkg = findPkgForItem(latestItem);
    // 2. 内存未命中 → Vault 兜底读取（根治 packages 异步竞态导致的静默无反应）
    if (!pkg && latestItem.packageId) {
      setVaultFetching((prev) => ({ ...prev, [platform]: true }));
      try {
        pkg = await fetchPkgFromVault(latestItem.packageId);
        if (pkg) {
          // 兜底命中：加入内存包列表，便于后续同步/发布复用
          const resolved = pkg;
          setPackages((prev) => {
            if (prev.find((p) => p.id === resolved.id)) return prev;
            return [...prev, resolved];
          });
        }
      } finally {
        setVaultFetching((prev) => ({ ...prev, [platform]: false }));
      }
    }
    if (!pkg) {
      setPlatformSyncResult((prev) => ({
        ...prev,
        [platform]: {
          success: false,
          message: `「${latestItem.topic}」没有内容包（生成时可能失败被误标完成）——请重新点「生成」，若仍失败会显示红字原因`,
        },
      }));
      setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [platform]: null })), 6000);
      return;
    }

    // 选中该包，便于后续编辑/发布
    setCurrentPackage(pkg);

    // 根据平台取对应脚本
    const scriptAsset =
      platform === 'wechat' ? pkg.articleScript :
      platform === 'douyin_enterprise' || platform === 'douyin_personal' ? pkg.douyinScript :
      platform === 'xiaohongshu' ? pkg.xiaohongshuScript :
      platform === 'bilibili' ? pkg.bilibiliScript :
      platform === 'wechat_channels' ? pkg.videoScript || pkg.douyinScript :
      platform === 'wechat_moments' ? pkg.momentsScript :
      pkg.articleScript || pkg.videoScript;

    if (scriptAsset) {
      setEditingScript({ asset: scriptAsset, content: scriptAsset.content || '' });
    } else if (pkg.cover?.url) {
      setPreview({ type: 'image', url: pkg.cover.url, title: latestItem.topic });
    } else {
      setPlatformSyncResult((prev) => ({
        ...prev,
        [platform]: { success: false, message: `${PLATFORM_CONFIG[platform].name}内容已生成但脚本为空，请重新生成` },
      }));
      setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [platform]: null })), 4000);
    }
  };

  // ============================================
  // Tab 配置
  // ============================================

  const TABS: Array<{ id: AssetTab; label: string; icon: typeof ImageIcon; color: string }> = [
    { id: "all", label: "全部", icon: Grid3x3, color: "#A855F7" },
    { id: "images", label: "图片", icon: ImageIcon, color: "#EC4899" },
    { id: "scripts", label: "脚本", icon: FileText, color: "#3B82F6" },
    { id: "video", label: "视频", icon: Video, color: "#06B6D4" },
    { id: "music", label: "音乐", icon: Music, color: "#8B5CF6" },
  ];

  // ============================================
  // 渲染
  // ============================================

  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Phase3 P0-3 业务说明 + 跨模块跳转（内容工厂） */}
        <WorkspaceBizBar
          desc="内容工厂：批量生产多平台素材，内容发布后数据会回写到【营销中心】"
          hint="内容发布产生咨询线索，自动流入【客户中心】"
          accent="#A855F7"
          jumps={[
            { label: "发布到营销平台", to: "marketing" },
            { label: "线索流入客户中心", to: "crm" },
          ]}
        />
        {/* ============ Header ============ */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
              <Sparkles size={14} className="text-purple-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">
                内容工厂
              </h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                Content Factory · 按平台逻辑生产不同产品 · 多米 API + Suno API + DeepSeek
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* v5.2 审计修复 P1：品牌选择（内容生产流程①） */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-gray-500">品牌</span>
              <select
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                className="px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-gray-300 outline-none focus:border-purple-500/40"
                title="选择本次内容生产的品牌（品牌身份将进入内容约束）"
              >
                {BRAND_OPTIONS.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            {/* v5.2.3 主播参考图：口播封面合成用（上传主播照片，生成封面时自动合成头像） */}
            <div className="flex items-center gap-1.5">
              <input
                ref={anchorInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAnchorPhotoUpload}
              />
              <button
                onClick={() => anchorInputRef.current?.click()}
                title={anchorPhoto ? "已设置主播照片，口播封面将合成主播头像（点击更换）" : "上传主播照片：口播封面将把主播头像合成到封面左下角"}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono border transition ${
                  anchorPhoto
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
                    : "bg-white/[0.03] border-white/[0.08] text-gray-400 hover:border-pink-500/30 hover:text-pink-300"
                }`}
              >
                {anchorPhoto ? (
                  <img src={anchorPhoto} alt="主播" className="w-4 h-4 rounded-full object-cover" />
                ) : (
                  <Camera size={11} />
                )}
                {anchorPhoto ? "主播已设置" : "主播照片"}
              </button>
              {anchorPhoto && (
                <button
                  onClick={() => { setAnchorPhoto(null); try { localStorage.removeItem("xeaos_anchor_photo"); } catch { /* 忽略 */ } }}
                  title="清除主播照片"
                  className="text-gray-500 hover:text-red-400 transition"
                >
                  <X size={11} />
                </button>
              )}
            </div>
            <span className="text-[10px] font-mono text-emerald-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              在线
            </span>
            <span className="text-[10px] font-mono text-gray-500">
              图片 · 脚本 · 视频 · 音乐
            </span>
          </div>
        </div>

        {/* ============ 主体内容（12 列网格） ============ */}
        {/* v6 收敛：核心 5 项平铺 + 低频 8 项收进"更多▾"下拉 */}
        <div className="relative z-30 flex items-center gap-1 px-4 py-2 border-b border-white/[0.06] bg-white/[0.01] flex-wrap">
          {/* 核心标签组（高频使用，始终可见） */}
          <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider mr-1">
            核心
          </span>
          {(
            [
              { id: "studio", label: "生成工作室", icon: Sparkles },
              { id: "calendar", label: "内容日历", icon: CalendarClock },
              { id: "quality", label: "质量评分", icon: Gauge },
              { id: "assetLibrary", label: "内容资产库", icon: FolderArchive },
              { id: "reviewWorkflow", label: "审核工作流", icon: FileSearch },
              // v6.3.16 口吻管理：内容生成的"人格层"入口（自助注入语料，替代后台代劳）
              { id: "voice", label: "口吻管理", icon: BookOpenText },
              // v6.7 订阅号「小舍AI」热度号：独立副业号（热点选题/文章管理，不走主链路）
              { id: "subscription", label: "订阅号小舍AI", icon: Newspaper },
              // v6.16 内容仓库：查看/复制所有生成内容
              { id: "repository", label: "内容仓库", icon: FolderOpen },
              // v6.24 官博发布：写文 → 一键发布到官网博客
              { id: "blog", label: "官博发布", icon: PenSquare },
            ] as const
          ).map((tab) => {
            const TIcon = tab.icon;
            const isActive = studioSection === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setStudioSection(tab.id as typeof studioSection)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                  isActive
                    ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                    : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <TIcon size={11} />
                {tab.label}
              </button>
            );
          })}

          {/* 更多▾ 下拉（低频功能，点击展开） */}
          <div className="relative">
            <button
              onClick={() => setShowMoreSections(!showMoreSections)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                showMoreSections
                  ? "bg-white/[0.06] text-white border border-white/[0.12]"
                  : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <span>更多</span>
              <ChevronDown size={10} className={showMoreSections ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            <AnimatePresence>
              {showMoreSections && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-30"
                    onClick={() => setShowMoreSections(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-1 z-40 min-w-[170px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl"
                  >
                    {(
                      [
                        { id: "content_protocol", label: "内容协议", icon: FileText },
                        { id: "distributor", label: "多平台分发", icon: Send },
                        { id: "optimizationLoop", label: "优化闭环", icon: RefreshCcw },
                        { id: "predictPanel", label: "效果预测", icon: TrendingUp },
                        { id: "seriesManager", label: "系列化管理", icon: Layers },
                        { id: "contentMatrix", label: "内容矩阵", icon: Grid3x3 },
                        { id: "ipPersona", label: "IP人设", icon: CircleUserRound },
                        { id: "monetization", label: "商业化", icon: Coins },
                      ] as const
                    ).map((tab) => {
                      const TIcon = tab.icon;
                      const isActive = studioSection === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => {
                            setStudioSection(tab.id as typeof studioSection);
                            setShowMoreSections(false);
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${
                            isActive
                              ? "text-purple-300 bg-purple-500/10"
                              : "text-gray-300 hover:bg-white/[0.05]"
                          }`}
                        >
                          <TIcon size={11} className="text-purple-400" />
                          <span className="flex-1 text-left">{tab.label}</span>
                          {isActive && <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />}
                        </button>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* 区块内容 */}
        {studioSection === "content_protocol" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* 从 Vault 加载的内容简报 */}
            <ViralContentBriefLoader
              onSelectBrief={(brief) => {
                setTopic(brief.content);
              }}
            />
            <ContentProtocolFlow />
            <DailyExperiencePanel />
          </div>
        )}
        {studioSection === "calendar" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentCalendar />
          </div>
        )}
        {studioSection === "quality" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentQualityScorer />
          </div>
        )}
        {studioSection === "distributor" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentDistributor />
          </div>
        )}
        {/* P1-功能4：内容资产库 */}
        {studioSection === "assetLibrary" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentAssetLibrary />
          </div>
        )}
        {/* P1-功能5：数据驱动内容优化闭环 */}
        {studioSection === "optimizationLoop" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentOptimizationLoop />
          </div>
        )}
        {/* v6.3.16 口吻管理（语料自助注入，替代后台代劳） */}
        {studioSection === "voice" && (
          <div className="flex-1 overflow-y-auto p-4">
            <PersonalVoiceManager />
          </div>
        )}
        {/* v6.7 订阅号「小舍AI」热度号工作台（独立副业号：热点选题+文章管理） */}
        {studioSection === "subscription" && (
          <div className="flex-1 overflow-y-auto p-4">
            <SubscriptionHub />
          </div>
        )}
        {/* v6.16 内容仓库（查看/复制所有生成内容） */}
        {studioSection === "repository" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentRepository />
          </div>
        )}
        {/* v6.24 官博发布（方案B：写文 → 生成文章页 → 推送到官网 blog/） */}
        {studioSection === "blog" && (
          <div className="flex-1 overflow-y-auto p-4">
            <BlogPublishPanel />
          </div>
        )}
        {/* MVP：内容效果预测与爆款反推 */}
        {studioSection === "predictPanel" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentPredictPanel />
          </div>
        )}
        {/* P1-功能6：内容审核工作流 */}
        {studioSection === "reviewWorkflow" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentReviewWorkflow />
          </div>
        )}
        {/* P1-功能7：内容系列化管理 */}
        {studioSection === "seriesManager" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentSeriesManager />
          </div>
        )}
        {/* P2-功能8：内容矩阵管理 */}
        {studioSection === "contentMatrix" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentMatrixManager />
          </div>
        )}
        {/* P2-功能9：IP 人设管理系统 */}
        {studioSection === "ipPersona" && (
          <div className="flex-1 overflow-y-auto p-4">
            <IPPersonaManager />
          </div>
        )}
        {/* P2-功能10：内容商业化引擎 */}
        {studioSection === "monetization" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ContentMonetizationEngine />
          </div>
        )}
        {studioSection === "studio" && (
        <div className="flex-1 min-h-0 overflow-hidden p-4">
          <div className="grid grid-cols-12 gap-3 h-full min-h-0">
            {/* ============ v6.45 左侧 8 列 = 自媒体 AI 工作台 ============
                原 AIGC 二级站 iframe 取消，改为工作台侧栏「AIGC 生成 ↗」外链（新窗口打开）。
                理由：左栏塞两套导航（工作台 sidebar + AIGC 站内导航）没人受得了；
                工作台与右侧每日计划同屏才是真正要的闭环：左边写、右边排期。 */}
            <div className="col-span-8 flex flex-col rounded-lg border border-white/[0.06] bg-black/30 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06] bg-white/[0.02] flex-none">
                <div className="flex items-center gap-2">
                  <Sparkles size={12} className="text-pink-400" />
                  <span className="text-[11px] font-mono text-gray-200 uppercase tracking-wider">
                    自媒体 AI 工作台
                  </span>
                  <span className="text-[9px] font-mono text-gray-600">
                    15 页 · 左侧导航 · 与右侧每日计划同屏
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href="https://aigc.xshetech.com"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono text-purple-300 bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-colors"
                    title="AIGC 内容生成二级站（已从左侧移除，改由工作台侧栏入口打开）"
                  >
                    <ExternalLink size={9} />
                    AIGC 二级站 ↗
                  </a>
                  <a
                    href="/workbench/index.html"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 transition-colors"
                    title="在新窗口打开完整工作台（15 页二级导航版）"
                  >
                    <ExternalLink size={9} />
                    新窗打开 ↗
                  </a>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <MediaWorkbench embedded goto={wbGoto} />
              </div>
            </div>

            {/* ============ 右侧 4 列：内容计划 + 待发布队列 + 历史 ============ */}
            {/* v6.45 窄视口保护：按钮文字一律不折行（否则列一窄就变成逐字竖排） */}
            <div className="col-span-4 h-full min-h-0 overflow-y-auto space-y-3 pr-1 [&_button]:whitespace-nowrap">
              {/* ============ 视图切换：每日计划 / 脚本库 ============ */}
              <div className="flex items-center gap-1 p-1 rounded-lg border border-white/[0.06] bg-white/[0.03] backdrop-blur-md">
                <button
                  onClick={() => setViewMode("plan")}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-mono transition-all ${
                    viewMode === "plan"
                      ? "text-white bg-cyan-500/20 border border-cyan-500/30"
                      : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] border border-transparent"
                  }`}
                >
                  <CalendarClock size={12} />
                  每日计划
                </button>
                <button
                  onClick={() => setViewMode("library")}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-mono transition-all ${
                    viewMode === "library"
                      ? "text-white bg-blue-500/20 border border-blue-500/30"
                      : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] border border-transparent"
                  }`}
                >
                  <Archive size={12} />
                  脚本库
                </button>
              </div>

              {viewMode === "plan" && (
                <>
              {/* ============ v6.1.20 周度内容大纲：AI 出周计划 → 与 Hermes 讨论确定 → 平台生成围绕大纲 ============ */}
              <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.04] backdrop-blur-md p-3">
                {/* v6.28 工具栏拆分为独立展示组件（状态/逻辑仍在本父组件） */}
                <WeeklyOutlineToolbar
                  weekLabel={weeklyWeekLabel}
                  focus={weeklyFocus}
                  onFocusChange={setWeeklyFocus}
                  onGenerate={() => guard(handleGenerateWeeklyOutline)}
                  generating={weeklyGenerating}
                  readonly={readonly}
                  onHistory={openOutlineHistory}
                />
                {weeklyLoading ? (
                  <div className="text-[10px] font-mono text-gray-600 py-2">加载周度大纲...</div>
                ) : weeklyOutline ? (
                  <>
                    {/* v6.28 状态徽章/概要/详细大纲按钮 + Hermes 就地讨论卡均拆为独立展示组件 */}
                    <WeeklyOutlineCard defaultApplied={weeklyDefaultApplied} weekLabel={weeklyWeekLabel} summary={weeklySummary} onOpenDetail={openWeeklyDetail} />
                    <HermesDiscussCard msgs={hermesMsgs} input={hermesInput} onInputChange={setHermesInput} busy={hermesBusy} onSend={handleHermesChat} onApply={handleApplyHermesOutline} onPreview={() => setChatPreviewOpen(true)} />
                  </>
                ) : (
                  <div className="text-[10px] font-mono text-gray-600 py-2">
                    暂无周度大纲。点「生成本周大纲」→ 弹窗内与 Hermes 讨论调整 → 确定后回每日计划逐平台生成。
                  </div>
                )}
              </div>

              {/* ============ 详情弹窗：完整大纲（大字体）+ Hermes 对话（左右分栏，一站式讨论） ============ */}
              <AnimatePresence>
                {weeklyDetailOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setWeeklyDetailOpen(false)}
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
                  >
                    <motion.div
                      initial={{ scale: 0.92 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0.92 }}
                      onClick={(e) => e.stopPropagation()}
                      className="relative w-full max-w-6xl h-[85vh] rounded-lg border border-purple-500/20 bg-[#14141a] flex flex-col overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <CalendarRange size={13} className="text-purple-400" />
                          <span className="text-[13px] font-mono text-purple-300">周度内容大纲 · {weeklyWeekLabel}</span>
                          <span className="text-[10px] font-mono text-gray-600">左侧详细内容（基于采纳后大纲生成） · 右侧讨论详细内容</span>
                        </div>
                        <button onClick={() => setWeeklyDetailOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="flex-1 grid grid-cols-5 min-h-0">
                        {/* 左栏：详细内容（基于采纳后大纲生成，v6.2.6） */}
                        <div className="col-span-3 overflow-y-auto px-4 py-3 border-r border-white/[0.06]">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-mono text-purple-300">📋 详细内容 · 基于当前大纲自动生成</span>
                            <div className="flex items-center gap-1.5">
                              {/* v6.2.7 闭环：把详细内容配到每日自媒体计划（各平台按规则差异化生成） */}
                              <button
                                onClick={handlePlanFromWeeklyDetail}
                                disabled={detailPlanGenerating || (!detailOutline && !weeklyOutline)}
                                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 transition-colors disabled:opacity-40"
                                title="把详细内容分配到每日自媒体计划的各平台（主体不变，按平台规则适配），点击后到「每日自媒体计划」逐平台生成"
                              >
                                {detailPlanGenerating ? <Loader2 size={10} className="animate-spin" /> : <CalendarClock size={10} />}
                                {detailPlanGenerating ? "生成计划中..." : "配到每日计划"}
                              </button>
                              <button
                                onClick={() => generateDetailOutline()}
                                disabled={detailGenerating || !weeklyOutline}
                                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 transition-colors disabled:opacity-40"
                                title="重新基于当前大纲生成详细内容"
                              >
                                {detailGenerating ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                {detailGenerating ? "生成中..." : "重新生成"}
                              </button>
                            </div>
                          </div>
                          {detailGenerating && !detailOutline ? (
                            <div className="text-[13px] font-mono text-purple-300/70 py-8 text-center">🧠 Hermes 正在基于采纳后的大纲生成详细内容...</div>
                          ) : (
                            <pre className="text-[13px] font-mono text-gray-200 whitespace-pre-wrap break-words leading-relaxed">
                              {detailOutline || weeklyOutline}
                            </pre>
                          )}
                        </div>
                        {/* 右栏：第二层 Hermes 对话（讨论详细内容，独立于第一层） */}
                        <div className="col-span-2 flex flex-col min-h-0">
                          <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06] flex-shrink-0">
                            <span className="text-[11px] font-mono text-blue-300">Hermes 参谋 · 讨论详细内容</span>
                            <span className="text-[9px] font-mono text-gray-600">第二层 · 经 Obsidian 检索/存档</span>
                          </div>
                          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
                            {detailMsgs.length === 0 ? (
                              <div className="text-[11px] font-mono text-gray-500 leading-relaxed">
                                可直接说：<br/>"周一的钩子再狠一点"<br/>"给每个主题补一句踩坑"<br/>"周三的平台建议改一下"
                              </div>
                            ) : (
                              detailMsgs.map((m, i) => (
                                <div key={i} className={`rounded px-2.5 py-1.5 text-[11px] font-mono leading-relaxed ${m.role === "user" ? "bg-blue-500/10 text-blue-200" : "bg-white/[0.03] text-gray-300"}`}>
                                  <span className="opacity-60">{m.role === "user" ? "你" : "Hermes"}：</span>
                                  <span className="whitespace-pre-wrap break-words">{m.content}</span>
                                </div>
                              ))
                            )}
                          </div>
                          <div className="p-2.5 border-t border-white/[0.06] flex-shrink-0 space-y-1.5">
                            <div className="flex items-start gap-1.5">
                              <textarea
                                value={detailInput}
                                onChange={(e) => setDetailInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleDetailChat(); }
                                }}
                                rows={2}
                                placeholder="讨论详细内容（如：改钩子/补踩坑/调平台）...（Enter 发送，Shift+Enter 换行）"
                                className="flex-1 px-2.5 py-1.5 text-[12px] font-mono bg-white/[0.03] border border-white/[0.08] rounded focus:outline-none focus:border-blue-500/40 placeholder-gray-600 text-gray-200 resize-y"
                              />
                              <button
                                onClick={handleDetailChat}
                                disabled={detailBusy || !detailInput.trim()}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 rounded transition-colors disabled:opacity-40"
                              >
                                {detailBusy ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                                发送
                              </button>
                            </div>
                            {(detailMsgs.some((m) => m.role === "assistant") || detailOutline) && (
                              <button
                                onClick={handleApplyDetailOutline}
                                className="w-full flex items-center justify-center gap-1 px-2.5 py-1.5 text-[11px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded transition-colors"
                                title="把详细内容讨论结果写回 Vault（周度大纲详细版）"
                              >
                                <Save size={10} />
                                保存详细内容 · 写入 Vault
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* v6.2.8 就地讨论放大预览弹窗：大屏查看完整对话（含采纳内容），方便复制/阅读 */}
              <AnimatePresence>
                {chatPreviewOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setChatPreviewOpen(false)}
                    className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
                  >
                    <motion.div
                      initial={{ scale: 0.92 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0.92 }}
                      onClick={(e) => e.stopPropagation()}
                      className="relative w-full max-w-4xl h-[80vh] rounded-lg border border-blue-500/20 bg-[#14141a] flex flex-col overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <MessageSquare size={13} className="text-blue-400" />
                          <span className="text-[13px] font-mono text-blue-300">Hermes 参谋 · 就地讨论（放大预览）</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {/* 复制全部对话 */}
                          <button
                            onClick={() => {
                              const text = hermesMsgs.map((m) => `${m.role === "user" ? "你" : "Hermes"}：\n${m.content}`).join("\n\n---\n\n");
                              if (text) navigator.clipboard?.writeText(text).catch(() => {});
                            }}
                            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-gray-300 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                            title="复制全部对话"
                          >
                            <Copy size={10} />
                            复制全部
                          </button>
                          <button onClick={() => setChatPreviewOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                        {hermesMsgs.length === 0 ? (
                          <div className="text-[13px] font-mono text-gray-500 py-10 text-center">暂无对话，在下方输入框开始讨论大纲</div>
                        ) : (
                          hermesMsgs.map((m, i) => (
                            <div key={i} className={`rounded px-3 py-2 text-[13px] font-mono leading-relaxed ${m.role === "user" ? "bg-blue-500/10 text-blue-200" : "bg-white/[0.04] text-gray-200"}`}>
                              <span className="opacity-60 block mb-1 text-[11px]">{m.role === "user" ? "👤 你" : "🧠 Hermes"}</span>
                              <span className="whitespace-pre-wrap break-words">{m.content}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* v6.2.9 历史大纲弹窗：集中查看每周生成/采纳的大纲（周度大纲 + 详细版），可恢复 */}
              <AnimatePresence>
                {outlineHistoryOpen && !viewingHistoryOutline && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setOutlineHistoryOpen(false)}
                    className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
                  >
                    <motion.div
                      initial={{ scale: 0.92 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0.92 }}
                      onClick={(e) => e.stopPropagation()}
                      className="relative w-full max-w-2xl h-[75vh] rounded-lg border border-purple-500/20 bg-[#14141a] flex flex-col overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <FolderArchive size={13} className="text-purple-400" />
                          <span className="text-[13px] font-mono text-purple-300">历史大纲（10_Operations/Content）</span>
                          <span className="text-[9px] font-mono text-gray-600">每周生成/采纳的大纲都在这里，点击查看，可恢复为当前大纲</span>
                        </div>
                        <button onClick={() => setOutlineHistoryOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
                        {outlineHistoryLoading ? (
                          <div className="text-[12px] font-mono text-gray-500 py-8 text-center">加载历史大纲...</div>
                        ) : outlineHistory.length === 0 ? (
                          <div className="text-[12px] font-mono text-gray-500 py-8 text-center">暂无历史大纲（当前周大纲会在这里显示）</div>
                        ) : (
                          <>
                            {outlineHistory.map((h, i) => (
                              <button
                                key={i}
                                onClick={() => viewHistoryOutline(h.path, h.name)}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded text-[11px] font-mono text-left text-gray-300 bg-white/[0.02] hover:bg-purple-500/10 hover:text-white border border-white/[0.06] transition-colors"
                                title={`查看 ${h.name}`}
                              >
                                {h.type === "weekly" ? <CalendarRange size={11} className="text-purple-400" /> : <FileText size={11} className="text-cyan-400" />}
                                <span className="flex-1 truncate">{h.name}</span>
                                <span className="text-[9px] text-gray-500 flex-shrink-0">{h.type === "weekly" ? "周度大纲" : "详细版"}</span>
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                )}
                {/* 历史大纲详情查看 + 恢复 */}
                {viewingHistoryOutline && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setViewingHistoryOutline(null)}
                    className="fixed inset-0 z-[65] bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
                  >
                    <motion.div
                      initial={{ scale: 0.92 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0.92 }}
                      onClick={(e) => e.stopPropagation()}
                      className="relative w-full max-w-4xl h-[80vh] rounded-lg border border-purple-500/20 bg-[#14141a] flex flex-col overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <FileText size={13} className="text-purple-400" />
                          <span className="text-[13px] font-mono text-purple-300 truncate">{viewingHistoryOutline.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => restoreHistoryOutline(viewingHistoryOutline.content)}
                            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 transition-colors"
                            title={`恢复为当前周大纲（${weeklyWeekLabel}）`}
                          >
                            <Save size={10} />
                            恢复为当前大纲
                          </button>
                          <button onClick={() => setViewingHistoryOutline(null)} className="text-gray-500 hover:text-white transition-colors">
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto px-4 py-3">
                        <pre className="text-[13px] font-mono text-gray-200 whitespace-pre-wrap break-words leading-relaxed">{viewingHistoryOutline.content}</pre>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ============ 每日自媒体内容计划 ============ */}
              <div id="daily-plan-section" className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.02] p-3 scroll-mt-4">
                <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                  <div className="flex items-center gap-2 shrink-0">
                    <CalendarClock size={12} className="text-cyan-400" />
                    <span className="text-[11px] font-mono text-cyan-300 uppercase tracking-wider whitespace-nowrap">
                      每日自媒体计划
                    </span>
                    {/* 关联每日动态标识：当计划来自新闻摘要派生时显示 */}
                    {contentPlan.some(i => i.knowledgeNote?.includes('每日动态')) && (
                      <span
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/20"
                        title="主题来自 Hermes 每日动态的当日 AI 新闻热点"
                      >
                        <Newspaper size={9} />
                        关联每日动态
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* 从 Vault 加载 Content Brief（经营中心生成的爆款分析结果） */}
                    <button
                      onClick={async () => {
                        setPlanLoading(true);
                        try {
                          const today = new Date().toISOString().slice(0, 10);
                          const resp = await fetch(`/vault-api/list?path=10_Operations（运营层）/Content_Analysis`);
                          if (resp.ok) {
                            const data = await resp.json();
                            // 找到今天的简报文件
                            const dateDirs = (data.files || []).filter((f: any) => 
                              f.type === "directory" && f.name && f.name >= today.slice(0, 7)
                            );
                            let briefItems: ContentPlanItem[] = [];
                            for (const dir of dateDirs) {
                              const subResp = await fetch(`/vault-api/list?path=10_Operations（运营层）/Content_Analysis/${dir.name}`);
                              if (subResp.ok) {
                                const subData = await subResp.json();
                                for (const file of (subData.files || [])) {
                                  if (file.name?.endsWith("_brief.md") || file.name?.endsWith("_geo_brief.md")) {
                                    const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Content_Analysis/${dir.name}/${file.name}`)}`);
                                    if (readResp.ok) {
                                      const r = await readResp.json();
                                      if (r.success && r.content) {
                                        const titleMatch = r.content.match(/^#\s+(.+)/m);
                                        const platformMatch = file.name.match(/^(douyin|wechat|xiaohongshu)_/);
                                        const plt: SocialPlatform = platformMatch?.[1] as SocialPlatform || "wechat_official";
                                        briefItems.push({
                                          id: `brief-${Date.now()}-${briefItems.length}`,
                                          platform: plt,
                                          topic: titleMatch?.[1]?.replace(` · ${file.name.split("_").slice(1).join(" ")}`, "") || "Content Brief",
                                          contentType: "article" as const,
                                          date: today,
                                          priority: "medium" as const,
                                          status: "pending" as const,
                                          knowledgeNote: `Vault: 10_Operations（运营层）/Content_Analysis/${dir.name}/${file.name}`,
                                        });
                                      }
                                    }
                                  }
                                }
                              }
                            }
                            if (briefItems.length > 0) {
                              setContentPlan((prev) => [...briefItems, ...prev]);
                              alert(`已从 Vault 加载 ${briefItems.length} 条 Content Brief`);
                            } else {
                              alert("Vault 中暂无 Content Brief。请先在经营中心运行「GEO+DSO 增长分析」。");
                            }
                          }
                        } catch (e) {
                          alert("加载失败: " + (e as Error).message);
                        } finally {
                          setPlanLoading(false);
                        }
                      }}
                      disabled={planLoading}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-violet-300 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 rounded transition-colors disabled:opacity-40"
                      title="从 Obsidian Vault 加载经营中心生成的 Content Brief"
                    >
                      <FileText size={10} />
                      加载 Vault 简报
                    </button>
                    {/* v5.2：一键采集今日新闻（复活链路源头） */}
                    <button
                      onClick={handleFetchTodayNews}
                      disabled={newsFetching || planLoading || generating.active}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="采集今日 AI 行业新闻写入 Vault（NewsRuntime），随后自动刷新今日计划，派生「每日动态」选题"
                    >
                      {newsFetching ? <Loader2 size={10} className="animate-spin" /> : <Newspaper size={10} />}
                      {newsFetching ? "采集中" : "采集今日新闻"}
                    </button>
                    {/* 手动刷新今日计划：强制从 Obsidian 知识库重新生成 */}
                    <button
                      onClick={handleRefreshTodayPlan}
                      disabled={refreshingPlan || planLoading || generating.active}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="从 Obsidian 知识库重新生成今日计划"
                    >
                      {refreshingPlan ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                      刷新今日计划
                    </button>
                    {/* v5.2 按主题重写：不满意自动选题时，输入主题（如 XEAOS）重写整份计划 */}
                    <div className="flex items-center gap-1">
                      <input
                        value={planTopicHint}
                        onChange={(e) => setPlanTopicHint(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && planTopicHint.trim()) handleRegenerateByTopic();
                        }}
                        placeholder="主题（如 XEAOS / 酒店 / 量化）"
                        className="w-[110px] px-2 py-1 text-[9px] font-mono bg-white/[0.03] border border-white/[0.08] rounded focus:outline-none focus:border-purple-500/40 placeholder-gray-600"
                      />
                      <button
                        onClick={handleRegenerateByTopic}
                        disabled={regeneratingTopic || !planTopicHint.trim() || generating.active}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="按输入主题重写今日计划（全部围绕该主题选题）"
                      >
                        {regeneratingTopic ? <Loader2 size={10} className="animate-spin" /> : <PencilRuler size={10} />}
                        按主题重写
                      </button>
                    </div>
                    <button
                      onClick={loadContentPlan}
                      disabled={planLoading}
                      className="p-1 text-gray-500 hover:text-cyan-400 transition-colors"
                      title="刷新计划"
                    >
                      <RefreshCw size={10} className={planLoading ? "animate-spin" : ""} />
                    </button>
                    {/* v6.1.10 封面风格下拉：默认每次随机，可固定选一个（同步生成封面用） */}
                    <select
                      value={style}
                      onChange={(e) => setStyle(e.target.value)}
                      className="px-1.5 py-1 rounded text-[9px] font-mono bg-white/[0.03] border border-white/[0.08] text-gray-300 outline-none focus:border-purple-500/40 max-w-[170px]"
                      title="封面视觉风格：默认每次随机不同，可固定选择一个"
                    >
                      {COVER_STYLES.map((s) => (
                        <option key={s} value={s} className="bg-gray-900 text-gray-200">
                          {s.split('，')[0]}（{s.split('，')[1] || ''}）
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* 计划存档位置提示：明确每日计划的 Obsidian 存储路径 */}
                <div className="flex items-center justify-between mt-0.5 mb-2">
                  <p className="text-[9px] font-mono text-gray-500">
                    📁 计划存档：Obsidian / 10_Operations（运营层）/Content/每日自媒体计划-今日日期.md
                  </p>
                  <button
                    onClick={loadTodayPlanDoc}
                    className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 rounded transition-colors"
                    title="在界面内查看完整的今日计划文档"
                  >
                    <Eye size={9} /> 查看今日计划
                  </button>
                </div>
                {/* 刷新今日计划结果提示：成功绿色 / 失败红色 */}
                {refreshMessage && (
                  <div
                    className={`mb-2 text-[10px] font-mono px-2 py-1.5 rounded border ${
                      refreshMessage.success
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                        : "bg-red-500/10 border-red-500/30 text-red-300"
                    }`}
                  >
                    {refreshMessage.message}
                  </div>
                )}
                {/* v5.2：新闻采集结果提示 */}
                {newsMessage && (
                  <div
                    className={`mb-2 text-[10px] font-mono px-2 py-1.5 rounded border ${
                      newsMessage.success
                        ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                        : "bg-red-500/10 border-red-500/30 text-red-300"
                    }`}
                  >
                    <Newspaper size={10} className="inline mr-1 mb-0.5" />
                    {newsMessage.message}
                  </div>
                )}
                {/* 今日计划为空时的引导提示：告诉用户去 CEO Workspace 生成 */}
                {!planLoading && contentPlan.length === 0 && (
                  <div className="mb-2 px-2.5 py-2 rounded border border-amber-500/30 bg-amber-500/[0.05] text-[10px] font-mono text-amber-300">
                    <CalendarClock size={11} className="inline mr-1 mb-0.5" />
                    今日内容计划为空。请先到 <span className="text-amber-200 font-semibold">CEO Workspace</span> 点击「生成今日计划」，
                    然后回到这里点击上方「刷新今日计划」按钮关联。
                  </div>
                )}
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {planLoading && contentPlan.length === 0 ? (
                    <div className="py-4 text-center">
                      <Loader2 size={14} className="mx-auto text-cyan-400 animate-spin" />
                      <p className="text-[10px] font-mono text-gray-600 mt-1">加载计划...</p>
                    </div>
                  ) : (
                    // 按平台分组展示（6平台独立分组，始终显示所有平台）
                    // 白皮书铁律一：无数据显示"暂无"但保持平台结构可见
                    // 用户偏好：每个平台独立分组，含图标/名称/内容数/✓N/待N/生成按钮
                    (Object.keys(PLATFORM_CONFIG) as SocialPlatform[]).map((platform) => {
                      const cfg = PLATFORM_CONFIG[platform];
                      const platformItems = contentPlan.filter(item => item.platform === platform);
                      const completedCount = platformItems.filter(i => i.status === 'completed' || i.status === 'published').length;
                      const pendingCount = platformItems.filter(i => i.status === 'pending' || i.status === 'failed').length;

                      return (
                        <div key={platform} className="rounded-md border border-white/[0.04] bg-white/[0.01] overflow-hidden">
                          {/* 平台标题栏：图标/名称/内容数量/已生成数(✓N)/待生成数(待N) + 分类操作按钮（查阅/同步/生成） */}
                          {/* v6.2.27 标题栏可点击展开/收起（平台多内容时避免下拉查找不便） */}
                          <div
                            className="flex items-center justify-between px-2.5 py-1.5 border-b border-white/[0.04] cursor-pointer select-none"
                            style={{ background: `${cfg.color}10` }}
                            onClick={() => togglePlatformExpand(platform)}
                            title={expandedPlatforms.has(platform) ? "点击收起" : "点击展开"}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <ChevronRight
                                size={10}
                                className={`text-gray-500 flex-shrink-0 transition-transform ${expandedPlatforms.has(platform) ? "rotate-90" : ""}`}
                              />
                              <span className="text-[14px] flex-shrink-0">{cfg.icon}</span>
                              <span className="text-[10px] font-mono font-semibold truncate" style={{ color: cfg.color }}>
                                {cfg.name}
                              </span>
                              <span className="text-[9px] font-mono text-gray-500 flex-shrink-0">
                                内容{platformItems.length}
                              </span>
                              {completedCount > 0 && (
                                <span className="text-[9px] font-mono text-emerald-400 flex-shrink-0">
                                  ✓{completedCount}
                                </span>
                              )}
                              {pendingCount > 0 && (
                                <span className="text-[9px] font-mono text-amber-400 flex-shrink-0">
                                  待{pendingCount}
                                </span>
                              )}
                            </div>
                            {/* 分类操作按钮组：自定义 / 查阅 / 同步 / 生成 */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {/* v6.45 去写稿：左栏工作台切到「AI 创作」并预选本平台（同屏联动，不跳页） */}
                              {WB_PLAT_MAP[platform] && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    gotoWorkbenchCreate(WB_PLAT_MAP[platform] as string);
                                  }}
                                  className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono text-pink-300 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30 rounded transition-colors"
                                  title={`左侧工作台切到「AI 创作」并预选「${WB_PLAT_MAP[platform]}」的模板字段`}
                                >
                                  <PenSquare size={9} /> 去写稿
                                </button>
                              )}
                              {/* v6.31 自定义标题生成：输入标题只给本平台生成一篇（不碰其他平台） */}
                              <button
                                onClick={() => setCustomOpen((prev) => ({ ...prev, [platform]: !prev[platform] }))}
                                className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded transition-colors"
                                title={`输入标题，只给${cfg.name}生成一篇（不影响其他平台）`}
                              >
                                <PenSquare size={9} /> 自定义
                              </button>
                              {/* 查阅按钮：该平台有已生成内容时显示，点击查看最新文章 */}
                              {completedCount > 0 && (
                                <button
                                  onClick={() => handleViewPlatformLatest(platform)}
                                  disabled={vaultFetching[platform]}
                                  className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors disabled:opacity-40"
                                  title={`查阅${cfg.name}最新生成的文章内容`}
                                >
                                  {vaultFetching[platform] ? <Loader2 size={9} className="animate-spin" /> : <Eye size={9} />} {vaultFetching[platform] ? '打开中...' : '查阅'}
                                </button>
                              )}
                              {/* v6.1.14 内容通过按钮：生成标题+封面并同步（用户诉求：不用单独点同步，
                                  生成内容后直接"内容通过"一步到位 = 标记通过 + 生成标题封面 + 同步营销中心） */}
                              {completedCount > 0 && (
                                <button
                                  onClick={() => handleSyncPlatformToMarketing(platform)}
                                  disabled={platformSyncing[platform]}
                                  className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 rounded transition-colors disabled:opacity-40"
                                  title={`内容通过：生成标题+封面并同步到营销中心${platform === 'wechat' ? ' + 微信公众号自动发布' : ''}`}
                                >
                                  {platformSyncing[platform] ? <Loader2 size={9} className="animate-spin" /> : <ArrowRightLeft size={9} />}
                                  {platformSyncing[platform] ? '通过中(含封面)...' : '内容通过 · 生成标题+封面并同步'}
                                </button>
                              )}
                              {/* 分类生成按钮：仅在该平台有待生成项时显示
                                  v5.2.2 修复：仅「本平台」生成中时转圈/禁用，不再全局冻结所有平台 */}
                              {pendingCount > 0 && (
                                <button
                                  onClick={() => handleGenerateByPlatform(platform)}
                                  disabled={generating.active && generating.platform === platform}
                                  className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono text-white rounded transition-colors disabled:opacity-40"
                                  style={{ background: `${cfg.color}cc` }}
                                  title={`一键生成${cfg.name}全部待处理内容`}
                                >
                                  {generating.active && generating.platform === platform ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                                  生成
                                </button>
                              )}
                            </div>
                          </div>
                          {/* v6.31 自定义标题输入框：只生成本平台这一篇 */}
                          {customOpen[platform] && (
                            <div className="px-2.5 py-1.5 border-b border-white/[0.04] flex items-center gap-1.5 bg-amber-500/[0.03]">
                              <input
                                value={customDraft[platform] || ''}
                                onChange={(e) => setCustomDraft((prev) => ({ ...prev, [platform]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') void handleGenerateCustomTopic(platform); }}
                                placeholder={`输入标题，只给${cfg.name}写这篇`}
                                autoFocus
                                className="flex-1 min-w-0 px-2 py-1 rounded text-[10px] bg-white/[0.05] border border-white/[0.08] text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/40"
                              />
                              <button
                                onClick={() => void handleGenerateCustomTopic(platform)}
                                disabled={generating.active && generating.platform === platform}
                                className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-white bg-amber-600/80 hover:bg-amber-600 disabled:opacity-40 transition-colors flex-shrink-0"
                              >
                                {generating.active && generating.platform === platform ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                                生成这篇
                              </button>
                            </div>
                          )}
                          {/* 平台同步结果提示：成功绿色 / 失败红色（每个分类框内独立显示） */}
                          {platformSyncResult[platform] && (
                            <div
                              className={`px-2.5 py-1 text-[9px] font-mono border-b ${
                                platformSyncResult[platform]!.success
                                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                                  : "bg-red-500/10 border-red-500/30 text-red-300"
                              }`}
                            >
                              {platformSyncResult[platform]!.message}
                            </div>
                          )}

                          {/* 平台下的内容项（v6.2.27 收起时隐藏，点击标题栏展开） */}
                          {expandedPlatforms.has(platform) && (
                          <div className="space-y-1 p-1.5">
                            {platformItems.length === 0 ? (
                              <p className="text-[10px] text-gray-600 text-center py-1.5 italic">
                                暂无内容
                              </p>
                            ) : platformItems.map((item) => {
                              const statusColor =
                                item.status === 'completed' ? "text-emerald-400" :
                                item.status === 'generating' ? "text-blue-400 animate-pulse" :
                                item.status === 'failed' ? "text-red-400" :
                                item.status === 'published' ? "text-emerald-400" :
                                "text-gray-500";
                              const statusText =
                                item.status === 'completed' ? "已生成" :
                                item.status === 'generating' ? "生成中" :
                                item.status === 'failed' ? "失败" :
                                item.status === 'published' ? "已发布" :
                                "待生成";
                              return (
                                <div
                                  key={item.id}
                                  className="rounded border border-white/[0.03] bg-white/[0.01] px-2 py-1.5"
                                >
                                  <div className="flex items-center gap-1.5 mb-1">
                                    {item.priority === 'high' && (
                                      <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-red-500/15 text-red-400">
                                        高
                                      </span>
                                    )}
                                    <span className="text-[11px] text-gray-200 font-medium truncate flex-1" title={item.topic}>
                                      {/* v6.1.2 知乎问答模式：标题显示"问题"（问答≠文章，问题即标题），原主题降级为次级信息 */}
                                      {item.platform === 'zhihu' && item.itemType === 'qa' && (item.question || zhihuDraft[item.id]?.trim())
                                        ? `❓ ${item.question || zhihuDraft[item.id]?.trim()}`
                                        : item.topic}
                                    </span>
                                    <span className={`text-[9px] font-mono ${statusColor}`}>
                                      {statusText}
                                    </span>
                                  </div>
                                  {item.knowledgeNote && (
                                    <div className="flex items-center gap-1 text-[9px] font-mono text-cyan-600 mb-1 truncate">
                                      <BookOpen size={9} />
                                      <span className="truncate">{item.knowledgeNote}</span>
                                    </div>
                                  )}
                                  {/* v6.1 知乎问答模式：模式切换 + 问题输入（知乎条目专属，未生成时可操作） */}
                                  {item.platform === 'zhihu' && (
                                    <div className="mb-1 rounded border border-blue-500/15 bg-blue-500/[0.04] p-1.5">
                                      <div className="flex items-center gap-1">
                                        <span className="text-[9px] font-mono text-gray-500 mr-0.5">模式</span>
                                        <button
                                          onClick={() => handleZhihuModeChange(item, 'article')}
                                          className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
                                            item.itemType !== 'qa'
                                              ? 'bg-blue-500/25 text-blue-300 border border-blue-500/40'
                                              : 'text-gray-500 hover:bg-white/[0.04] border border-transparent'
                                          }`}
                                          title="文章模式：生成知乎专栏文章"
                                        >
                                          文章
                                        </button>
                                        <button
                                          onClick={() => handleZhihuModeChange(item, 'qa')}
                                          className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
                                            item.itemType === 'qa'
                                              ? 'bg-blue-500/25 text-blue-300 border border-blue-500/40'
                                              : 'text-gray-500 hover:bg-white/[0.04] border border-transparent'
                                          }`}
                                          title="问答模式：填入问题，生成知乎回答（更能体现知乎提问价值）"
                                        >
                                          ❓问答
                                        </button>
                                        {item.itemType === 'qa' && (
                                          <span className="ml-auto text-[8px] font-mono text-blue-400/70">
                                            {item.question ? '已设问题' : '待填问题'}
                                          </span>
                                        )}
                                      </div>
                                      {item.itemType === 'qa' && (
                                        <div className="mt-1 flex items-center gap-1">
                                          <input
                                            value={zhihuDraft[item.id] ?? item.question ?? ''}
                                            onChange={(e) => setZhihuDraft((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                            placeholder="输入知乎问题，如：做 AI 自动运营半年是什么体验？"
                                            className="flex-1 min-w-0 px-1.5 py-1 rounded text-[9px] font-mono bg-white/[0.04] border border-white/[0.08] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-500/40"
                                          />
                                          <button
                                            onClick={() => {
                                              const q = (zhihuDraft[item.id] ?? item.question ?? '').trim();
                                              handleZhihuQuestionSave(item, q);
                                              setZhihuDraft((prev) => ({ ...prev, [item.id]: q }));
                                            }}
                                            className="flex-shrink-0 px-2 py-1 rounded text-[9px] font-mono text-blue-300 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 transition-colors"
                                            title="保存问题：生成时按此问题输出知乎回答"
                                          >
                                            设为问题
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {/* 生成完成预览：封面大图可点击查看全屏，脚本预览更长可点击查看完整
                                      （v5.2.2 兼容：不强制 packageId，findPkgForItem 支持 topic 匹配） */}
                                  {item.status === 'completed' && (() => {
                                    // v5.2：容错查找（packageId → topic → currentPackage）
                                    const pkg = findPkgForItem(item);
                                    if (!pkg) return null;
                                    const script = pkg.articleScript || pkg.videoScript || pkg.xiaohongshuScript || pkg.bilibiliScript || pkg.douyinScript || pkg.momentsScript;
                                    // v6.33 恢复全文预览：不再 slice(0,150)+line-clamp-3 截断（用户反馈"之前能看到全文，直接复制即可"）
                                    const scriptPreview = script?.content || '';
                                    const coverUrl = pkg.cover?.url || pkg.wechatCover?.url || pkg.douyinCover?.url;
                                    return (
                                      <div className="mt-1.5 mb-1 rounded border border-cyan-500/20 bg-cyan-500/[0.03] p-2">
                                        {/* 封面大图：可点击查看全屏（复用已有 setPreview 全屏预览 Modal） */}
                                        {coverUrl && (
                                          <img
                                            src={coverUrl}
                                            alt="封面"
                                            className="w-full h-24 object-cover rounded mb-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            onClick={() => setPreview({ type: 'image', url: coverUrl, title: '封面' })}
                                          />
                                        )}
                                        {/* 脚本预览：全文直接展示（长文内部滚动，可直接复制，无需另开入口） */}
                                        {scriptPreview && (
                                          <div className="cursor-pointer hover:bg-white/[0.03] rounded p-1 -m-1 transition-colors"
                                               onClick={() => setEditingScript({ asset: script!, content: script?.content || '' })}>
                                            <p className="text-[10px] font-mono text-gray-300 leading-relaxed whitespace-pre-wrap break-words max-h-[320px] overflow-y-auto">
                                              {scriptPreview}
                                            </p>
                                            <p className="text-[9px] text-cyan-400 mt-0.5">全文已直接展示（点击可进入编辑）</p>
                                          </div>
                                        )}
                                        {/* 无内容提示 */}
                                        {!coverUrl && !scriptPreview && (
                                          <p className="text-[10px] text-gray-500 text-center py-1">素材已生成，点击下方查看</p>
                                        )}
                                      </div>
                                    );
                                  })()}
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {(item.status === 'pending' || item.status === 'failed') && (
                                      <>
                                        <button
                                          onClick={() => {
                                            // v5.2 成本优化：直接生成（1 次 LLM）——对比生成需 2 次 LLM 多耗 token，按用户决策取消
                                            handleGenerateFromPlanItem(item);
                                          }}
                                          // v5.2.2 修复：仅当前项生成中时禁用/转圈，不再全局冻结所有项
                                          disabled={generating.active && generating.itemId === item.id}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 rounded transition-colors disabled:opacity-40"
                                        >
                                          {generating.active && generating.itemId === item.id ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                                          {generating.active && generating.itemId === item.id ? "生成中..." : "生成"}
                                        </button>
                                        {/* v6：未生成也可「换一篇」= 换选题（标题不满意不用先生成，直接换个主题） */}
                                        <button
                                          onClick={() => handleSwitchTopic(item)}
                                          disabled={generating.active}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-purple-200 bg-purple-500/20 hover:bg-purple-500/35 border border-purple-500/40 rounded transition-colors disabled:opacity-40"
                                          title="换一篇：标题/选题不满意就点这里，从今日新闻或知识库换一个新选题"
                                        >
                                          <RefreshCcw size={9} />
                                          换一篇
                                        </button>
                                      </>
                                    )}
                                    {/* v6 已生成项双按钮：换标题（换选题）+ 换内容（同标题重新生成）
                                        用户诉求：已生成内容基于同一标题太单一，标题/内容都应可换 */}
                                    {item.status === 'completed' && (
                                      <>
                                        <button
                                          onClick={() => handleSwitchTopic(item)}
                                          disabled={generating.active}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-purple-200 bg-purple-500/15 hover:bg-purple-500/30 border border-purple-500/30 rounded transition-colors disabled:opacity-40"
                                          title="换标题：换一个新选题（从今日新闻/知识库），内容随之重新生成"
                                        >
                                          <RefreshCw size={9} />
                                          换标题
                                        </button>
                                        <button
                                          onClick={() => handleRegenerateItem(item)}
                                          disabled={generating.active && generating.itemId === item.id}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-purple-200 bg-purple-500/20 hover:bg-purple-500/35 border border-purple-500/40 rounded transition-colors disabled:opacity-40"
                                          title="换内容：保持当前标题，重新生成一篇全新内容"
                                        >
                                          {generating.active && generating.itemId === item.id ? <Loader2 size={9} className="animate-spin" /> : <RefreshCcw size={9} />}
                                          {generating.active && generating.itemId === item.id ? "生成中..." : "换内容"}
                                        </button>
                                      </>
                                    )}
                                    {/* v5.2.2 关键修复：预览/改脚本/发布 全部独立于 currentPackage/packages 内存态，
                                        点击时通过 resolvePkgForItem（内存 → Vault 兜底）实时解析，
                                        根治「重新生成后内存未就绪 → 按钮全部消失」的竞态 */}
                                    {item.status === 'completed' && item.packageId && (
                                      <>
                                        <button
                                          onClick={async () => {
                                            const pkg = await resolvePkgForItem(item);
                                            if (!pkg) {
                                              setPlatformSyncResult((prev) => ({
                                                ...prev,
                                                [item.platform]: { success: false, message: `未找到「${item.topic}」的内容包，请点重新生成` },
                                              }));
                                              setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [item.platform]: null })), 4000);
                                              return;
                                            }
                                            const script = pkg.articleScript || pkg.videoScript || pkg.xiaohongshuScript || pkg.bilibiliScript || pkg.douyinScript || pkg.momentsScript;
                                            if (script) {
                                              setEditingScript({ asset: script, content: script.content || '' });
                                            } else if (pkg.cover?.url) {
                                              setPreview({ type: 'image', url: pkg.cover.url, title: item.topic });
                                            } else {
                                              setPlatformSyncResult((prev) => ({
                                                ...prev,
                                                [item.platform]: { success: false, message: `${PLATFORM_CONFIG[item.platform].name}内容已生成但脚本为空，请点重新生成` },
                                              }));
                                              setTimeout(() => setPlatformSyncResult((prev) => ({ ...prev, [item.platform]: null })), 4000);
                                            }
                                          }}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded transition-colors"
                                        >
                                          <Eye size={9} /> 预览
                                        </button>
                                        <button
                                          onClick={async () => {
                                            const pkg = await resolvePkgForItem(item);
                                            if (!pkg) return;
                                            const script = pkg.articleScript || pkg.videoScript || pkg.xiaohongshuScript || pkg.bilibiliScript || pkg.douyinScript || pkg.momentsScript;
                                            if (script) setEditingScript({ asset: script, content: script.content || '' });
                                          }}
                                          // v5.2.2 修复：仅当前项生成中时禁用
                                          disabled={generating.active && generating.itemId === item.id}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded transition-colors disabled:opacity-40"
                                        >
                                          <Edit3 size={9} /> 改脚本
                                        </button>
                                        <button
                                          onClick={async () => {
                                            const pkg = await resolvePkgForItem(item);
                                            if (!pkg) return;
                                            await handlePublishToPlatform(item, pkg);
                                          }}
                                          disabled={publishing === item.id}
                                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors disabled:opacity-40"
                                        >
                                          {publishing === item.id ? <Loader2 size={9} className="animate-spin" /> : <Send size={9} />}
                                          {item.platform === 'wechat' ? '自动发布' : '手动发布'}
                                        </button>
                                      </>
                                    )}
                                  </div>
                                  {publishResult[item.id] && (
                                    <div className={`mt-1 text-[9px] font-mono ${publishResult[item.id].success ? "text-emerald-400" : "text-red-400"}`}>
                                      {publishResult[item.id].message}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* 今日计划文档查看器：界面内直接查看完整计划内容，无需翻找 Obsidian */}
              {planDocViewer && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setPlanDocViewer(null)}>
                  <div className="w-full max-w-3xl max-h-[80vh] rounded-lg border border-cyan-500/30 bg-[#0B1220] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-cyan-400" />
                        <span className="text-[12px] font-mono text-cyan-300 uppercase tracking-wider">今日自媒体计划</span>
                      </div>
                      <button onClick={() => setPlanDocViewer(null)} className="text-gray-500 hover:text-white transition-colors">
                        <X size={16} />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      {planDocViewer.loading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 size={20} className="text-cyan-400 animate-spin" />
                        </div>
                      ) : (
                        <pre className="text-[11px] font-mono text-gray-300 whitespace-pre-wrap leading-relaxed">
                          {planDocViewer.content}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 待发布队列 */}
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.02] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Send size={12} className="text-emerald-400" />
                  <span className="text-[11px] font-mono text-emerald-300 uppercase tracking-wider">
                    待发布队列
                  </span>
                  <span className="text-[10px] font-mono text-gray-600 ml-auto">
                    {publishQueue.length}
                  </span>
                </div>
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {publishQueue.length === 0 ? (
                    <p className="text-[11px] text-gray-600 text-center py-3">
                      暂无待发布素材
                    </p>
                  ) : (
                    publishQueue.map((pkg) => (
                      <div
                        key={pkg.id}
                        className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-2"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] text-gray-200 font-medium truncate">
                            {pkg.topic}
                          </span>
                          <button
                            onClick={() => handlePublish(pkg.id)}
                            className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors flex-shrink-0"
                          >
                            <Send size={9} />
                            发布
                          </button>
                        </div>
                        <div className="flex items-center gap-1">
                          {pkg.cover && <ImageIcon size={9} className="text-pink-400" />}
                          {pkg.articleScript && <FileText size={9} className="text-blue-400" />}
                          {pkg.video && <Video size={9} className="text-cyan-400" />}
                          {pkg.bgm && <Music size={9} className="text-purple-400" />}
                          <span className="text-[9px] font-mono text-gray-600 ml-1">
                            {pkg.createdAt.slice(0, 10)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* 发布按钮（当前包） */}
              {currentPackage && (
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Send size={12} className="text-emerald-400" />
                    <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                      当前包发布
                    </span>
                  </div>
                  <div className="text-[11px] font-mono text-gray-500 mb-2">
                    已审核：{approvedAssets.size} / {countAssets(currentPackage)} 项
                  </div>
                  <button
                    onClick={() => handlePublish(currentPackage.id)}
                    disabled={approvedAssets.size === 0}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-medium text-white bg-emerald-600/80 hover:bg-emerald-600 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send size={12} />
                    一键发布
                  </button>

                  {/* 同步到营销中心（关联6平台，不开新tab） */}
                  <button
                    onClick={handleSyncToMarketing}
                    disabled={syncingToMarketing}
                    className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-medium text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    title="将各平台脚本写入 ContentRuntime，营销中心可查询（微信公众号/抖音企业号/抖音个人号/小红书/知乎/B站）"
                  >
                    {syncingToMarketing ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <ArrowRightLeft size={12} />
                    )}
                    {syncingToMarketing ? "同步6平台中..." : "同步到营销中心（6平台）"}
                  </button>
                  {syncMessage && (
                    <div
                      className={`mt-2 text-[10px] font-mono px-2 py-1.5 rounded border ${
                        syncMessage.success
                          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                          : "bg-red-500/10 border-red-500/30 text-red-300"
                      }`}
                    >
                      <div>{syncMessage.message}</div>
                      {syncMessage.canJumpToMarketing && (
                        <button
                          onClick={() => setWorkspace("marketing")}
                          className="mt-1 w-full flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded transition-colors"
                        >
                          <ExternalLink size={10} />
                          跳转到营销中心查看公众号内容
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 历史媒体包 */}
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Layout size={12} className="text-blue-400" />
                  <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                    历史媒体包
                  </span>
                  <span className="text-[10px] font-mono text-gray-600 ml-auto">
                    {packages.length}
                  </span>
                </div>
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                  {packages.length === 0 ? (
                    <p className="text-[11px] text-gray-600 text-center py-4">
                      暂无历史记录
                    </p>
                  ) : (
                    packages.map((pkg) => (
                      <button
                        key={pkg.id}
                        onClick={() => setCurrentPackage(pkg)}
                        className="w-full text-left px-2.5 py-2 rounded-md hover:bg-white/[0.03] transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-gray-300 font-medium truncate">
                            {pkg.topic}
                          </span>
                          <ChevronRight
                            size={12}
                            className="text-gray-600 group-hover:text-gray-400 flex-shrink-0"
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <StatusDot status={pkg.status} />
                          <span className="text-[10px] font-mono text-gray-600">
                            {pkg.createdAt.slice(0, 16).replace("T", " ")}
                          </span>
                          {pkg.sourceNotes && pkg.sourceNotes.length > 0 && (
                            <BookOpen size={9} className="text-cyan-600" />
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
                </>
              )}

              {viewMode === "library" && (
                <ScriptLibraryView
                  scripts={libraryScripts}
                  loading={libraryLoading}
                  onRefresh={loadLibraryScripts}
                  onView={handleViewScript}
                />
              )}
            </div>
          </div>
        </div>
        )}
      </motion.div>

      {/* ============ 生成前对比选择弹窗（v5.2：约束版 vs 裸写版 → 人选最优） ============ */}
      <ContentPreCompareModal
        open={!!compareItem}
        topic={compareItem?.topic || ""}
        platformLabel={compareItem ? PLATFORM_CONFIG[compareItem.platform].name : ""}
        platformId={compareItem?.platform || "wechat"}
        onClose={() => setCompareItem(null)}
        onSelect={handleCompareSelect}
      />

      {/* ============ 脚本编辑模态框 ============ */}      <AnimatePresence>
        {editingScript && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditingScript(null)}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl max-h-[80vh] bg-[#0a0e1a] rounded-xl border border-blue-500/20 overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Edit3 size={14} className="text-blue-400" />
                  <h3 className="text-[14px] font-semibold text-white">
                    编辑脚本 · {editingScript.asset.title || editingScript.asset.purpose}
                  </h3>
                  <span className="text-[10px] font-mono text-gray-600 ml-2">
                    {editingScript.content.length} 字
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadScript}
                    className="flex items-center gap-1 px-3 py-1 text-[11px] font-mono text-cyan-300 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/30 rounded transition-colors"
                    title="下载为 Markdown 文件到本地"
                  >
                    <Download size={11} /> 下载
                  </button>
                  <button
                    onClick={handleSaveScript}
                    className="flex items-center gap-1 px-3 py-1 text-[11px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors"
                    title="保存：更新内容并写回 Obsidian（XEAOS/ContentPackages/）"
                  >
                    <Save size={11} /> 保存
                  </button>
                  <button
                    onClick={() => setEditingScript(null)}
                    className="p-1 text-gray-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <textarea
                  value={editingScript.content}
                  onChange={(e) => setEditingScript({ ...editingScript, content: e.target.value })}
                  className="w-full h-[55vh] text-[13px] text-gray-200 bg-white/[0.03] border border-white/[0.06] rounded-md p-3 focus:outline-none focus:border-blue-500/40 resize-none font-mono leading-relaxed"
                  placeholder="在此编辑脚本内容..."
                />
              </div>
              <div className="px-5 py-2 border-t border-white/[0.06] flex items-center justify-between gap-3 text-[10px] font-mono text-gray-600">
                {/* v5.2.3 参考图入口：先生成封面 → 自动把主播头像+标题文字合成（口播平台） */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <input
                    ref={anchorInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAnchorPhotoUpload}
                  />
                  <button
                    onClick={() => anchorInputRef.current?.click()}
                    title="上传主播参考图：封面生成后将把主播头像合成到左下角（口播平台）+ 自动添加标题文字"
                    className={`flex items-center gap-1 px-2 py-1 rounded border transition ${
                      anchorPhoto
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
                        : "bg-white/[0.03] border-white/[0.08] text-gray-400 hover:border-pink-500/30 hover:text-pink-300"
                    }`}
                  >
                    {anchorPhoto ? <img src={anchorPhoto} alt="参考图" className="w-4 h-4 rounded-full object-cover" /> : <Camera size={11} />}
                    {anchorPhoto ? "参考图已设置" : "上传主播参考图"}
                  </button>
                  {anchorPhoto && (
                    <button
                      onClick={() => { setAnchorPhoto(null); try { localStorage.removeItem("xeaos_anchor_photo"); } catch { /* 忽略 */ } }}
                      title="清除参考图"
                      className="text-gray-500 hover:text-red-400 transition"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* v5.2 生成流程拆分：内容通过 → 在此触发标题+封面生成并同步营销中心
                      v5.2.4 交互优化：未设参考图时先确认是否需要插入（提醒作用），否→直接生成标题+封面 */}
                  <button
                    onClick={async () => {
                      // 参考图确认交互
                      if (!anchorPhoto) {
                        const needRef = window.confirm(
                          "未设置主播参考图。\n\n点「确定」→ 先上传主播参考图（口播封面将合成主播头像）\n点「取消」→ 直接生成标题文字 + AI 封面"
                        );
                        if (needRef) {
                          // 引导上传：打开文件选择器，上传后需再点一次本按钮完成同步
                          anchorInputRef.current?.click();
                          return;
                        }
                      }
                      // 找到当前脚本所属平台（v6.1.14 优先用包的真实平台，避免 purpose 推断错误）
                      const purpose = editingScript.asset.purpose;
                      const pkgPlatform = (currentPackage as any)?.platform as SocialPlatform | undefined;
                      const platformOfScript: SocialPlatform =
                        pkgPlatform && PLATFORM_CONFIG[pkgPlatform]
                          ? pkgPlatform
                          : purpose === 'article_script' ? 'wechat' :
                          purpose === 'douyin_script' ? (currentPackage?.id?.includes('douyin_enterprise') ? 'douyin_enterprise' : 'douyin_personal') :
                          purpose === 'xiaohongshu_script' ? 'xiaohongshu' :
                          purpose === 'bilibili_script' ? 'bilibili' :
                          purpose === 'video_script' ? 'wechat' : 'wechat';
                      setEditingScript(null);
                      await handleSyncPlatformToMarketing(platformOfScript);
                    }}
                    className="flex items-center gap-1 px-3 py-1 text-[11px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 rounded transition-colors"
                    title="内容确认无误后：生成标题+封面 → 同步到营销中心（微信同步时自动发布）"
                  >
                    <ArrowRightLeft size={11} /> 内容通过 · 生成标题+封面并同步
                  </button>
                </div>
              </div>
              {saveTip && (
                <div className="px-5 py-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/[0.06] border-t border-emerald-500/20">
                  {saveTip}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ 全屏预览弹窗 ============ */}
      <AnimatePresence>
        {preview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreview(null)}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-8"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-4xl max-h-[90vh] rounded-lg overflow-hidden"
            >
              {preview.type === "image" && preview.url && (
                <img
                  src={preview.url}
                  alt={preview.title}
                  className="max-w-full max-h-[90vh] object-contain"
                />
              )}
              {preview.type === "video" && preview.url && (
                <video
                  src={preview.url}
                  controls
                  autoPlay
                  className="max-w-full max-h-[90vh]"
                />
              )}
              {preview.type === "audio" && preview.url && (
                <div className="bg-white/[0.04] p-8 min-w-[400px]">
                  <Music size={32} className="text-purple-400 mx-auto mb-3" />
                  <p className="text-center text-white text-sm mb-3">
                    {preview.title}
                  </p>
                  <audio
                    src={preview.url}
                    controls
                    autoPlay
                    className="w-full"
                  />
                </div>
              )}
              {preview.type === "script" && preview.content && (
                <div className="bg-[#0a0e1a] border border-white/10 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-blue-400" />
                      <h3 className="text-[14px] font-semibold text-white">{preview.title}</h3>
                      <span className="text-[10px] font-mono text-gray-600 ml-2 px-1.5 py-0.5 rounded bg-white/[0.04]">
                        Markdown 渲染
                      </span>
                    </div>
                    <button
                      onClick={() => setPreview(null)}
                      className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-5">
                    <SimpleMarkdownRenderer content={preview.content} />
                  </div>
                </div>
              )}
              {preview.type !== "script" && (
                <button
                  onClick={() => setPreview(null)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
                >
                  <X size={16} className="text-white" />
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ 脚本库内容查看 Modal ============ */}
      <AnimatePresence>
        {viewingScript && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setViewingScript(null)}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl max-h-[80vh] bg-[#0a0e1a] rounded-xl border border-blue-500/20 overflow-hidden flex flex-col"
            >
              {/* 标题栏：平台图标 + 主题 */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[16px] flex-shrink-0">
                    {getPlatformDisplay(viewingScript.item.platform).icon}
                  </span>
                  <h3 className="text-[14px] font-semibold text-white truncate">
                    {viewingScript.item.topic}
                  </h3>
                  <span className="text-[10px] font-mono text-gray-600 ml-2 flex-shrink-0">
                    {viewingScript.item.platform} · {viewingScript.item.date}
                  </span>
                </div>
                <button
                  onClick={() => setViewingScript(null)}
                  className="p-1 text-gray-400 hover:text-white flex-shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
              {/* 内容区：Markdown 原文（pre 标签简单展示） */}
              <div className="flex-1 overflow-y-auto p-4">
                {viewingScript.loading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 size={20} className="text-blue-400 animate-spin" />
                  </div>
                ) : (
                  <pre className="text-[12px] text-gray-200 font-mono whitespace-pre-wrap break-words leading-relaxed">
                    {viewingScript.content}
                  </pre>
                )}
              </div>
              {/* 底部：关闭按钮 */}
              <div className="px-5 py-2 border-t border-white/[0.06] flex items-center justify-between text-[10px] font-mono text-gray-600">
                <span>
                  {viewingScript.content.length} 字 · {viewingScript.item.filename}
                </span>
                <button
                  onClick={() => setViewingScript(null)}
                  className="flex items-center gap-1 px-3 py-1 text-[11px] font-mono text-white bg-white/[0.06] hover:bg-white/[0.1] rounded transition-colors"
                >
                  <X size={11} /> 关闭
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// v5.2.3 主播参考图封面合成（口播封面样式）
// 把主播照片（圆形头像）合成到 AI 封面左下角，形成抖音口播封面
// 纯前端 Canvas 实现，不依赖图片 API 的图生图能力
// ============================================
async function composeAnchorCover(
  coverUrl: string,
  topic: string,
  platform: string
): Promise<string | null> {
  try {
    const anchor = localStorage.getItem("xeaos_anchor_photo");
    // 口播类平台才合成主播头像；标题文字所有平台都加
    const spoken = platform === 'douyin_enterprise' || platform === 'douyin_personal' || platform === 'live_stream';
    if (!coverUrl) return null;

    const loadImg = (src: string): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });

    // v6.1.10 跨域兜底：AI 封面来自外域图片服务，直连无 CORS 头会加载失败 →
    // 标题合成静默跳过（用户反馈"标题没合成上去"的根因）。失败时经 /img-proxy 代理加载。
    const loadImgWithFallback = async (src: string): Promise<HTMLImageElement | null> => {
      const direct = await loadImg(src).catch(() => null);
      if (direct) return direct;
      try {
        const proxyResp = await fetch(`/img-proxy?url=${encodeURIComponent(src)}`);
        if (!proxyResp.ok) return null;
        const blob = await proxyResp.blob();
        const objUrl = URL.createObjectURL(blob);
        const proxied = await loadImg(objUrl).catch(() => null);
        return proxied;
      } catch {
        return null;
      }
    };

    const coverImg = await loadImgWithFallback(coverUrl);
    if (!coverImg) return null;

    const W = coverImg.naturalWidth || 1024;
    const H = coverImg.naturalHeight || 1792;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // 1. 绘制 AI 封面
    ctx.drawImage(coverImg, 0, 0, W, H);

    // 2. 标题文字排版（v5.2.4 高级排版：渐变色 + 大小字层次 + 顶部布局，不遮挡主体）
    //    竖版（抖音 1024x1792）：顶部安全区 8%-26%；横版（公众号 1792x1024）：顶部 6%-22%
    const isPortrait = H > W;
    const rawTitle = String(topic || '').replace(/^#+\s*/, '').slice(0, 26);
    // 智能断行：按自然语义断（遇到空格/冒号/破折号优先断），每行不超 maxChars
    const maxChars = isPortrait ? 9 : 13;
    const chars = Array.from(rawTitle);
    const lines: string[] = [];
    let cur = '';
    const breakChars = [' ', '：', ':', '，', ',', '——', '—', '（', '(', '？', '？'];
    for (const ch of chars) {
      if (cur.length >= maxChars || (cur.length >= 4 && breakChars.includes(ch))) {
        lines.push(cur);
        cur = ch === ' ' ? '' : ch;
        if (lines.length >= 3) break;
      } else {
        cur += ch;
      }
    }
    if (cur && lines.length < 3) lines.push(cur);
    if (lines.length === 0) lines.push('XEAOS');
    const showLines = lines.slice(0, 3);

    // 字号层级：首行最大（主标题），后续递减
    const baseFont = isPortrait ? Math.round(W * 0.088) : Math.round(W * 0.058);
    const fonts = showLines.map((_, i) => Math.round(baseFont * (i === 0 ? 1 : 0.82)));
    const lineHeight = Math.round(baseFont * 1.28);
    // 标题区位置：顶部留白区（避开中心主体/人像）
    const topArea = isPortrait ? H * 0.075 : H * 0.06;
    const totalTextH = lineHeight * (showLines.length - 1) + Math.round(baseFont * 1.1);
    const boxW = W * 0.9;
    const boxX = (W - boxW) / 2;
    const boxY = topArea - Math.round(baseFont * 0.55);
    const boxH = totalTextH + Math.round(baseFont * 0.9);

    // 标题背景：深色渐变压暗条（保证文字在任何封面图上可读）
    ctx.save();
    const grad = ctx.createLinearGradient(0, boxY, 0, boxY + boxH);
    grad.addColorStop(0, 'rgba(0,0,0,0.78)');
    grad.addColorStop(0.6, 'rgba(0,0,0,0.55)');
    grad.addColorStop(1, 'rgba(0,0,0,0.25)');
    ctx.fillStyle = grad;
    const r = Math.round(baseFont * 0.42);
    ctx.beginPath();
    ctx.moveTo(boxX + r, boxY);
    ctx.lineTo(boxX + boxW - r, boxY);
    ctx.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + r, r);
    ctx.lineTo(boxX + boxW, boxY + boxH - r);
    ctx.arcTo(boxX + boxW, boxY + boxH, boxX + boxW - r, boxY + boxH, r);
    ctx.lineTo(boxX + r, boxY + boxH);
    ctx.arcTo(boxX, boxY + boxH, boxX, boxY + boxH - r, r);
    ctx.lineTo(boxX, boxY + r);
    ctx.arcTo(boxX, boxY, boxX + r, boxY, r);
    ctx.closePath();
    ctx.fill();

    // 标题文字：渐变色（品牌金→橙）+ 白色描边，每行不同字号
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const textGrad = ctx.createLinearGradient(0, boxY + boxH * 0.2, 0, boxY + boxH * 0.8);
    textGrad.addColorStop(0, '#FFE9A8');  // 淡金
    textGrad.addColorStop(0.45, '#FFD34D'); // 金
    textGrad.addColorStop(0.75, '#FF8C42'); // 橙
    textGrad.addColorStop(1, '#FF5E3A');   // 橙红
    const startY = boxY + boxH / 2 + Math.round(baseFont * 0.08);
    showLines.forEach((line, i) => {
      const y = startY - ((showLines.length - 1) * lineHeight) / 2 + i * lineHeight;
      ctx.save();
      ctx.font = `bold ${fonts[i]}px "PingFang SC", "Microsoft YaHei", sans-serif`;
      ctx.lineWidth = Math.max(3, Math.round(fonts[i] * 0.09));
      ctx.strokeStyle = 'rgba(0,0,0,0.85)';
      ctx.lineJoin = 'round';
      ctx.strokeText(line, W / 2, y);
      ctx.fillStyle = textGrad;
      ctx.fillText(line, W / 2, y);
      ctx.restore();
    });
    ctx.restore();

    // 3. 主播头像（仅口播平台 + 已配置参考图）
    if (spoken && anchor) {
      const anchorImg = await loadImgWithFallback(anchor);
      if (anchorImg) {
        const size = Math.round(W * 0.20);
        const margin = Math.round(W * 0.05);
        const cx = margin + size / 2;
        const cy = H - margin - size / 2;
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, size / 2 + 5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(anchorImg, cx - size / 2, cy - size / 2, size, size);
        ctx.restore();
        // 昵称标签
        ctx.save();
        ctx.font = `bold ${Math.round(W * 0.032)}px sans-serif`;
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        const label = '小舍AI员工';
        const tw = ctx.measureText(label).width;
        const lx = margin + size + 12;
        const ly = cy + Math.round(W * 0.012);
        ctx.fillRect(lx - 8, ly - Math.round(W * 0.028), tw + 16, Math.round(W * 0.042));
        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, lx, ly);
        ctx.restore();
      }
    }

    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    return null; // 合成失败时保留原封面
  }
}

// ============================================
// 媒体预览网格子组件（支持 Tab 过滤）
// ============================================

// v6.3.17 知识飞轮入口：发布成功/手动发布提示 → 记录内容结果到 ExperienceRuntime
// （零 token 入库；效果指标待人工回填；flywheel: 内容发布 → 记录 → 提炼经验 → 写回 Obsidian）
async function recordContentResultToExperience(input: {
  id: string;
  title: string;
  topic: string;
  domain: string;
  platform: string;
  metrics: { views: number; likes: number; leads?: number; deals?: number };
}): Promise<void> {
  try {
    await RuntimeManager.execute(RuntimeName.Experience, 'record_result', {
      ...input,
      publishedAt: new Date().toISOString(),
    });
    console.log('[知识飞轮] 已记录内容结果:', input.topic, '@', input.platform);
  } catch (err) {
    // 飞轮记录失败不阻断发布主流程
    console.warn('[知识飞轮] 记录结果失败（不阻断）:', err);
  }
}

function MediaPreviewGrid({
  pkg,
  activeTab,
  approvedAssets,
  audioPlaying,
  setAudioPlaying,
  onPreview,
  onApprove,
  onReject,
  onRegenerate,
  onEditScript,
}: {
  pkg: MediaPackage;
  activeTab: AssetTab;
  approvedAssets: Set<string>;
  audioPlaying: string | null;
  setAudioPlaying: (id: string | null) => void;
  onPreview: (p: PreviewState) => void;
  onApprove: (id: string) => void;
  onReject: (asset: MediaAsset) => void;
  onRegenerate: (asset: MediaAsset) => void;
  onEditScript?: (asset: MediaAsset) => void;
}) {
  const showImages = activeTab === "all" || activeTab === "images";
  const showScripts = activeTab === "all" || activeTab === "scripts";
  const showVideo = activeTab === "all" || activeTab === "video";
  const showMusic = activeTab === "all" || activeTab === "music";

  return (
    <div className="space-y-3">
      {/* ============ 图片类素材 ============ */}
      {showImages && (
        <>
          {/* 封面图行 */}
          <div className="grid grid-cols-3 gap-3">
            {pkg.cover && (
              <MediaCard
                asset={pkg.cover}
                label="文章封面"
                icon={<ImageIcon size={11} />}
                approved={approvedAssets.has(pkg.cover.id)}
                onPreview={() =>
                  pkg.cover?.url &&
                  onPreview({ type: "image", url: pkg.cover.url, title: "文章封面" })
                }
                onApprove={() => onApprove(pkg.cover!.id)}
                onReject={() => onReject(pkg.cover!)}
                onRegenerate={() => onRegenerate(pkg.cover!)}
              />
            )}
            {pkg.wechatCover && (
              <MediaCard
                asset={pkg.wechatCover}
                label="公众号配图"
                icon={<Layout size={11} />}
                approved={approvedAssets.has(pkg.wechatCover.id)}
                onPreview={() =>
                  pkg.wechatCover?.url &&
                  onPreview({ type: "image", url: pkg.wechatCover.url, title: "公众号配图" })
                }
                onApprove={() => onApprove(pkg.wechatCover!.id)}
                onReject={() => onReject(pkg.wechatCover!)}
                onRegenerate={() => onRegenerate(pkg.wechatCover!)}
              />
            )}
            {pkg.douyinCover && (
              <MediaCard
                asset={pkg.douyinCover}
                label="抖音封面"
                icon={<Video size={11} />}
                approved={approvedAssets.has(pkg.douyinCover.id)}
                onPreview={() =>
                  pkg.douyinCover?.url &&
                  onPreview({ type: "image", url: pkg.douyinCover.url, title: "抖音封面" })
                }
                onApprove={() => onApprove(pkg.douyinCover!.id)}
                onReject={() => onReject(pkg.douyinCover!)}
                onRegenerate={() => onRegenerate(pkg.douyinCover!)}
              />
            )}
          </div>

          {/* 小红书九宫格 */}
          {pkg.xiaohongshuGrid && pkg.xiaohongshuGrid.length > 0 && (
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Grid3x3 size={12} className="text-pink-400" />
                <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                  小红书九宫格（{pkg.xiaohongshuGrid.length} 张）
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {pkg.xiaohongshuGrid.map((img, i) => (
                  <div
                    key={img.id}
                    className="relative aspect-square rounded-md overflow-hidden bg-white/[0.03] border border-white/[0.06] cursor-pointer group"
                    onClick={() =>
                      img.url &&
                      onPreview({
                        type: "image",
                        url: img.url,
                        title: `小红书-${i + 1}`,
                      })
                    }
                  >
                    {img.url ? (
                      <img
                        src={img.url}
                        alt={img.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={16} className="text-gray-600" />
                      </div>
                    )}
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-[9px] font-mono text-white">
                      {i + 1}
                    </div>
                    {approvedAssets.has(img.id) && (
                      <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check size={10} className="text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ 脚本类素材 ============ */}
      {showScripts && (
        <div className="grid grid-cols-3 gap-3">
          {pkg.articleScript && (
            <ScriptCard
              asset={pkg.articleScript}
              label="公众号文章脚本"
              approved={approvedAssets.has(pkg.articleScript.id)}
              onPreview={() =>
                onPreview({
                  type: "script",
                  content: pkg.articleScript?.content || "",
                  title: "公众号文章脚本",
                })
              }
              onApprove={() => onApprove(pkg.articleScript!.id)}
              onReject={() => onReject(pkg.articleScript!)}
              onRegenerate={() => onRegenerate(pkg.articleScript!)}
              onEdit={onEditScript ? () => onEditScript(pkg.articleScript!) : undefined}
            />
          )}
          {pkg.videoScript && (
            <ScriptCard
              asset={pkg.videoScript}
              label="视频分镜脚本"
              approved={approvedAssets.has(pkg.videoScript.id)}
              onPreview={() =>
                onPreview({
                  type: "script",
                  content: pkg.videoScript?.content || "",
                  title: "视频分镜脚本",
                })
              }
              onApprove={() => onApprove(pkg.videoScript!.id)}
              onReject={() => onReject(pkg.videoScript!)}
              onRegenerate={() => onRegenerate(pkg.videoScript!)}
              onEdit={onEditScript ? () => onEditScript(pkg.videoScript!) : undefined}
            />
          )}
          {pkg.xiaohongshuScript && (
            <ScriptCard
              asset={pkg.xiaohongshuScript}
              label="小红书图文脚本"
              approved={approvedAssets.has(pkg.xiaohongshuScript.id)}
              onPreview={() =>
                onPreview({
                  type: "script",
                  content: pkg.xiaohongshuScript?.content || "",
                  title: "小红书图文脚本",
                })
              }
              onApprove={() => onApprove(pkg.xiaohongshuScript!.id)}
              onReject={() => onReject(pkg.xiaohongshuScript!)}
              onRegenerate={() => onRegenerate(pkg.xiaohongshuScript!)}
              onEdit={onEditScript ? () => onEditScript(pkg.xiaohongshuScript!) : undefined}
            />
          )}
          {pkg.bilibiliScript && (
            <ScriptCard
              asset={pkg.bilibiliScript}
              label="B站视频脚本"
              approved={approvedAssets.has(pkg.bilibiliScript.id)}
              onPreview={() =>
                onPreview({
                  type: "script",
                  content: pkg.bilibiliScript?.content || "",
                  title: "B站视频脚本",
                })
              }
              onApprove={() => onApprove(pkg.bilibiliScript!.id)}
              onReject={() => onReject(pkg.bilibiliScript!)}
              onRegenerate={() => onRegenerate(pkg.bilibiliScript!)}
              onEdit={onEditScript ? () => onEditScript(pkg.bilibiliScript!) : undefined}
            />
          )}
          {pkg.douyinScript && (
            <ScriptCard
              asset={pkg.douyinScript}
              label="抖音完整脚本（标题/标签/分镜/口播/BGM）"
              approved={approvedAssets.has(pkg.douyinScript.id)}
              onPreview={() =>
                onPreview({
                  type: "script",
                  content: pkg.douyinScript?.content || "",
                  title: "抖音完整脚本",
                })
              }
              onApprove={() => onApprove(pkg.douyinScript!.id)}
              onReject={() => onReject(pkg.douyinScript!)}
              onRegenerate={() => onRegenerate(pkg.douyinScript!)}
              onEdit={onEditScript ? () => onEditScript(pkg.douyinScript!) : undefined}
            />
          )}
        </div>
      )}

      {/* ============ 视频类素材 ============ */}
      {showVideo && pkg.video && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Video size={12} className="text-blue-400" />
            <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
              短视频
            </span>
            <StatusDot status={pkg.video.status} />
          </div>
          {pkg.video.url ? (
            <video
              src={pkg.video.url}
              controls
              className="w-full rounded-md aspect-video bg-black"
            />
          ) : (
            <div className="aspect-video rounded-md bg-white/[0.03] flex items-center justify-center">
              {pkg.video.status === "failed" ? (
                <div className="text-center">
                  <span className="text-[11px] text-red-400 block">生成失败</span>
                  <span className="text-[9px] font-mono text-gray-600 mt-1 block">
                    {pkg.video.error || "未知错误"}
                  </span>
                </div>
              ) : (
                <Loader2 size={20} className="text-gray-600 animate-spin" />
              )}
            </div>
          )}
          {pkg.video.error && (
            <p className="text-[10px] text-red-400 mt-1 truncate">
              {pkg.video.error}
            </p>
          )}
          <div className="flex items-center gap-1 mt-2">
            <button
              onClick={() => onApprove(pkg.video!.id)}
              disabled={approvedAssets.has(pkg.video!.id)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded transition-colors disabled:opacity-40"
            >
              <Check size={10} /> 通过
            </button>
            <button
              onClick={() => onRegenerate(pkg.video!)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] rounded transition-colors"
            >
              <RefreshCw size={10} /> 重新生成
            </button>
          </div>
        </div>
      )}

      {/* ============ 音乐类素材 ============ */}
      {showMusic && pkg.bgm && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Music size={12} className="text-purple-400" />
            <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
              背景音乐
            </span>
            <StatusDot status={pkg.bgm.status} />
          </div>
          {pkg.bgm.url ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 rounded-md bg-white/[0.03]">
                <button
                  onClick={() =>
                    setAudioPlaying(
                      audioPlaying === pkg.bgm!.id ? null : pkg.bgm!.id
                    )
                  }
                  className="w-8 h-8 rounded-full bg-purple-500/20 hover:bg-purple-500/30 flex items-center justify-center transition-colors"
                >
                  {audioPlaying === pkg.bgm?.id ? (
                    <Pause size={14} className="text-purple-400" />
                  ) : (
                    <Play size={14} className="text-purple-400" />
                  )}
                </button>
                <div className="flex-1">
                  <p className="text-[11px] text-gray-300 truncate">
                    {pkg.bgm.title || "背景音乐"}
                  </p>
                  <p className="text-[9px] font-mono text-gray-600">
                    {pkg.bgm.model || "suno"}
                  </p>
                </div>
              </div>
              {audioPlaying === pkg.bgm.id && (
                <audio
                  src={pkg.bgm.url}
                  autoPlay
                  controls
                  className="w-full h-8"
                />
              )}
            </div>
          ) : (
            <div className="h-16 rounded-md bg-white/[0.03] flex items-center justify-center">
              {pkg.bgm.status === "failed" ? (
                <div className="text-center">
                  <span className="text-[11px] text-red-400 block">生成失败</span>
                  <span className="text-[9px] font-mono text-gray-600 mt-1 block">
                    {pkg.bgm.error || "未知错误"}
                  </span>
                </div>
              ) : (
                <Loader2 size={16} className="text-gray-600 animate-spin" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// 媒体卡片子组件
// ============================================

function MediaCard({
  asset,
  label,
  icon,
  approved,
  onPreview,
  onApprove,
  onReject,
  onRegenerate,
}: {
  asset: MediaAsset;
  label: string;
  icon: React.ReactNode;
  approved: boolean;
  onPreview: () => void;
  onApprove: () => void;
  onReject: () => void;
  onRegenerate: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  // 图片显示优先级：URL → b64Json → 失败提示
  const imgSrc = imgError && asset.b64Json ? asset.b64Json : asset.url;
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-2.5">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-purple-400">{icon}</span>
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
          {label}
        </span>
        <StatusDot status={asset.status} />
      </div>
      {imgSrc ? (
        <div
          onClick={onPreview}
          className="relative aspect-video rounded-md overflow-hidden bg-white/[0.03] cursor-pointer group"
        >
          <img
            src={imgSrc}
            alt={label}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
          {imgError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2 bg-black/40">
              <span className="text-[10px] text-red-400 block">图片加载失败</span>
              <span className="text-[8px] font-mono text-gray-400 mt-0.5 block">
                {asset.b64Json ? "已切换到 base64 预览" : "外部 URL 可能被拦截，可点击「重生」重新生成"}
              </span>
            </div>
          )}
          {approved && (
            <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check size={10} className="text-white" />
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-video rounded-md bg-white/[0.03] flex items-center justify-center">
          {asset.status === "failed" ? (
            <div className="text-center px-2">
              <span className="text-[10px] text-red-400 block">失败</span>
              <span className="text-[8px] font-mono text-gray-600 mt-0.5 block truncate">
                {asset.error || ""}
              </span>
            </div>
          ) : (
            <Loader2 size={16} className="text-gray-600 animate-spin" />
          )}
        </div>
      )}
      <div className="flex items-center gap-1 mt-2">
        <button
          onClick={onApprove}
          disabled={approved}
          className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded transition-colors disabled:opacity-40"
        >
          <Check size={9} /> 通过
        </button>
        <button
          onClick={onReject}
          className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded transition-colors"
        >
          <X size={9} /> 拒绝
        </button>
        <button
          onClick={onRegenerate}
          className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] rounded transition-colors"
        >
          <RefreshCw size={9} /> 重生
        </button>
      </div>
    </div>
  );
}

// ============================================
// 脚本卡片子组件
// ============================================

function ScriptCard({
  asset,
  label,
  approved,
  onPreview,
  onApprove,
  onReject,
  onRegenerate,
  onEdit,
}: {
  asset: MediaAsset;
  label: string;
  approved: boolean;
  onPreview: () => void;
  onApprove: () => void;
  onReject: () => void;
  onRegenerate: () => void;
  onEdit?: () => void;
}) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-2.5">
      <div className="flex items-center gap-1.5 mb-2">
        <FileText size={11} className="text-blue-400" />
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
          {label}
        </span>
        <StatusDot status={asset.status} />
      </div>
      <div
        onClick={onPreview}
        className="relative aspect-video rounded-md bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 cursor-pointer group flex flex-col items-center justify-center p-2"
      >
        <FileText size={20} className="text-blue-400/60 mb-1" />
        <span className="text-[9px] font-mono text-gray-500 text-center">
          {asset.content ? `${asset.content.length} 字` : "空"}
        </span>
        <span className="text-[8px] font-mono text-blue-400 mt-1 group-hover:underline">
          点击查看
        </span>
        {approved && (
          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
            <Check size={10} className="text-white" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 mt-2">
        <button
          onClick={onApprove}
          disabled={approved}
          className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded transition-colors disabled:opacity-40"
        >
          <Check size={9} /> 通过
        </button>
        <button
          onClick={onReject}
          className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded transition-colors"
        >
          <X size={9} /> 拒绝
        </button>
        <button
          onClick={onRegenerate}
          className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] rounded transition-colors"
        >
          <RefreshCw size={9} /> 重生
        </button>
      </div>
      {onEdit && (
        <button
          onClick={onEdit}
          className="w-full mt-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded transition-colors"
        >
          <Edit3 size={9} /> 编辑脚本
        </button>
      )}
    </div>
  );
}

// ============================================
// 状态点子组件
// ============================================

function StatusDot({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    completed: "bg-emerald-400",
    pending: "bg-yellow-400",
    generating: "bg-blue-400 animate-pulse",
    processing: "bg-blue-400 animate-pulse",
    failed: "bg-red-400",
    partial: "bg-yellow-400",
    profitable: "bg-emerald-400",
    growing: "bg-blue-400",
    breakeven: "bg-yellow-400",
    loss: "bg-red-400",
  };
  return (
    <span
      className={`w-1.5 h-1.5 rounded-full ${colorMap[status] || "bg-gray-500"}`}
    />
  );
}

// ============================================
// 辅助函数
// ============================================

function countAssets(pkg: MediaPackage): number {
  let count = 0;
  if (pkg.cover) count++;
  if (pkg.wechatCover) count++;
  if (pkg.xiaohongshuGrid) count += pkg.xiaohongshuGrid.length;
  if (pkg.douyinCover) count++;
  if (pkg.video) count++;
  if (pkg.bgm) count++;
  if (pkg.articleScript) count++;
  if (pkg.videoScript) count++;
  if (pkg.xiaohongshuScript) count++;
  if (pkg.bilibiliScript) count++;
  if (pkg.douyinScript) count++;
  return count;
}

// ============================================
// 脚本库：平台显示配置辅助函数
// ============================================

/** 平台显示信息 */
interface PlatformDisplay {
  /** 分组 key（用于聚合抖音系平台） */
  groupKey: string;
  /** 平台名称 */
  name: string;
  /** 平台图标 */
  icon: string;
  /** 平台主色 */
  color: string;
}

/**
 * 根据脚本文件名中的平台前缀，返回用于分组展示的显示信息
 *
 * 文件名中的平台前缀可能来自 generate_script（原始 platform 值）或 generate_full_package（简化值），
 * 其中 'douyin' / 'douyin_enterprise' / 'douyin_personal' 统一聚合为"抖音"分组
 */
function getPlatformDisplay(platform: string): PlatformDisplay {
  // 抖音系统一聚合
  if (platform === 'douyin' || platform === 'douyin_enterprise' || platform === 'douyin_personal') {
    return { groupKey: 'douyin', name: '抖音', icon: '🎬', color: '#FE2C55' };
  }
  // 复用 PLATFORM_CONFIG（覆盖 wechat / xiaohongshu / zhihu / bilibili）
  const cfg = (PLATFORM_CONFIG as Record<string, { name: string; icon: string; color: string }>)[platform];
  if (cfg) {
    return { groupKey: platform, name: cfg.name, icon: cfg.icon, color: cfg.color };
  }
  // 兜底：未知平台直接用原名
  return { groupKey: platform, name: platform, icon: '📄', color: '#888888' };
}

// ============================================
// 脚本库视图子组件（按平台分组浏览历史脚本）
// ============================================

function ScriptLibraryView({
  scripts,
  loading,
  onRefresh,
  onView,
}: {
  scripts: ScriptLibraryItem[];
  loading: boolean;
  onRefresh: () => void;
  onView: (item: ScriptLibraryItem) => void;
}) {
  // 按平台分组（使用 groupKey 聚合抖音系）
  const groups = new Map<string, { display: PlatformDisplay; items: ScriptLibraryItem[] }>();
  for (const s of scripts) {
    const display = getPlatformDisplay(s.platform);
    const existing = groups.get(display.groupKey);
    if (existing) {
      existing.items.push(s);
    } else {
      groups.set(display.groupKey, { display, items: [s] });
    }
  }

  // 分组排序：按每组脚本数量倒序
  const sortedGroups = Array.from(groups.values()).sort(
    (a, b) => b.items.length - a.items.length
  );

  return (
    <div className="rounded-lg border border-blue-500/20 bg-blue-500/[0.02] p-3">
      {/* 头部：标题 + 数量 + 刷新 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Archive size={12} className="text-blue-400" />
          <span className="text-[11px] font-mono text-blue-300 uppercase tracking-wider">
            脚本库
          </span>
          <span className="text-[10px] font-mono text-gray-600">
            共 {scripts.length} 篇 · {sortedGroups.length} 个平台
          </span>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-1 text-gray-500 hover:text-blue-400 transition-colors"
          title="刷新脚本库"
        >
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* 内容区 */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
        {loading && scripts.length === 0 ? (
          <div className="py-4 text-center">
            <Loader2 size={14} className="mx-auto text-blue-400 animate-spin" />
            <p className="text-[10px] font-mono text-gray-600 mt-1">加载脚本库...</p>
          </div>
        ) : scripts.length === 0 ? (
          <p className="text-[11px] text-gray-600 text-center py-3">
            脚本库为空（脚本生成后会自动保存到 Vault）
          </p>
        ) : (
          sortedGroups.map(({ display, items }) => (
            <div
              key={display.groupKey}
              className="rounded-md border border-white/[0.04] bg-white/[0.01] overflow-hidden"
            >
              {/* 平台分组标题栏 */}
              <div
                className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-white/[0.04]"
                style={{ background: `${display.color}10` }}
              >
                <span className="text-[14px] flex-shrink-0">{display.icon}</span>
                <span
                  className="text-[10px] font-mono font-semibold truncate"
                  style={{ color: display.color }}
                >
                  {display.name}
                </span>
                <span className="text-[9px] font-mono text-gray-500 flex-shrink-0">
                  {items.length} 篇
                </span>
              </div>

              {/* 平台下的脚本卡片列表 */}
              <div className="space-y-1 p-1.5">
                {items.map((item) => (
                  <div
                    key={item.path}
                    className="rounded border border-white/[0.03] bg-white/[0.01] px-2 py-1.5"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <FileText size={9} className="text-blue-400 flex-shrink-0" />
                      <span className="text-[11px] text-gray-200 font-medium truncate flex-1">
                        {item.topic}
                      </span>
                      <span className="text-[9px] font-mono text-gray-600 flex-shrink-0">
                        {item.date}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onView(item)}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[9px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 rounded transition-colors"
                      >
                        <FileText size={9} /> 查看内容
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
